import { ApiError, NOTHING_CLOSED, resolveSalesDrill } from './types'
import { maintenanceDetected } from './maintenance'
import { actingAs } from './act-as'
import type { Answer } from './answer'
import type {
  AccessMatrixCell, AccessPeople, AccessPeopleFilter, AccessPerson, AccessPersonPage, AccessPersonRow,
  AccessPersonStatus, AccessWarning,
  AccessApprovalCard, AccessApprovalFilter, AccessApprovalRow, AccessApprovals, AccessDecision,
  AccessHrRoleBody, AccessHrRoleEffect, AccessHrRolePage, AccessHrRoleRow, AccessHrRoles, AccessHrRoleSummary,
  AccessProblems, AccessProblemItem, AccessProblemItems, AccessSync, AccessSyncPull, AccessHeldPull, AccessAcceptResult,
  AccessEmailDomain, AccessEmailDomains, AccessEmailDomainEffect,
  AccessStaff, AccessHistory, AccessOrgCodes, AccessPersonSettingsBody, AccessExtraScopeBody, AccessPersonEffect,
  AccessHierarchyPreview, AccessHierarchyPreviewRow, AccessScopeEdit, AccessCardSees,
  AccessPersonEffectSummary, AccessSignInLinkEffect,
  ActAsCandidate,
  ActAsTarget,
  MaintenanceNotice,
  MaintenanceState,
  MpOverview, MpStockRow, MpPurchaseOrderRow, MpSalesMonth, MpItemStatusRow, MpOrderStatus,
  MpFilterRow, MpBreakdownRow, MpBreakdownKind, MpShipmentRow, MpSalesPerformanceRow,
  MpItemSummary, MpStockSort, MpStockGroupBy, MpShipmentView, MpItemOrderRow,
  InvFilterRow, InvOverview, InvMatrixRow, InvLotRow, InvGroupBy, InvWindow, InvAgeBand, InvExpiryBand,
  Currency,
  NewsItem,
  PublishNewsRequest,
  UpdateNewsRequest,
  MaintenanceStatus,
  MaintenanceWindow,
  MatrixLevel,
  MatrixNode,
  MatrixPath,
  RecvFilters,
  RecvFilterRow,
  StoredGridLayout,
  StoredReaderView,
  Me,
  ReaderSettings,
  TransactionPage,
  TransactionRow,
  DecomposeLevel,
  DecomposeResponse,
  DsoNode,
  PermissionAnomaly,
  PermissionGrant,
  PermissionUser,
  ReportAccessEntry,
  GrantAccessRequest,
  GrantAccessResult,
  RoleHolder,
  PlatformRoleCode,
  SetRoleRequest,
  AdminReport,
  WeekComparison,
  WeekTrend,
  SalespersonReview,
  ColumnGovernance,
  ColumnGovernanceRow,
  PermissionCatalogRow,
  SetColumnPermissionsRequest,
  ColumnPermissionChange,
  ColumnDefinition,
  ColumnVisibility,
  ColumnSettingBody,
  ColumnSettingEffect,
  ColumnGroupPage,
  ColumnGroupBody,
  ColumnGroupEffect,
  ColumnGroupRow,
  SetMaintenanceRequest,
  SalesBoardRow,
  SalesDimension,
  SalesDrillLevel,
  SalesExplorerDimension,
  SalesExplorerResponse,
  SalesFilterRow,
  CurrencyRate,
  DevFlag,
  DevFlagKey,
  SetDevFlagRequest,
  MeasureDoc,
  MeasureKind,
  MeasureRef,
  SalesLine,
  SalesLineFilterRow,
  SalesLinesResponse,
  SalesOverview,
  SalesTreeResponse,
  SalesTrendRow,
  StatementCompany,
  StatementCustomer,
  StatementHeader,
  StatementKind,
  StatementLine,
  StatementResponse,
} from './types'

/**
 * DEMO MODE — invented data, a fake signed-in user, NO AUTHENTICATION.
 *
 * It exists so the platform can be reviewed and designed against before Entra
 * and the database are wired up. Reviewing a UI you cannot run is guesswork.
 *
 * Three things keep it from becoming a hole:
 *   1. `npm run build` THROWS if VITE_MBI_MODE=demo (vite.config.ts).
 *      Not a warning — the bundle cannot be produced.
 *   2. A permanent banner sits at the top of every screen, so nobody can mistake
 *      demo data for real data in a screenshot or a meeting.
 *   3. The numbers are visibly invented and the companies are the fixture's, so
 *      a figure from here cannot be mistaken for a real receivables balance.
 *
 * The shapes match the API exactly. If the API changes and this does not, the
 * TypeScript build fails — which is the point of it implementing the same
 * interface rather than being loose mock JSON.
 */

const DEMO_LATENCY_MS = 320

/** One wording for every page demo mode declines to fake. */
const NOT_IN_DEMO = (page: string) =>
  `${page} is not available in demo mode — it needs real weekly history, and invented figures here would be mistaken for a design. Run against the API to see it.`

function delay<T>(value: T): Promise<T> {
  // Deliberately not instant. A UI designed against zero-latency data has no
  // loading states, and then gets them for free in production, badly.
  return new Promise((resolve) => setTimeout(() => resolve(value), DEMO_LATENCY_MS))
}

const DEMO_ME: Me = {
  user: 'demo.user@redachem.com',
  roles: ['SystemAdmin'],
  seesAllData: true,
  canViewAsData: true,
  scopeNodeCount: 12,
  reports: ['receivables', 'sales', 'material-planning', 'inventory-aging', 'statement-of-account'],
  targetIsRegistered: true,
  // Filled in per call by demoMaintenance() — a constant here would be a demo in
  // which opening a window changes the console and nothing else, which is the
  // half of the feature that was never the problem.
  maintenance: NOTHING_CLOSED,
  // Filled in per call, like maintenance above: the demo has to be able to show
  // an acting session, because a reviewer signed in as a System Admin sees
  // nothing of this feature otherwise.
  actor: null,
  correlationId: 'demo-0000-0000',
}

/** The API's sentences for the entry codes the demo produces (ReportGuards.AdminSentence). */
const DEMO_ENTRY_WHY: Record<string, string> = {
  DENIED_LEFT: 'Left (inactive in Mindzer).',
  DENIED_DISABLED: 'Disabled in Mindzer.',
  DENIED_NO_HR_ROLE: 'No HR role in Mindzer.',
  DENIED_ROLE_NOT_SET: 'HR role not set up in MBI.',
  DENIED_NOT_APPROVED: 'Not approved yet.',
}

/* The demo's business units and their aging totals: the filter menus and the
   Login As narrowing read them (the by-business-unit read itself is gone, D50). */
const DEMO_AGING: { businessUnit: string; advancePayment: number | null; totalReceivables: number | null; totalOverdue: number | null }[] = [
  { businessUnit: 'FI', advancePayment: 120_400, totalReceivables: 4_820_500, totalOverdue: 1_930_200 },
  { businessUnit: 'LAB', advancePayment: 38_900, totalReceivables: 2_140_750, totalOverdue: 402_100 },
  { businessUnit: 'IND', advancePayment: 0, totalReceivables: 1_688_300, totalOverdue: 1_204_880 },
  { businessUnit: 'AGRI', advancePayment: 61_250, totalReceivables: 1_205_400, totalOverdue: 96_500 },
  { businessUnit: 'PHARMA', advancePayment: 15_000, totalReceivables: 940_120, totalOverdue: 512_600 },
  { businessUnit: 'WATER', advancePayment: 0, totalReceivables: 615_880, totalOverdue: 611_300 },
  { businessUnit: 'ENERGY', advancePayment: 204_000, totalReceivables: 402_950, totalOverdue: 12_400 },
]

/**
 * Deterministic fake object ids for the demo.
 *
 * SOME PEOPLE DELIBERATELY HAVE NONE. On the real clone, 377 people appear in
 * the ERP permission table and 12 have ever signed in — so the picker's second
 * half, the greyed "never signed in" list, is most of it. A demo where everyone
 * is selectable would hide the single most visible thing about this screen.
 *
 * The first four demo users have signed in; the rest have not.
 */
const DEMO_OID_PREFIX = 'demo0000-0000-4000-8000-00000000'

function demoOidFor(email: string): string | null {
  const i = DEMO_USERS.findIndex((u) => u.email === email)
  return i >= 0 && i < 4 ? DEMO_OID_PREFIX + String(1000 + i) : null
}

/**
 * A PERSON WITH NO DATA PERMISSIONS — the demo's way onto the error path.
 *
 * Added 22 Sep 2026, after the same bug shipped twice in two days: Material
 * Planning and then Sales each crashed into the error boundary for a reader who
 * holds nothing, instead of showing the panel that says so. Both lived on the
 * path that only a REFUSED request walks, and the demo — signed in as a System
 * Admin who sees everything — had no way to walk it. So nothing ever did, until
 * the owner used Login As against production.
 *
 * New Joiner is the realistic version of that person: on a report's access list
 * (see `demoAccess`), signed in, and with NO ROWS in the permission table — the
 * gap between being granted a report and being granted any data in it, which
 * is the most common way a real account reaches this state. Acting as them,
 * every report read is refused exactly as the API refuses it: a 403 "No data
 * permissions", decoded as kind 'no-permissions' (see withDemoPersonas).
 */
const DEMO_NO_GRANTS = {
  email: 'new.joiner@redachem.com',
  displayName: 'New Joiner',
  oid: DEMO_OID_PREFIX + '1900',
} as const

/** The person an oid names, or null — the demo's sec.Principal_MBI lookup. */
function demoTargetFor(oid: string | null): PermissionUser | null {
  if (!oid) return null
  if (oid === DEMO_NO_GRANTS.oid) {
    // A principal who is NOT in the permission table — so no grants, no
    // companies, and none of the D365 fields a real row would carry.
    return {
      email: DEMO_NO_GRANTS.email, displayName: DEMO_NO_GRANTS.displayName, userClass: '',
      grantRows: 0, companies: 0, companyList: '', wholeCompany: false, duplicateRows: 0,
    }
  }
  const user = DEMO_USERS.find((u) => demoOidFor(u.email) === oid)
  if (user) return user
  // A signed-in person from Access › People ("Login As this person").
  const p = DEMO_ACCESS_PEOPLE.find((x) => (x.lastSignIn !== null || x.found) && DEMO_OID_PREFIX + String(x.id) === oid)
  return p
    ? { email: `person${p.id}@redachem.com`, displayName: `Person ${p.id}`, userClass: '', grantRows: 1, companies: 1,
        companyList: p.comp.toLowerCase(), wholeCompany: false, duplicateRows: 0 }
    : null
}

/**
 * Report reads refuse while acting as New Joiner — every one, as the API does.
 *
 * A Proxy rather than a check pasted into forty methods: a refusal added to
 * each by hand is forty chances to forget one, and the one forgotten would be
 * exactly the surface this persona exists to test. Methods are matched by
 * name, and the name test is the whole contract: every report read on `Api`
 * starts with one of these prefixes, and nothing else does.
 *
 * Methods are BOUND TO THE TARGET, not the proxy, because DemoClient uses
 * private members (`#mpSort`) and a private member is only reachable when
 * `this` is the real instance.
 */
const REPORT_READ = /^(receivables|sales|materialPlanning|inventoryAging|statement|salespersonReview)/

/** A report read as today's model answers it: the body, and nothing said about withheld figures (M8). */
const today = <T,>(data: T): Answer<T> => ({ data, vis: null })

/* THE DEMO'S NEW-MODEL READER (M8): open the demo with ?model=v2 and Inventory
   Aging answers as the new model would for a reader whose access shows every
   company's quantities but r010's money to nobody here. So the screen
   can be seen with a withheld cell, a partly withheld tile and a totals row that
   says so. Kept for the tab's life, as the address loses it on the first click. */
const DEMO_V2_NO_MONEY = 'r010'
function demoV2(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('model') === 'v2') sessionStorage.setItem('mbi.demo.model', 'v2')
    return sessionStorage.getItem('mbi.demo.model') === 'v2'
  } catch {
    return false
  }
}
/** A report read as the model the demo runs answers it. */
const answer = <T,>(data: T): Answer<T> =>
  demoV2() ? { data, vis: { shown: new Set(['quantity', 'value', 'unitCost', 'currencyCode']), teamSales: null } } : today(data)

export function withDemoPersonas<T extends object>(client: T): T {
  return new Proxy(client, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver)
      if (typeof value !== 'function') return value
      const bound = value.bind(target)
      if (typeof prop !== 'string' || !REPORT_READ.test(prop)) return bound
      return (...args: unknown[]) =>
        actingAs() === DEMO_NO_GRANTS.oid
          ? Promise.reject(new ApiError({ kind: 'no-permissions', correlationId: 'demo-no-grants' }))
          : bound(...args)
    },
  })
}

const DEMO_USERS: PermissionUser[] = [
  { email: "ziad.katabi@redachem.com", displayName: "Ziad Katabi", userClass: "FUNC-MGR", grantRows: 12, companies: 6, companyList: "r002, r003, r010, r011, r022, r040", wholeCompany: true, duplicateRows: 2 },
  { email: "atif.majeed@redachem.com", displayName: "Atif Majeed", userClass: "BU-MGR", grantRows: 4, companies: 2, companyList: "r002, r010", wholeCompany: true, duplicateRows: 2 },
  { email: "nour.habli@redachem.com", displayName: "Nour Habli", userClass: "BT-MGR", grantRows: 2, companies: 1, companyList: "r002", wholeCompany: false, duplicateRows: 0 },
  { email: "sara.mansour@redachem.com", displayName: "Sara Mansour", userClass: "BT-MGR", grantRows: 1, companies: 1, companyList: "r003", wholeCompany: false, duplicateRows: 0 },
  { email: "omar.fahd@redachem.com", displayName: "Omar Fahd", userClass: "SALES", grantRows: 1, companies: 1, companyList: "r011", wholeCompany: false, duplicateRows: 0 },
  { email: "layla.aziz@redachem.com", displayName: "Layla Aziz", userClass: "BU-MGR", grantRows: 3, companies: 1, companyList: "r002", wholeCompany: false, duplicateRows: 2 },
]

const DEMO_GRANTS: Record<string, PermissionGrant[]> = {
  "nour.habli@redachem.com": [
    { company: "r002", companyName: "REDAChem KSA", businessUnit: "FI", businessTeam: "FI-BKR", breadth: "One team", userClass: "BT-MGR" },
    { company: "r002", companyName: "REDAChem KSA", businessUnit: "FI", businessTeam: "FI-IND", breadth: "One team", userClass: "BT-MGR" },
  ],
}

const DEMO_ANOMALIES: PermissionAnomaly[] = [
  { severity: "review", finding: "Whole-company access", subject: "ziad.katabi@redachem.com", detail: "Sees all units and teams in r002 (REDAChem KSA)", action: "Confirm this breadth is still needed." },
  { severity: "review", finding: "Unusually broad access", subject: "ziad.katabi@redachem.com", detail: "12 grants across 6 companies (typical is 1-3)", action: "Check the grants are all still required." },
  { severity: "broken", finding: "Grant with no email", subject: "Hassan Q.", detail: "Grant on r022 / LAB has no Emp_Email, so it matches no one who signs in", action: "Add the address or delete the row." },
  { severity: "tidy", finding: "Duplicate grant", subject: "layla.aziz@redachem.com", detail: "The same grant appears 2 times: r002 / LAB / ALL", action: "Remove the duplicates; access is unchanged." },
]

/**
 * The demo matrix: a synthetic tree that behaves like the real one.
 *
 * Generated rather than hand-written, because the property that has to hold is
 * arithmetic: every level must sum EXACTLY to its parent. A hand-written tree
 * drifts on the first edit, and a demo whose subtotals do not reconcile teaches
 * a reviewer to distrust the real report.
 *
 * Each node's measures are its children's, summed. The leaves carry the numbers.
 */
const DEMO_CHILD_NAMES: Record<string, string[]> = {
  BusinessUnit: ['FI-BKR', 'FI-IND', 'FI-AGR'],
  BusinessTeam: ['Atif Majeed', 'Nour Habli', 'Omar Fahd'],
  Salesperson: ['MASDAR AL HAYAT', 'KUDU FOOD', 'AL RASHED TRADING'],
  Customer: ['INV-100418', 'INV-100692', 'INV-101155', 'INV-101902'],
}

/* Grid layouts for the demo, one key holding the lot. localStorage rather than
   a module variable because the whole point of the feature is surviving a
   reload — a stand-in that lost them on F5 would demonstrate the bug this
   replaced. Every access is wrapped: localStorage throws in private-mode Safari
   and wherever storage is denied, and a column order must never be able to
   break the page it decorates. */
const DEMO_LAYOUT_KEY = 'mbi:demo:grid-layouts'

function demoLayouts(): Record<string, string> {
  try {
    const raw = localStorage.getItem(DEMO_LAYOUT_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {}
  } catch {
    return {}
  }
}

function writeDemoLayouts(all: Record<string, string>): void {
  try {
    localStorage.setItem(DEMO_LAYOUT_KEY, JSON.stringify(all))
  } catch {
    // A preference we cannot persist is not worth throwing over.
  }
}

/* The remembered display currency for the demo (036), PER IDENTITY, so Login As
   shows the target's choice exactly as the API does. Same shelf rules as the
   layouts above: localStorage, every access wrapped. */
const DEMO_SETTINGS_KEY = 'mbi:demo:reader-settings'

/** The offered rates, shared by currencyRates() and the remembered currency. */
const DEMO_CURRENCY_RATES: CurrencyRate[] = [
  { code: 'AED', ratePerUsd: 3.6725, note: null },
  { code: 'EGP', ratePerUsd: 49.4466, note: null },
  { code: 'EUR', ratePerUsd: 0.85109, note: null },
  { code: 'GBP', ratePerUsd: 0.7585, note: null },
  { code: 'KWD', ratePerUsd: 0.309, note: null },
  { code: 'QAR', ratePerUsd: 3.65, note: null },
  { code: 'SAR', ratePerUsd: 3.75, note: null },
  { code: 'ZAR', ratePerUsd: 18.62, note: null },
]

function demoSettings(): Record<string, string> {
  try {
    const raw = localStorage.getItem(DEMO_SETTINGS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {}
  } catch {
    return {}
  }
}

/** What 036's settings read returns for one person: today's rate, and nothing for an unoffered code. */
function demoSettingsFor(email: string): ReaderSettings {
  const value = demoSettings()[email]
  if (!value) return { currency: null }
  if (value === 'local' || value === 'usd') return { currency: { value, ratePerUsd: null } }
  const rate = DEMO_CURRENCY_RATES.find((r) => r.code === value)
  return { currency: rate ? { value, ratePerUsd: rate.ratePerUsd } : null }
}

/* Named views for the demo. `report` is carried on the row here where the real
   table keys on Report_Id — one flat shelf is simpler than a second key, and
   it is stripped before the row goes to the caller so the demo and the API
   hand back the same shape. */
type DemoView = StoredReaderView & { report: string }

const DEMO_VIEWS_KEY = 'mbi:demo:reader-views'

function demoViews(): DemoView[] {
  try {
    const raw = localStorage.getItem(DEMO_VIEWS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? (parsed as DemoView[]) : []
  } catch {
    return []
  }
}

function writeDemoViews(all: DemoView[]): void {
  try {
    localStorage.setItem(DEMO_VIEWS_KEY, JSON.stringify(all))
  } catch {
    // As above.
  }
}

/** Deterministic pseudo-random from a string, so the demo is stable across reloads. */
function seeded(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return ((h >>> 0) % 100000) / 100000
}

/* ===========================================================================
   THE RECEIVABLES FILTER BAR, in the demo.

   The demo has to NARROW, not merely draw the controls. A bar that renders
   beautifully and changes no figure teaches a reviewer the opposite of what the
   real report does — and this is the build the documentation screenshots are
   taken from, so whatever it does becomes what people believe it does.

   Every document-level attribute is derived from the node's own seed by the one
   function below, and both the matrix and the transactions grid read it. So a
   reader who picks "121 To 180 Days" sees the tiles, the tree and the grid all
   move together, which is the property the real procedures have by construction
   and the demo would otherwise have to be lucky to keep.

   The vocabulary is the real one, read off the clone on 24 Sep 2026: seven
   aging labels, five transaction types (Payment carries negative money there,
   which is why it is worth being able to isolate), and payment-term codes in
   the ERP's own spelling rather than invented "Net 30" prose.
   =========================================================================== */
const DEMO_AGING_PERIODS = [
  '0 To 30 Days', '31 To 60 Days', '61 To 90 Days', '91 To 120 Days',
  '121 To 180 Days', '181 To 240 Days', '241 To 365 Days', 'Over 365 Days',
] as const
const DEMO_TRX_TYPES = ['Sales', 'GeneralJournal', 'Payment', 'Cust'] as const
const DEMO_RECV_TERMS = ['030 Days', '060 Days', '090 Days', 'CASH', 'ADV', '30EOM'] as const
const DEMO_RECV_REGIONS = ['CENTRAL', 'EASTERN', 'WESTERN', 'GULF'] as const

/** The label the row's age falls in — the same step function the source view applies. */
function demoAgingPeriod(daysOld: number): string {
  if (daysOld <= 30) return DEMO_AGING_PERIODS[0]
  if (daysOld <= 60) return DEMO_AGING_PERIODS[1]
  if (daysOld <= 90) return DEMO_AGING_PERIODS[2]
  if (daysOld <= 120) return DEMO_AGING_PERIODS[3]
  if (daysOld <= 180) return DEMO_AGING_PERIODS[4]
  if (daysOld <= 240) return DEMO_AGING_PERIODS[5]
  if (daysOld <= 365) return DEMO_AGING_PERIODS[6]
  return DEMO_AGING_PERIODS[7]
}

/** One document's filterable attributes, from its seed. Stable across reloads. */
function demoDocAttrs(seed: string) {
  const daysOld = 20 + Math.floor(seeded(seed + 'age') * 320)
  return {
    daysOld,
    // Negative for a document not yet due, exactly as Days_Overdue is in the
    // source: 1,453 of the real open rows are. Clamping it to zero here would
    // make the "overdue only" switch look like it does nothing.
    daysOverdue: daysOld - 30,
    agingPeriod: demoAgingPeriod(daysOld),
    trxType: DEMO_TRX_TYPES[Math.floor(seeded(seed + 'tt') * DEMO_TRX_TYPES.length)],
    terms: DEMO_RECV_TERMS[Math.floor(seeded(seed + 'pt') * DEMO_RECV_TERMS.length)],
    region: DEMO_RECV_REGIONS[Math.floor(seeded(seed + 'rg') * DEMO_RECV_REGIONS.length)],
  }
}

/** Does this document survive the bar's document-level filters? */
function demoDocPasses(seed: string, doc: string, f?: RecvFilters): boolean {
  if (!f) return true
  const a = demoDocAttrs(seed)
  if (f.aging && a.agingPeriod !== f.aging) return false
  if (f.trxType && a.trxType !== f.trxType) return false
  if (f.terms && a.terms !== f.terms) return false
  if (f.region && a.region !== f.region) return false
  if (f.overdueOnly && a.daysOverdue <= 0) return false
  if (f.minDaysOverdue !== undefined && a.daysOverdue < f.minDaysOverdue) return false
  if (f.doc && !doc.toLowerCase().includes(f.doc.toLowerCase())) return false
  return true
}

function demoNode(level: MatrixLevel, key: string, seed: string, scale: number): MatrixNode {
  const r = seeded(seed)
  const total = Math.round(scale * (0.4 + r * 1.2))
  // Buckets that add up to the total, so Total Receivables reconciles against
  // the aging columns the way it does in the source view.
  const b = [0.22, 0.19, 0.16, 0.13, 0.11, 0.1, 0.09].map((f, i) =>
    Math.round(total * f * (0.5 + seeded(seed + i) )),
  )
  const sum = b.reduce((s, v) => s + v, 0)
  b[0] += total - sum // absorb rounding into the first bucket

  const overdue = total - b[0]
  return {
    // Single-company sample data, so the code is determinate everywhere.
    // The MIXED case (null) is exercised against the real database, not here.
    currencyCode: 'SAR',
    nodeKey: key,
    groupedBy: level,
    hasChildren: level !== 'Document',
    advancePayment: Math.round(total * 0.04),
    totalReceivables: total,
    totalOverdue: overdue,
    over30DaysOverdue: overdue - b[1],
    d0To30: b[0],
    d31To60: b[1],
    d61To90: b[2],
    d91To120: b[3],
    d121To180: b[4],
    d181To365: b[5],
    dOver365: b[6],
  }
}

const MEASURE_KEYS = [
  'advancePayment', 'totalReceivables', 'totalOverdue', 'over30DaysOverdue',
  'd0To30', 'd31To60', 'd61To90', 'd91To120', 'd121To180', 'd181To365', 'dOver365',
] as const

/** Children of `path`, with each node's measures equal to the sum of its own children. */
function demoMatrix(path: MatrixPath, viewAs?: string, f?: RecvFilters): MatrixNode[] {
  const units = viewAs ? (DEMO_VIEW_AS_UNITS[viewAs] ?? ['FI']) : DEMO_AGING.map((r) => r.businessUnit)

  let level: MatrixLevel = 'BusinessUnit'
  let names = units
  let seedBase = ''

  if (path.customer) {
    level = 'Document'; names = DEMO_CHILD_NAMES.Customer
    seedBase = `${path.bu}/${path.bt}/${path.sp}/${path.customer}`
  } else if (path.sp) {
    level = 'Customer'; names = DEMO_CHILD_NAMES.Salesperson
    seedBase = `${path.bu}/${path.bt}/${path.sp}`
  } else if (path.bt) {
    level = 'Salesperson'; names = DEMO_CHILD_NAMES.BusinessTeam
    seedBase = `${path.bu}/${path.bt}`
  } else if (path.bu) {
    level = 'BusinessTeam'; names = DEMO_CHILD_NAMES.BusinessUnit
    seedBase = `${path.bu}`
  }

  /* THE STANDING FILTER, at the level it belongs to.
     Each dimension prunes the names of its OWN level, and because every level
     above sums its children, that one cut propagates upward on its own: filter
     to a business team and the business-unit totals shrink to that team without
     anything here having to say so. It is the same property the procedures get
     from AND-ing every predicate into one statement.
     Customer is a substring and the rest are exact, matching the real ones. */
  const exact = level === 'BusinessUnit' ? f?.bu : level === 'BusinessTeam' ? f?.bt : level === 'Salesperson' ? f?.sp : undefined
  if (exact) names = names.filter((n) => n === exact)
  if (level === 'Customer' && f?.cust) {
    const needle = f.cust.toLowerCase()
    names = names.filter((n) => n.toLowerCase().includes(needle))
  }
  if (level === 'Document') {
    names = names.filter((n) => demoDocPasses(`${seedBase}/${n}`, n, f))
  }

  // Leaves carry generated numbers; everything above sums its own children, so
  // the tree reconciles at every level.
  const scale = level === 'Document' ? 90_000 : 400_000
  const nodes = names.map((n) => demoNode(level, n, `${seedBase}/${n}`, scale))

  if (level === 'Document') return nodes

  return nodes
    .map((n) => {
      const kids = demoMatrix(childPathOf(path, n), viewAs, f)
      const rolled: MatrixNode = { ...n }
      for (const k of MEASURE_KEYS) {
        rolled[k] = kids.reduce((s, c) => s + (c[k] ?? 0), 0)
      }
      // A node whose every document was filtered out has nothing left to show
      // and nothing to expand into. Keeping it would put a row of zeros in the
      // grid and a bar of nothing in the tree.
      return kids.length === 0 ? null : rolled
    })
    .filter((n): n is MatrixNode => n !== null)
}

function childPathOf(parent: MatrixPath, n: MatrixNode): MatrixPath {
  const key = n.nodeKey ?? ''
  if (n.groupedBy === 'BusinessUnit') return { ...parent, bu: key }
  if (n.groupedBy === 'BusinessTeam') return { ...parent, bt: key }
  if (n.groupedBy === 'Salesperson') return { ...parent, sp: key }
  if (n.groupedBy === 'Customer') return { ...parent, customer: key }
  return parent
}

/**
 * What each demo person can see, keyed to the breadth shown in DEMO_USERS so the
 * picker and the report tell the same story: Ziad holds 6 companies and sees
 * most units; Nour holds one team and sees one unit.
 *
 * Nour also has Credit_Limit masked in the real fixture, so her advance-payment
 * measure comes back null here — the mask follows the TARGET, not the
 * administrator, and a demo that showed every column would hide that.
 */
const DEMO_VIEW_AS_UNITS: Record<string, string[]> = {
  'ziad.katabi@redachem.com': ['FI', 'LAB', 'IND', 'AGRI', 'PHARMA'],
  'atif.majeed@redachem.com': ['FI', 'LAB', 'AGRI'],
  'nour.habli@redachem.com': ['FI'],
  'sara.mansour@redachem.com': ['PHARMA'],
  'omar.fahd@redachem.com': ['WATER'],
  'layla.aziz@redachem.com': ['LAB', 'IND'],
}

/**
 * Mutable so the maintenance screens are genuinely interactive in demo mode.
 *
 * A LIST, not one window. Modelling a single window is what let the real bug
 * hide: several scopes can be open at once — the platform plus any number of
 * reports — and each is separately liftable. A demo that can only hold one
 * would never have reproduced "I opened a window on a report and nothing
 * happened".
 */
let demoWindows: MaintenanceWindow[] = []

/**
 * The News feed, seeded with the two cards migration 025 publishes for real.
 *
 * Mutable at module scope, like demoWindows and for the same reason: publishing,
 * editing and removing have to actually change what the page shows, or the demo
 * cannot be used to review the controls that do the changing.
 */
const daysFromNow = (d: number) => new Date(Date.now() + d * 86_400_000).toISOString()

function demoReportName(slug: string): string {
  return slug === 'sales' ? 'Sales'
    : slug === 'receivables' ? 'Receivables'
    : slug === 'material-planning' ? 'Material Planning'
    : slug === 'inventory-aging' ? 'Inventory Aging'
    : slug === 'statement-of-account' ? 'Statement of Account'
    : slug
}

let demoNews: NewsItem[] = [
  {
    newsId: 2,
    reportSlug: 'sales',
    reportDisplayName: 'Sales',
    headline: 'The Sales report is live',
    summary:
      'The two Power BI sales dashboards have been rebuilt here as one report: an overview, an explorer, leaderboards and invoice lines. Cost and margin appear only if your permissions include them.',
    details:
      'Twenty-one pages across the old "Sales Dashboard" and "Sales DashboardV2" have been folded into four screens, because most of those pages were the same figures filtered differently — which the explorer now does on demand.\n\nTwo differences worth knowing:\n  - Month-to-date counts now pin the year. The old report counted the same month across every year, so a customer who last bought in March two years ago was counted as a customer this March.\n  - Sample shipments still count towards money, but no longer appear in customer counts or leaderboards, where they were crowding out real customers.\n\nCost, margin and GP are shown only to people whose permissions include sales cost. If you see sales without margin, that is deliberate and not a fault.',
    publishedUtc: daysFromNow(-3),
    expiresUtc: daysFromNow(27),
    isRetired: false,
    isExpired: false,
  },
  {
    newsId: 1,
    reportSlug: 'receivables',
    reportDisplayName: 'Receivables',
    headline: 'Trends and week comparison now open in about a second',
    summary:
      'The weekly trend and the week-by-week comparison used to take around six seconds to draw. They now appear in roughly one. Nothing about the figures has changed — the same numbers, arriving sooner.',
    details:
      'The two pages were rebuilding every week from scratch each time you opened them, which meant reading the whole aging table once per week on the chart. A twelve-week trend meant twelve passes.\n\nThose weekly totals are now worked out once and kept, so opening a trend reads a prepared answer instead of recomputing one. The stored figures are checked against the original calculation before anything is published, so a difference would be caught rather than shown to you.\n\nWhat has not changed: who can see what. Your rows, your columns and your currency behave exactly as before.',
    publishedUtc: daysFromNow(-11),
    expiresUtc: daysFromNow(19),
    isRetired: false,
    isExpired: false,
  },
  {
    // Already past. Invisible by default, and the reason "Show past updates"
    // has something to reveal when a System Admin turns it on.
    newsId: 0,
    reportSlug: 'receivables',
    reportDisplayName: 'Receivables',
    headline: 'Customer column now shows the full trading name',
    summary:
      'The customer column was truncating longer trading names. It now shows them in full, and the column widens to fit rather than clipping.',
    details: null,
    publishedUtc: daysFromNow(-64),
    expiresUtc: daysFromNow(-34),
    isRetired: false,
    isExpired: true,
  },
]

/** The status envelope the API returns, derived from the list. */
function demoStatus(): MaintenanceStatus {
  return {
    active: demoWindows.length > 0,
    windows: [...demoWindows],
    window: demoWindows.find((w) => w.scope === 'platform') ?? demoWindows[0] ?? null,
  }
}

/**
 * Does a platform-wide window REFUSE this read? Report scopes do not.
 *
 * `allowReadGrace` is honoured here because MaintenanceMiddleware honours it:
 * under read grace a GET is served with a Warning header, not refused. The demo
 * used to refuse regardless, so ticking "Keep serving reads" produced a screen
 * carrying both the strip that says figures are still arriving and the panel
 * that says they are not — a reviewer evaluating that checkbox in the demo was
 * being shown the opposite of what it does.
 */
function demoPlatformWindow(): MaintenanceWindow | undefined {
  return demoWindows.find((w) => w.scope === 'platform' && !w.allowReadGrace)
}

/**
 * The refusal a gated demo call rejects with — and the signal that goes with it.
 *
 * Both halves, in one place, because the real client does both in one place too:
 * a demo that threw the error without announcing it would leave a reviewer who
 * opens a window mid-session watching the old door-by-door behaviour and
 * concluding the change does not work.
 */
function demoRefusal(w: MaintenanceWindow): ApiError {
  const failure = {
    kind: 'maintenance' as const,
    message: w.message,
    scope: w.scope,
    expectedEndUtc: w.expectedEndUtc,
    retryAfterSeconds: 300,
  }
  maintenanceDetected(failure)
  return new ApiError(failure)
}

/** One open window in the shape /api/v1/me reports it. */
function demoNotice(w: MaintenanceWindow): MaintenanceNotice {
  return {
    scope: w.scope,
    slug: w.scope.startsWith('report:') ? w.scope.slice('report:'.length) : null,
    message: w.message,
    expectedEndUtc: w.expectedEndUtc,
    allowReadGrace: w.allowReadGrace,
  }
}

/**
 * The maintenance block on /api/v1/me, computed the way IdentityEndpoints
 * computes it: platform scope short-circuits, only the caller's own reports are
 * named, and View As never bypasses.
 *
 * Reproduced rather than stubbed because the demo is what a reviewer is shown.
 * A demo signed in as a System Admin would otherwise never render the closed
 * screens at all — the admin passes through every one of them — so the feature
 * would look like it does nothing. Turning View As on is how a reviewer sees
 * what everybody else sees, which is the same trick the real platform offers.
 */
function demoMaintenance(reports: string[], bypass: boolean): MaintenanceState {
  // ANY platform window, grace or not — unlike demoPlatformWindow above, which
  // answers the narrower question "is this read refused". /me reports what is
  // OPEN; the client decides from allowReadGrace whether that closes a screen or
  // annotates it, exactly as IdentityEndpoints leaves that decision.
  const platform = demoWindows.find((w) => w.scope === 'platform')
  if (platform) return { bypass, platform: demoNotice(platform), reports: [] }

  return {
    bypass,
    platform: null,
    reports: demoWindows
      .filter((w) => w.scope.startsWith('report:'))
      .map(demoNotice)
      .filter((n) => n.slug !== null && reports.includes(n.slug)),
  }
}

/**
 * The three platform roles, as 008 defines them.
 *
 * seesAllData is the only property the screen renders, and it is the only one
 * that matters: it is the difference between a role that manages a list and a
 * role that reads every company's book.
 */
const DEMO_ROLE_META: Record<PlatformRoleCode, { roleName: string; seesAllData: boolean }> = {
  SystemAdmin: { roleName: 'System Admin', seesAllData: true },
  MasterViewer: { roleName: 'Master Viewer', seesAllData: true },
  SystemMasterAdmin: { roleName: 'System Master Admin', seesAllData: true },
  SystemDevelopment: { roleName: 'System Development', seesAllData: true },
}

/** ?model=v2: the System Master Admin, set by a database script, never on the screen. */
const DEMO_MASTER_ADMIN: RoleHolder = {
  email: 'group.it@redachem.com', displayName: 'Group IT', roleCode: 'SystemMasterAdmin', roleName: 'System Master Admin',
  seesAllData: true, grantedBy: 'set-system-master-admin.sql', grantedUtc: '2026-10-20T08:00:00Z', reason: 'The switch hour',
}

/**
 * Mutable, so granting and revoking are genuinely interactive in demo mode.
 *
 * Seeded with one person the permission data does NOT know, because that is the
 * state a reviewer most needs to see: granted the report, and will still open it
 * to an empty grid. It reads as a bug unless the screen says why.
 */
let demoAccess: ReportAccessEntry[] = [
  { email: 'ziad.katabi@redachem.com', displayName: 'Ziad Katabi', grantedBy: 'demo.user@redachem.com',
    grantedUtc: '2026-07-14T09:12:00Z', reason: 'Finance lead', knownToD365: true },
  { email: 'atif.majeed@redachem.com', displayName: 'Atif Majeed', grantedBy: 'demo.user@redachem.com',
    grantedUtc: '2026-07-14T09:12:00Z', reason: 'BU manager', knownToD365: true },
  { email: 'nour.habli@redachem.com', displayName: 'Nour Habli', grantedBy: 'demo.user@redachem.com',
    grantedUtc: '2026-07-21T11:40:00Z', reason: null, knownToD365: true },
  { email: 'new.joiner@redachem.com', displayName: null, grantedBy: 'demo.user@redachem.com',
    grantedUtc: '2026-08-04T08:02:00Z', reason: 'Starts Monday', knownToD365: false },
]

/**
 * The column→permission mapping, in the shape 004 returns.
 *
 * A representative slice rather than all 69: one of each STATUS, so every
 * branch of the screen is reachable — required, mapped to a wide permission,
 * mapped to a narrow one, and one deliberately UNMAPPED so the
 * "hidden from everyone" warning can be seen without breaking anything.
 */
let demoColumns: ColumnGovernanceRow[] = [
  { reportColumnId: 4, columnName: 'BU', displayName: 'Business Unit', isRequired: true, sortOrder: 40, permissions: null, status: 'always visible' },
  { reportColumnId: 7, columnName: 'Customer_Name', displayName: 'Customer', isRequired: true, sortOrder: 70, permissions: null, status: 'always visible' },
  { reportColumnId: 21, columnName: 'Salesperson_Name', displayName: 'Salesperson', isRequired: false, sortOrder: 90, permissions: 'SLS_Mod', status: 'mapped' },
  { reportColumnId: 27, columnName: 'Amt_Remaining_Fun', displayName: 'Amount Remaining', isRequired: false, sortOrder: 220, permissions: 'AR_Mod', status: 'mapped' },
  { reportColumnId: 30, columnName: 'Credit_Limit', displayName: 'Credit Limit', isRequired: false, sortOrder: 240, permissions: 'Fin_Mod', status: 'mapped' },
  { reportColumnId: 34, columnName: 'Total Receivables', displayName: 'Total Receivables', isRequired: false, sortOrder: 300, permissions: 'AR_Mod', status: 'mapped' },
  { reportColumnId: 99, columnName: 'Dim_Segment', displayName: 'Segment', isRequired: false, sortOrder: 700, permissions: null, status: 'UNMAPPED - hidden from everyone' },
]

/** Holder counts as measured on the clone, 5 Aug 2026. Payroll_Mod really is 0. */
const DEMO_CATALOGUE: PermissionCatalogRow[] = [
  { permissionCode: 'AR_Mod', displayName: 'Accounts Receivable', kind: 'module', notes: '369 of 377 people', peopleHolding: 369 },
  { permissionCode: 'SLS_Mod', displayName: 'Sales', kind: 'module', notes: 'One row is blank; treated as deny', peopleHolding: 370 },
  { permissionCode: 'Fin_Mod', displayName: 'Finance', kind: 'module', notes: '706 of 2222 rows', peopleHolding: 43 },
  { permissionCode: 'AP_Mod', displayName: 'Accounts Payable', kind: 'module', notes: '746 of 2222 rows', peopleHolding: 47 },
  { permissionCode: 'Inv_Mod', displayName: 'Inventory', kind: 'module', notes: '2209 of 2222 rows', peopleHolding: 366 },
  { permissionCode: 'Payroll_Mod', displayName: 'Payroll', kind: 'module', notes: 'NOBODY holds this today', peopleHolding: 0 },
  { permissionCode: 'Sales_Cost', displayName: 'Sales cost', kind: 'cost', notes: '214 of 377 people', peopleHolding: 214 },
  { permissionCode: 'Inv_Cost', displayName: 'Inventory cost', kind: 'cost', notes: 'Three-valued; 2 rows hold Full', peopleHolding: 216 },
]

/*
 * COLUMN GROUPS (D53, owner 28 Oct 2026): made by an admin, one report each, and
 * the new model's column settings, each column Everyone, hidden or gated by its
 * report's groups. A few per report, so every state of Column definition shows.
 */
interface DemoColumnGroup { id: number; report: string; name: string; description: string | null; version: string }

const DEMO_COLUMN_GROUPS: DemoColumnGroup[] = [
  { id: 2001, report: 'receivables', name: 'Receivables amounts', description: 'The ageing buckets and the totals.', version: '2026-10-28T09:00:00.000Z' },
  { id: 2002, report: 'receivables', name: 'Credit', description: 'Credit limit and payment terms: Finance only.', version: '2026-10-28T09:01:00.000Z' },
  { id: 2003, report: 'sales', name: 'Sales amounts', description: 'Sales value, quantity and average price.', version: '2026-10-28T09:02:00.000Z' },
  { id: 2004, report: 'sales', name: 'Sales cost', description: 'Cost, GP and GP%.', version: '2026-10-28T09:03:00.000Z' },
  { id: 2005, report: 'inventory-aging', name: 'Stock value', description: 'Unit cost and stock value.', version: '2026-10-28T09:04:00.000Z' },
  { id: 2006, report: 'statement-of-account', name: 'Statement amounts', description: null, version: '2026-10-28T09:05:00.000Z' },
]
let demoNextGroupId = 2100

interface DemoColumnSetting {
  id: number
  report: string
  columnName: string
  displayName: string
  isRequired: boolean
  sortOrder: number
  visibility: 'EVERYONE' | 'GROUPS' | 'HIDDEN'
  groups: number[]
  version: string | null
}

const DEMO_V2_COLUMNS: DemoColumnSetting[] = [
  { id: 4, report: 'receivables', columnName: 'BU', displayName: 'Business Unit', isRequired: true, sortOrder: 40, visibility: 'EVERYONE', groups: [], version: null },
  { id: 7, report: 'receivables', columnName: 'Customer_Name', displayName: 'Customer', isRequired: true, sortOrder: 70, visibility: 'EVERYONE', groups: [], version: null },
  { id: 21, report: 'receivables', columnName: 'Salesperson_Name', displayName: 'Salesperson', isRequired: false, sortOrder: 90, visibility: 'EVERYONE', groups: [], version: '2026-10-28T09:10:00.000Z' },
  { id: 27, report: 'receivables', columnName: 'Amt_Remaining_Fun', displayName: 'Amount Remaining', isRequired: false, sortOrder: 220, visibility: 'GROUPS', groups: [2001], version: '2026-10-28T09:11:00.000Z' },
  { id: 30, report: 'receivables', columnName: 'Credit_Limit', displayName: 'Credit Limit', isRequired: false, sortOrder: 240, visibility: 'GROUPS', groups: [2002], version: '2026-10-28T09:12:00.000Z' },
  { id: 34, report: 'receivables', columnName: 'Total Receivables', displayName: 'Total Receivables', isRequired: false, sortOrder: 300, visibility: 'GROUPS', groups: [2001, 2002], version: '2026-10-28T09:13:00.000Z' },
  { id: 99, report: 'receivables', columnName: 'Dim_Segment', displayName: 'Segment', isRequired: false, sortOrder: 700, visibility: 'HIDDEN', groups: [], version: null },
  { id: 201, report: 'sales', columnName: 'Customer_Name', displayName: 'Customer', isRequired: true, sortOrder: 10, visibility: 'EVERYONE', groups: [], version: null },
  { id: 202, report: 'sales', columnName: 'Item_Name', displayName: 'Item', isRequired: true, sortOrder: 20, visibility: 'EVERYONE', groups: [], version: null },
  { id: 210, report: 'sales', columnName: 'Extended_Price', displayName: 'Sales Amount', isRequired: false, sortOrder: 100, visibility: 'GROUPS', groups: [2003], version: '2026-10-28T09:20:00.000Z' },
  { id: 211, report: 'sales', columnName: 'Quantity', displayName: 'Quantity', isRequired: false, sortOrder: 110, visibility: 'EVERYONE', groups: [], version: '2026-10-28T09:21:00.000Z' },
  { id: 212, report: 'sales', columnName: 'Cost', displayName: 'Cost', isRequired: false, sortOrder: 120, visibility: 'GROUPS', groups: [2004], version: '2026-10-28T09:22:00.000Z' },
  { id: 213, report: 'sales', columnName: 'GP', displayName: 'GP', isRequired: false, sortOrder: 130, visibility: 'GROUPS', groups: [2004], version: '2026-10-28T09:23:00.000Z' },
  { id: 214, report: 'sales', columnName: '%GP', displayName: 'GP%', isRequired: false, sortOrder: 140, visibility: 'GROUPS', groups: [2004], version: '2026-10-28T09:24:00.000Z' },
  { id: 215, report: 'sales', columnName: 'Line_Discount', displayName: 'Discount', isRequired: false, sortOrder: 150, visibility: 'HIDDEN', groups: [], version: null },
]


/* What a column group unlocks, in the report's order, as 052's sec.fn_Column_Group_Unlocks_MBI says it. */
function demoGroupUnlocks(groupId: number): string | null {
  const cols = DEMO_V2_COLUMNS.filter((c) => c.visibility === 'GROUPS' && c.groups.includes(groupId))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.displayName.localeCompare(b.displayName))
  return cols.length ? cols.map((c) => c.displayName).join(', ') : null
}

/** Two System Admins on purpose, so the self-demotion guard is reachable in the demo. */
let demoRoles: RoleHolder[] = [
  { email: 'demo.user@redachem.com', displayName: 'Demo User', roleCode: 'SystemAdmin',
    roleName: 'System Admin', seesAllData: true, grantedBy: 'platform', grantedUtc: '2026-06-01T00:00:00Z',
    reason: 'Platform owner' },
  { email: 'ziad.katabi@redachem.com', displayName: 'Ziad Katabi', roleCode: 'SystemAdmin',
    roleName: 'System Admin', seesAllData: true, grantedBy: 'demo.user@redachem.com',
    grantedUtc: '2026-07-02T10:00:00Z', reason: 'Second administrator, so nobody is the only one' },
  { email: 'atif.majeed@redachem.com', displayName: 'Atif Majeed', roleCode: 'MasterViewer',
    roleName: 'Master Viewer', seesAllData: true, grantedBy: 'demo.user@redachem.com',
    grantedUtc: '2026-07-02T10:05:00Z', reason: 'Reads every company for the quarterly review' },
]


/* ------------------------------------------------------------ material planning
   ONE list of items, and every Material Planning screen aggregates from it.
   That is the rule the other reports follow here too: if each tab invented its
   own numbers they would disagree, and a reviewer would reasonably believe the
   disagreement was real.

   The shapes to notice, because they are the ones production actually has:
     * MP-004 carries NO cost figures at all - over half of production is like this
     * MP-005 has NO pallet size             - so pallets is null, never zero
     * MP-006 sits below its minimum         - the case the screen exists to show
     * PO-2004 has a null "still due"        - null means unknown, not zero
   ------------------------------------------------------------------------------- */
interface DemoMpItem {
  company: string; companyName: string; item: string; product: string; group: string
  bu: string; buName: string; bt: string; team: string; warehouse: string | null; supplier: string
  /* Null quantities are the shape 55% of production rows have: a product
     with no inventory record anywhere. MP-007 is that row. */
  onHand: number | null; physical: number | null; onOrder: number | null; reserved: number | null
  /* RESERVORDERED, the manager's "Open SOs". Deliberately unlike `reserved`
     on every row, because in production they are different columns that
     disagree on 1,264 rows and a demo where they match teaches the opposite.
     MP-002 and MP-005 are short of their open SOs; the rest are not. */
  openSo: number | null
  /* THE UNIT THE QUANTITIES ARE IN. Seeded so both answers are reachable:
     r002 holds kg and t, r010 holds kg and l, so a COMPANY row mixes units in
     both — but r002/C1, r010/C1 and r010/LUB are each single, so the screen's
     other branch is reachable too. That is the shape production has: 37 of 43
     companies mix, and 110 of 226 business units do not. */
  unit: string | null
  /* `minStock` is the legacy column; `minInv` is the minimum the screen shows. */
  minStock: number; minInv: number; pallet: number | null
  lastMove: string | null; days: number | null
  cost: number | null; value: number | null; unitCost: number | null
  /* Signed quantity sold this year and last; a negative is a credit note. */
  sold: number; soldPY: number
}

const DEMO_MP_ITEMS: DemoMpItem[] = [
  { company: 'r002', companyName: 'REDAChem KSA', item: 'MP-001', product: 'Natrosol 250 HBR', group: 'Cellulosics', unit: 'kg',
    bu: 'C1', buName: 'Chemicals', bt: 'C1-DRIL', team: 'Drilling', warehouse: 'JED-01', supplier: 'Ashland India Private Limited',
    onHand: 12400, physical: 12400, onOrder: 6000, reserved: 800, openSo: 15000, minStock: 0, minInv: 8000, pallet: 40,
    lastMove: '2026-07-28', days: 7, cost: 148800, value: 152300, unitCost: 12, sold: 9800, soldPY: 8600 },
  { company: 'r002', companyName: 'REDAChem KSA', item: 'MP-002', product: 'Xanthan Gum XCD', group: 'Polymers', unit: 'kg',
    bu: 'C1', buName: 'Chemicals', bt: 'C1-DRIL', team: 'Drilling', warehouse: 'JED-01', supplier: 'Fufeng Group',
    onHand: 3200, physical: 3200, onOrder: 0, reserved: 0, openSo: 4200, minStock: 0, minInv: 2500, pallet: 25,
    lastMove: '2026-02-11', days: 175, cost: 92800, value: 95100, unitCost: 29, sold: 2650, soldPY: 3100 },
  { company: 'r002', companyName: 'REDAChem KSA', item: 'MP-003', product: 'Calcium Carbonate F', group: 'Minerals', unit: 'kg',
    bu: 'C3', buName: 'Industrial', bt: 'C3-GEN', team: 'General', warehouse: 'DMM-02', supplier: 'Omya Gulf',
    onHand: 54000, physical: 54000, onOrder: 20000, reserved: 4000, openSo: 22000, minStock: 0, minInv: 30000, pallet: 60,
    lastMove: '2026-08-01', days: 3, cost: 43200, value: 44600, unitCost: 0.8, sold: 41000, soldPY: 37500 },
  { company: 'r002', companyName: 'REDAChem KSA', item: 'MP-004', product: 'Barite API 4.2', group: 'Minerals', unit: 't',
    bu: 'C3', buName: 'Industrial', bt: 'C3-GEN', team: 'General', warehouse: 'DMM-02', supplier: 'Anglo Minerals',
    onHand: 88000, physical: 88000, onOrder: 0, reserved: 0, openSo: 0, minStock: 0, minInv: 0, pallet: 80,
    lastMove: null, days: null, cost: null, value: null, unitCost: null, sold: 60000, soldPY: 64000 },
  { company: 'r010', companyName: 'REDAChem UAE', item: 'MP-005', product: 'Soda Ash Dense', group: 'Minerals', unit: 'kg',
    bu: 'C1', buName: 'Chemicals', bt: 'C1-GEN', team: 'General', warehouse: 'DXB-01', supplier: 'Ciner Resources',
    onHand: 26000, physical: 26000, onOrder: 12000, reserved: 1500, openSo: 40000, minStock: 0, minInv: 15000, pallet: null,
    lastMove: '2025-11-20', days: 258, cost: 20800, value: 21400, unitCost: 0.8, sold: 18200, soldPY: 0 },
  { company: 'r010', companyName: 'REDAChem UAE', item: 'MP-006', product: 'Kluberoil 4 UH1-460', group: 'Lubricants', unit: 'l',
    bu: 'LUB', buName: 'Lubricants', bt: 'LUB-FOOD', team: 'Food grade', warehouse: 'DXB-01', supplier: 'Kluber Lubrication',
    onHand: 340, physical: 340, onOrder: 900, reserved: 120, openSo: 300, minStock: 0, minInv: 1200, pallet: 12,
    lastMove: '2026-07-30', days: 5, cost: 27200, value: 28900, unitCost: 80, sold: 1450, soldPY: 1620 },
  /* The empty row: a product that exists and has never had stock. Hidden by
     default, shown with "Every item", drawn blank rather than as zeros. */
  { company: 'r002', companyName: 'REDAChem KSA', item: 'MP-007', product: 'Ferric Chloride 40%', group: 'Chemicals', unit: 'l',
    bu: 'C1', buName: 'Chemicals', bt: 'C1-GEN', team: 'General', warehouse: null, supplier: 'Gulf Chem Supply',
    onHand: null, physical: null, onOrder: null, reserved: null, openSo: null, minStock: 0, minInv: 0, pallet: null,
    lastMove: null, days: null, cost: null, value: null, unitCost: null, sold: 0, soldPY: 0 },
]

/**
 * A date `n` days from today as `YYYY-MM-DD`. Used by the two orders whose
 * meaning IS their distance from today; everything else here is a fixed date
 * on purpose, so screenshots stay comparable.
 */
function mpDay(offset: number): string {
  const d = new Date()
  d.setDate(d.getDate() + offset)
  return d.toISOString().slice(0, 10)
}

interface DemoMpPo {
  company: string; po: string; line: number; item: string; itemName: string
  supplier: string; site: string; currency: string; category: string
  poDate: string; poYear: number; delivery: string; eta: string; grn: string | null
  etd: string | null; ship: string | null; ship2: string | null; readiness: string | null
  terms: string; mawb: string | null; mode: string; responsible: string
  lineStatus: number; approval: number; orderStatus: string; progress: string
  orderProgress: string; shipmentStatus: string
  /* THE LINE's own unit, which is not always the item's: PO-2004 is bought
     by the drum where the product is held in litres, mirroring the 1,857 of
     33,476 production lines whose purchase unit differs from the master's. */
  unit: string
  /* Created_By - who raised the order, which is not the same person as
     `responsible`, who is chasing it. Both are on the manager's column list. */
  orderer: string
  /* The flag the headline tiles define "open" by. Seeded across all three
     production states so the two definitions of open visibly differ. */
  fileClosed: number | null
  qty: number; stillDue: number | null; price: number; basis: number; amount: number
}

const DEMO_MP_POS: DemoMpPo[] = [
  { company: 'r002', po: 'PO-2001', line: 1, item: 'MP-001', itemName: 'Natrosol 250 HBR',
    supplier: 'Ashland India Private Limited', site: 'JED-01', currency: 'USD', category: 'Principal', unit: 'kg',
    poDate: '2026-07-14', poYear: 2026, delivery: '2026-09-20', eta: '2026-09-18', grn: null,
    etd: '2026-08-30', ship: '2026-08-31', ship2: '2026-09-01', readiness: '2026-08-20',
    terms: 'CIF', mawb: null, mode: 'Sea', responsible: 'Hana Mostafa', orderer: 'Sara Al Otaibi',
    lineStatus: 1, approval: 40, orderStatus: 'Open Order', progress: 'On the sea',
    orderProgress: '20-GIT', shipmentStatus: 'In Transit', fileClosed: 0,
    qty: 6000, stillDue: 6000, price: 12, basis: 1, amount: 72000 },
  { company: 'r002', po: 'PO-2002', line: 1, item: 'MP-003', itemName: 'Calcium Carbonate F',
    supplier: 'Omya Gulf', site: 'DMM-02', currency: 'SAR', category: 'Regional', unit: 'kg',
    poDate: '2026-08-01', poYear: 2026, delivery: '2026-09-30', eta: '2026-09-26', grn: null,
    etd: null, ship: null, ship2: null, readiness: '2026-09-05',
    terms: 'FOB', mawb: null, mode: 'Land', responsible: 'Hana Mostafa', orderer: 'Nabil Haddad',
    lineStatus: 1, approval: 40, orderStatus: 'Open Order', progress: 'Waiting Shipping Details',
    orderProgress: '10-WOC', shipmentStatus: 'Pending', fileClosed: null,
    qty: 20000, stillDue: 20000, price: 3, basis: 1, amount: 60000 },
  { company: 'r010', po: 'PO-2003', line: 1, item: 'MP-005', itemName: 'Soda Ash Dense',
    supplier: 'Ciner Resources', site: 'DXB-01', currency: 'AED', category: 'Principal', unit: 'kg',
    poDate: '2026-08-11', poYear: 2026, delivery: '2026-10-05', eta: '2026-10-02', grn: null,
    etd: '2026-09-10', ship: null, ship2: null, readiness: '2026-09-02',
    terms: 'CFR', mawb: null, mode: 'Sea', responsible: 'Omar Farouk', orderer: 'Sara Al Otaibi',
    lineStatus: 2, approval: 40, orderStatus: 'Open Order', progress: 'Waiting Sailing',
    orderProgress: 'Waiting Order Confirmation', shipmentStatus: 'Pending', fileClosed: 0,
    qty: 12000, stillDue: 12000, price: 3, basis: 1, amount: 36000 },
  { company: 'r010', po: 'PO-2004', line: 1, item: 'MP-006', itemName: 'Kluberoil 4 UH1-460',
    supplier: 'Kluber Lubrication', site: 'DXB-01', currency: 'EUR', category: 'Principal', unit: 'drum',
    poDate: '2026-06-02', poYear: 2026, delivery: '2026-08-15', eta: '2026-08-12', grn: '2026-08-14',
    etd: '2026-07-20', ship: '2026-07-21', ship2: '2026-07-21', readiness: '2026-07-10',
    terms: 'DAP', mawb: '020-44817736', mode: 'Air', responsible: 'Omar Farouk', orderer: 'Nabil Haddad',
    lineStatus: 1, approval: 40, orderStatus: 'Open Order', progress: 'Goods Received',
    orderProgress: '60-RFC', shipmentStatus: 'Delivered', fileClosed: null,
    qty: 900, stillDue: null, price: 62, basis: 1, amount: 55800 },
  { company: 'r002', po: 'PO-1990', line: 1, item: 'MP-002', itemName: 'Xanthan Gum XCD',
    supplier: 'Fufeng Group', site: 'JED-01', currency: 'USD', category: 'Principal', unit: 'kg',
    poDate: '2026-03-09', poYear: 2026, delivery: '2026-05-20', eta: '2026-05-18', grn: '2026-05-19',
    etd: '2026-04-22', ship: '2026-04-23', ship2: '2026-04-23', readiness: '2026-04-10',
    terms: 'CIF', mawb: null, mode: 'Sea', responsible: 'Hana Mostafa', orderer: 'Sara Al Otaibi',
    lineStatus: 3, approval: 50, orderStatus: 'Invoiced', progress: 'Invoiced',
    orderProgress: '70-Costed', shipmentStatus: 'Delivered', fileClosed: 1,
    qty: 4000, stillDue: 0, price: 29, basis: 1, amount: 116000 },
  /* Last year's order for the same item, so the Item Summary has a prior
     year to compare against. */
  { company: 'r002', po: 'PO-1810', line: 1, item: 'MP-001', itemName: 'Natrosol 250 HBR',
    supplier: 'Ashland India Private Limited', site: 'JED-01', currency: 'USD', category: 'Principal', unit: 'kg',
    poDate: '2025-06-18', poYear: 2025, delivery: '2025-08-25', eta: '2025-08-22', grn: '2025-08-24',
    etd: '2025-08-01', ship: '2025-08-02', ship2: '2025-08-02', readiness: '2025-07-20',
    terms: 'CIF', mawb: null, mode: 'Sea', responsible: 'Hana Mostafa', orderer: 'Nabil Haddad',
    lineStatus: 3, approval: 50, orderStatus: 'Invoiced', progress: 'Invoiced',
    orderProgress: 'Costed', shipmentStatus: 'Delivered', fileClosed: 1,
    qty: 5000, stillDue: 0, price: 11, basis: 1, amount: 55000 },
  /* -------------------------------------------------------------------------
     THE TWO ROWS THAT KEEP ARRIVING SOON AND LATE SHIPMENTS ANSWERING.

     Every other order above carries a fixed date, which is right for a demo
     that must look the same in every screenshot. These two cannot: their whole
     point is a relationship to TODAY, and a fixed date means the two new
     screens are empty a month from now and nobody notices until a reviewer
     opens them. So these two alone are relative - one arriving in ten days,
     one twelve days overdue with no goods receipt.

     The fixed orders still drift through the windows as the calendar moves,
     which is honest: an order that was arriving soon in September IS late by
     November, and the demo showing that is the demo telling the truth.
     ------------------------------------------------------------------------- */
  { company: 'r002', po: 'PO-2005', line: 1, item: 'MP-002', itemName: 'Xanthan Gum XCD',
    supplier: 'Fufeng Group', site: 'JED-01', currency: 'USD', category: 'Principal', unit: 'kg',
    poDate: mpDay(-64), poYear: new Date().getFullYear(), delivery: mpDay(12), eta: mpDay(10), grn: null,
    etd: mpDay(-16), ship: mpDay(-15), ship2: mpDay(-15), readiness: mpDay(-24),
    terms: 'CIF', mawb: null, mode: 'Sea', responsible: 'Hana Mostafa', orderer: 'Sara Al Otaibi',
    lineStatus: 1, approval: 40, orderStatus: 'Open Order', progress: 'On the sea',
    orderProgress: '20-GIT', shipmentStatus: 'Arriving Soon', fileClosed: 0,
    qty: 4200, stillDue: 4200, price: 28, basis: 1, amount: 117600 },
  { company: 'r010', po: 'PO-2006', line: 1, item: 'MP-006', itemName: 'Kluberoil 4 UH1-460',
    supplier: 'Kluber Lubrication', site: 'DXB-01', currency: 'EUR', category: 'Principal', unit: 'drum',
    poDate: mpDay(-96), poYear: new Date().getFullYear(), delivery: mpDay(-10), eta: mpDay(-12), grn: null,
    etd: mpDay(-40), ship: mpDay(-38), ship2: mpDay(-38), readiness: mpDay(-52),
    terms: 'DAP', mawb: '020-44902118', mode: 'Air', responsible: 'Omar Farouk', orderer: 'Nabil Haddad',
    lineStatus: 2, approval: 40, orderStatus: 'Open Order', progress: 'Waiting Shipping Details',
    orderProgress: '20-GIT', shipmentStatus: 'Late', fileClosed: 0,
    qty: 300, stillDue: 300, price: 60, basis: 1, amount: 18000 },
]

/* Local currency here is 3.75 to the dollar, so a USD figure is the local one
   divided by 3.75 - and a conversion in the wrong direction is visible at a
   glance rather than arguable. */
const DEMO_MP_RATE = 3.75
/**
 * Each company books in its own currency, one per company, as production does
 * (100% populated, exactly one per company, 22 of them). Two are enough to
 * make a cross-company total genuinely mixed. Both convert at the one demo
 * rate above, which real data would not do — the demo is about the LABEL.
 */
const DEMO_MP_CURRENCY: Record<string, string> = { r002: 'SAR', r010: 'AED' }
const mpCur = (company: string) => DEMO_MP_CURRENCY[company] ?? 'SAR'
const mpMoney = (v: number | null, usd: boolean, scale = 1) =>
  v === null ? null : usd ? Math.round((v / DEMO_MP_RATE) * scale * 100) / 100 : v

/* ------------------------------------------------------------------ inventory aging
   Fourteen invented lots, every date RELATIVE TO TODAY so the buckets they land
   in never drift: each shape the real data has is here once — drop-ship, the
   1 Jan 1900 expiry placeholder, no expiry date, an arrival in the future, a lot
   with no business unit, negative stock, and one product in two companies.
   ----------------------------------------------------------------------------- */
interface DemoInvLot {
  company: string; companyName: string; bu: string | null; bt: string
  item: string; product: string; description: string; supplier: string | null
  lot: string; warehouse: string; unit: string | null
  qty: number; cost: number; value: number
  arrival: string | null; expiry: string | null; mfg: string | null; currency: string
}
/** A date `offset` days from today, as the API sends one. */
const invDay = (offset: number) => {
  const d = new Date()
  d.setUTCHours(0, 0, 0, 0)
  d.setUTCDate(d.getUTCDate() + offset)
  return d.toISOString()
}
const invLot = (
  company: string, bu: string | null, bt: string, item: string, product: string, lot: string, warehouse: string,
  unit: string | null, qty: number, cost: number, arrival: number | null, expiry: number | 'placeholder' | null,
  supplier: string | null = 'Supplier Demo',
): DemoInvLot => ({
  company, companyName: company === 'r002' ? 'REDAChem KSA' : 'REDAChem UAE',
  bu, bt, item, product, description: `${product}, demo lot`, supplier, lot, warehouse, unit,
  qty, cost, value: Math.round(qty * cost * 100) / 100,
  arrival: arrival === null ? null : invDay(arrival),
  expiry: expiry === null ? null : expiry === 'placeholder' ? '1900-01-01T00:00:00.000Z' : invDay(expiry),
  mfg: arrival === null ? null : invDay(arrival - 30),
  currency: company === 'r002' ? 'SAR' : 'AED',
})
const DEMO_INV_LOTS: DemoInvLot[] = [
  invLot('r002', 'FI', 'FI-BKR', 'INV-1001', 'Alkyd Resin 60%', 'B24-0115', 'KHO', 'KG', 1200, 18.5, -12, 210),
  invLot('r002', 'FI', 'FI-BKR', 'INV-1001', 'Alkyd Resin 60%', 'B24-0098', 'KHO', 'KG', 800, 18.5, -75, 40),
  invLot('r002', 'FI', 'FI-BKR', 'INV-1001', 'Alkyd Resin 60%', 'B23-1120', 'JED', 'KG', 300, 18.5, -260, -14),
  invLot('r002', 'FI', 'FI-OTHER', 'INV-1044', 'Titanium Dioxide R-902', 'TD-5521', 'KHO', 'KG', 2500, 9.2, -150, 500),
  invLot('r002', 'FI', 'FI-OTHER', 'INV-1044', 'Titanium Dioxide R-902', 'TD-5402', 'DROPSHIP', 'KG', 400, 9.2, -20, 600),
  invLot('r002', 'LAB', 'LAB-A', 'INV-2210', 'Reagent Grade Ethanol', 'ET-339', 'KHO', 'L', 640, 4.75, -400, 'placeholder'),
  invLot('r002', 'LAB', 'LAB-A', 'INV-2215', 'Buffer Solution pH 7', 'BF-0071', 'KHO', 'EA', 96, 31, -45, 0),
  invLot('r002', 'LAB', 'LAB-A', 'INV-2216', 'Calibration Standard', 'CS-12', 'DIC', 'EA', 30, 120, 5, 80),
  invLot('r002', null, 'BT-POLY-RAW', 'INV-3301', 'Polyol Base', 'PB-881', 'KHO', 'KG', 5000, 2.1, null, 120, null),
  invLot('r010', 'FI', 'FI-BKR', 'INV-1001', 'Alkyd Resin 60%', 'A-7781', 'JAFZA', 'KG', 950, 19, -33, 300),
  invLot('r010', 'FI', 'FI-BKR', 'INV-4410', 'Defoamer DF-20', 'DF-220', 'JAFZA', 'L', 210, 14, -190, 25),
  invLot('r010', 'LUB', 'LUB-A', 'INV-5120', 'Lithium Grease EP2', 'LG-0912', 'JAFZA', 'KG', 1800, 6.4, -700, -120),
  invLot('r010', 'LUB', 'LUB-A', 'INV-5120', 'Lithium Grease EP2', 'LG-1003', 'JAFZA', 'KG', -50, 6.4, -95, 350),
  invLot('r010', 'LUB', 'LUB-A', 'INV-5133', 'Gear Oil 220', 'GO-44', 'DROP', 'L', 1000, 3.3, -10, null),
]
/** Whole days from a to b, on UTC dates — DATEDIFF(day, a, b). */
const invDays = (a: string, b: string) =>
  Math.round((Date.parse(b.slice(0, 10)) - Date.parse(a.slice(0, 10))) / 86_400_000)
/** One lot's age and expiry, worked out exactly as 040 does. */
function invFacts(l: DemoInvLot) {
  const today = invDay(0)
  const drop = l.warehouse.toUpperCase().includes('DROP')
  const daysOld = l.arrival === null ? null : invDays(l.arrival, today)
  const daysToExpire = l.expiry === null ? null : invDays(today, l.expiry)
  const band = (d: number) =>
    d <= 30 ? '0_30' : d <= 60 ? '31_60' : d <= 90 ? '61_90' : d <= 120 ? '91_120'
      : d <= 180 ? '121_180' : d <= 240 ? '181_240' : d <= 365 ? '241_365' : 'over365'
  const age: InvAgeBand = drop ? 'drop' : daysOld === null || daysOld < 0 ? 'unknown' : band(daysOld)
  const expiryBand: InvExpiryBand = daysToExpire === null ? 'none' : daysToExpire < 0 ? 'expired' : band(daysToExpire)
  return { l, drop, daysOld, daysToExpire, age, expiryBand }
}

/**
 * What the demo does with a currency choice, mirroring the procedure's shape:
 * convert to dollars, THEN multiply by one rate. Never a second way of
 * computing money, because a demo that converts differently from the server is
 * a demo that agrees with nothing.
 *
 * `div` is the divisor to apply to a local figure, so one number covers both
 * steps: the peg for dollars, and the peg over the chosen rate for a currency.
 */
/**
 * The MONEY columns of a transaction row — the only ones a currency change may
 * touch. daysOld and daysOverdue are numbers too, and dividing a day count by
 * an exchange rate is the kind of wrong that looks plausible in a grid.
 */
const MONEY_COLS = new Set([
  'advancePayment', 'totalReceivables', 'totalOverdue', 'over30DaysOverdue',
  'd0To30', 'd31To60', 'd61To90', 'd91To120', 'd121To180', 'd181To365', 'dOver365',
])

/**
 * The order grids' bottom row, as 027 computes it (7 Oct 2026): over EVERY
 * matching line, with a unit or currency only when they all share one, and
 * none when any line lacks its unit, figure or rate.
 */
function demoPoTotals(all: DemoMpPo[], usd: boolean, scale: number, code: string | null) {
  const units = new Set(all.map((p) => p.unit || ''))
  const qtyKnown = all.every((p) => p.qty !== null && p.qty !== undefined)
  const amounts = all.map((p) => mpMoney(p.amount, usd, scale))
  const amountsKnown = amounts.every((a) => a !== null)
  const currencies = new Set(all.map((p) => p.currency || ''))
  return {
    totalQty: all.reduce((a, p) => a + (p.qty ?? 0), 0),
    totalQtyUnit: qtyKnown && units.size === 1 && !units.has('') ? all[0]?.unit ?? null : null,
    totalAmount: amounts.reduce<number>((a, v) => a + (v ?? 0), 0),
    totalAmountCurrency: !amountsKnown ? null
      : code ? code
      : currencies.size === 1 && !currencies.has('') ? all[0]?.currency ?? null : null,
  }
}

function demoFx(c: Currency | undefined): { usd: boolean; div: number; scale: number; code: string | null } {
  if (!c || c.mode === 'local') return { usd: false, div: 1, scale: 1, code: null }
  if (c.mode === 'usd') return { usd: true, div: DEMO_MP_RATE, scale: 1, code: 'USD' }
  return { usd: true, div: DEMO_MP_RATE / c.rate, scale: c.rate, code: c.code }
}

/* ------------------------------------------------------------------ statement
   Invented customers, read by the same rules as 043 (version 2): Full prints
   every line, D365's matching pairs included; Open prints what is unpaid
   today; one section per company and per transaction currency, the company's
   own currency first; the template's order (date, due date, a payment's cheque
   reference, voucher). Dates are relative to today so the demo reads the same
   whatever day it is opened. */
interface DemoStmtRow {
  company: string; account: string; party: string
  /** D365's TRANSTYPE: 2 invoice, 15 payment, 24 matching pair, 30 transfer between companies. */
  type: 2 | 15 | 24 | 30
  invoice: string | null; voucher: string; payRef: string | null; salesOrder: string | null; customerPo: string | null
  date: number; due: number | null; currency: string; amount: number; settled: number
}
/* Addresses and numbers are made up and look it -- a demo must never pass for a
   real letterhead. Line feeds and the trailing "%1" are as D365 sends them. */
const DEMO_STMT_COMPANIES: Record<string, {
  name: string; currency: string; salesMan: string; address: string; phone: string | null; fax: string | null
  /** Company currency per unit of a foreign one, for the demo's credit available. */
  rates: Record<string, number>
}> = {
  R002: { name: 'Redachem Saudi', currency: 'SAR', salesMan: 'Sami Al-Harbi',
    address: 'Unit 12, Example Business Park,\nKing Example Road, P.O. Box 00000,\nRIYADH\nSaudi Arabia',
    phone: '+966 11 000 0000', fax: '+966 11 000 0001', rates: { USD: 3.75 } },
  R010: { name: 'Redachem Emirates', currency: 'AED', salesMan: 'Rana Haddad',
    address: 'Office 000, Example Tower,\nSheikh Example Road, P.O. Box 00000,\nDUBAI\nUnited Arab Emirates',
    phone: '+971 4 000 0000', fax: null, rates: {} },
  // A third company none of the two-company customer's accounts is in: what
  // shows the Company box narrowing once that customer is chosen.
  R030: { name: 'Redachem Qatar', currency: 'QAR', salesMan: 'Omar Nasser',
    address: 'Office 00, Example Business Centre,\nExample Street, P.O. Box 00000,\nDOHA\nQatar',
    phone: null, fax: null, rates: {} },
}
const DEMO_STMT_CUSTOMERS: {
  party: string; company: string; account: string; name: string; terms: string; address: string; creditLimit: number
}[] = [
  { party: '900001001', company: 'R002', account: 'C-1001', name: 'شركة الخليج للدهانات', terms: '060 Days',
    address: 'ص.ب 00000،\nمبنى 12، طريق المثال الصناعي (بوابة 3)،\nالدمام,%1', creditLimit: 50_000 },
  { party: '900001001', company: 'R010', account: 'C-1001', name: 'شركة الخليج للدهانات', terms: '030 Days',
    address: 'P.O. Box 00000,\nExample Industrial Area 2,\nSharjah,%1', creditLimit: 20_000 },
  { party: '900001002', company: 'R002', account: 'C-1002', name: 'Al Noor Coatings Est.', terms: '030 Days',
    address: 'P.O. Box 00000,\nDammam,%1', creditLimit: 0 },
  { party: '900002001', company: 'R010', account: 'C-2001', name: 'Gulf Polymers FZE', terms: '045 Days',
    address: 'Plot 00, Example Free Zone,\nSharjah,%1', creditLimit: 10_000 },
  { party: '900003001', company: 'R030', account: 'C-3001', name: 'Doha Coatings W.L.L.', terms: '060 Days',
    address: 'P.O. Box 00000,\nDoha,%1', creditLimit: 25_000 },
]
const srow = (company: string, account: string, type: DemoStmtRow['type'], invoice: string | null, voucher: string,
  payRef: string | null, salesOrder: string | null, customerPo: string | null, date: number, due: number | null,
  currency: string, amount: number, settled: number): DemoStmtRow => ({
  company, account, party: DEMO_STMT_CUSTOMERS.find((c) => c.company === company && c.account === account)!.party,
  type, invoice, voucher, payRef, salesOrder, customerPo, date, due, currency, amount, settled,
})
const DEMO_STMT: DemoStmtRow[] = [
  // 900001001 in R002, SAR: a part-paid invoice carried in; a paid invoice with
  // its matching pair and two cheques on one day, printed in CHEQUE order; a
  // part-paid invoice; an unpaid one; a payment not yet matched.
  srow('R002', 'C-1001',  2, 'SLS-00380', 'SIV-00380', null, 'ORD-25-00101', 'PO-7710', -400, -340, 'SAR',  18_500,  12_000),
  srow('R002', 'C-1001', 15, null, 'CP-000118', 'Chq#000611/BSF', null, null, -300, -300, 'SAR', -12_000, -12_000),
  srow('R002', 'C-1001',  2, 'SLS-00412', 'SIV-00412', null, 'ORD-25-00133', 'PO-7781', -150, -90, 'SAR',  42_300,  42_300),
  srow('R002', 'C-1001', 24, 'SLS-00412', 'R002-80000154', null, null, null, -60, null, 'SAR', -42_300, -42_300),
  srow('R002', 'C-1001', 24, 'SLS-00412', 'R002-80000154', null, null, null, -60, null, 'SAR',  42_300,  42_300),
  srow('R002', 'C-1001', 15, null, 'CP-000232', 'Chq#000702/SNB', null, null, -60, -60, 'SAR', -12_300, -12_300),
  srow('R002', 'C-1001', 15, null, 'CP-000233', 'Chq#000701/SNB', null, null, -60, -60, 'SAR', -30_000, -30_000),
  srow('R002', 'C-1001',  2, 'SLS-00588', 'SIV-00588', null, 'ORD-25-00151', 'PO-7902', -75, -15, 'SAR',  61_800,  30_000),
  srow('R002', 'C-1001', 15, null, 'CP-000301', 'TT/30-000', null, null, -30, -30, 'SAR', -30_000, -30_000),
  srow('R002', 'C-1001',  2, 'SLS-00671', 'SIV-00671', null, 'ORD-25-00170', 'PO-8011', -12, 48, 'SAR',  27_450,       0),
  srow('R002', 'C-1001', 15, null, 'CP-000340', 'Chq#000777/SNB', null, null, -5, -5, 'SAR',  -5_000,       0),
  // ... and in US dollars.
  srow('R002', 'C-1001',  2, 'SLS-00590', 'SIV-00590', null, 'ORD-25-00152', 'PO-7903', -70, -10, 'USD',   9_800,   4_000),
  srow('R002', 'C-1001', 15, null, 'CP-000320', 'TT-55021', null, null, -20, -20, 'USD',  -4_000,  -4_000),
  // 900001001 in R010, AED: an invoice paid by a transfer from R002, which
  // prints its own voucher, never the other company's document.
  srow('R010', 'C-1001',  2, 'SLS-10070', 'SIV-10070', null, 'ORD-26-00009', 'GP-305', -90, -60, 'AED',   2_100,   2_100),
  srow('R010', 'C-1001', 30, 'RB-00999', 'R010-80000041', null, null, null, -40, null, 'AED',  -2_100,  -2_100),
  srow('R010', 'C-1001',  2, 'SLS-10077', 'SIV-10077', null, 'ORD-26-00012', 'GP-311', -50, -20, 'AED',  12_600,       0),
  // 900001002, R002 only: everything paid.
  srow('R002', 'C-1002',  2, 'SLS-00505', 'SIV-00505', null, 'ORD-25-00140', 'AN-118', -90, -60, 'SAR',   8_400,   8_400),
  srow('R002', 'C-1002', 15, null, 'CP-000290', 'Chq#000655/RJHI', null, null, -40, -40, 'SAR',  -8_400,  -8_400),
  // 900002001, R010 only: one unpaid invoice.
  srow('R010', 'C-2001',  2, 'SLS-10081', 'SIV-10081', null, 'ORD-26-00014', 'GPF-77', -45, -15, 'AED',   5_000,       0),
  // 900003001, R030 only: a part-paid invoice and its cheque.
  srow('R030', 'C-3001',  2, 'SLS-30011', 'SIV-30011', null, 'ORD-26-30005', 'DC-41', -80, -20, 'QAR',  14_200,   6_000),
  srow('R030', 'C-3001', 15, null, 'CP-300021', 'Chq#000123/QNB', null, null, -30, -30, 'QAR',  -6_000,  -6_000),
]
/** A demo date `offset` days from today, at midnight UTC, as the API sends dates. */
const stmtDay = (offset: number) => {
  const d = new Date(); d.setUTCHours(0, 0, 0, 0); d.setUTCDate(d.getUTCDate() + offset); return d.toISOString()
}
const isoDay = (iso: string) => iso.slice(0, 10)

/* ===========================================================================
   THE ACCESS MAP'S SCREENS, DEMO (new model, Part 17). Invented people, named
   by number as in the build plan's sketches, covering every badge: waiting,
   can enter, approved but never signed in, an approval taken back, a role
   not set up, no HR role, left, disabled, and a System Admin who sees all.
   =========================================================================== */

interface DemoHrRole {
  version?: string
  id: number
  name: string
  fn: string
  setUp: boolean
  reports: string[]
  /** Column group IDs, each of a report the role gives (D53). */
  groups: number[]
  hierarchy: number
  phone: boolean
}

/** The role saves this session made, newest first, for each role's History tab (17g; R-26). */
const demoRoleHistory: Record<number, AccessHistory['history']> = {}

const DEMO_HR_ROLES: DemoHrRole[] = [
  { id: 1101, name: 'Supply Chain Executive', fn: 'Supply-Chain', setUp: true, reports: ['material-planning', 'inventory-aging'], groups: [2005], hierarchy: 0, phone: false },
  { id: 1102, name: 'Business Unit Manager', fn: 'Commercial', setUp: true, reports: ['receivables', 'sales', 'statement-of-account'], groups: [2001, 2003, 2004, 2006], hierarchy: 2, phone: true },
  { id: 1103, name: 'Sales Executive', fn: 'Sales', setUp: true, reports: ['sales'], groups: [2003], hierarchy: 1, phone: true },
  { id: 1104, name: 'Credit Controller', fn: 'Finance', setUp: false, reports: [], groups: [], hierarchy: 0, phone: false },
]

/** The roles set up when the demo starts, with their settings then: their History's first save. */
const DEMO_ROLES_SET_UP_AT_START = new Map(DEMO_HR_ROLES.filter((r) => r.setUp).map((r) => [r.id, JSON.stringify({
  Set_Up: 1, Hierarchy_Mode: r.hierarchy, Mobile_Allowed: r.phone ? 1 : 0, Reports: r.reports,
  Column_Groups: r.groups.map((g) => { const x = DEMO_COLUMN_GROUPS.find((y) => y.id === g); return x ? `${demoReportName(x.report)}: ${x.name}` : String(g) }),
})]))

const DEMO_ACCESS_REPORTS: { slug: string; label: string }[] = [
  { slug: 'inventory-aging', label: 'Inventory Aging' },
  { slug: 'material-planning', label: 'Material Planning' },
  { slug: 'receivables', label: 'Receivables' },
  { slug: 'sales', label: 'Sales' },
  { slug: 'statement-of-account', label: 'Statement of Account' },
]

interface DemoAccessPerson {
  id: number
  comp: string
  role: number | null
  status: AccessPersonStatus
  approval: 'APPROVED' | 'WITHDRAWN' | null
  decided: string | null
  lastSignIn: string | null
  active?: boolean
  disabled?: boolean
  systemRole?: string
  own?: { reports?: Record<string, 'A' | 'R'>; groups?: Record<number, 'A' | 'R'> }
  extras?: { id: number; comp: string; reports: string[]; groups: number[]; lines?: { unit: string | null; team: string | null }[] }[]
  warnings?: AccessWarning[]
  lineManager?: number
  /** An update gave them more than their approval covered (D41): it no longer counts. */
  frozen?: boolean
  /** Their account was found in Microsoft by Login As (D51): linked, though never signed in. */
  found?: boolean
}

const DEMO_ACCESS_PEOPLE: DemoAccessPerson[] = [
  {
    id: 5001, comp: 'R002', role: 1101, status: 'WAITING', approval: null, decided: null, lastSignIn: '2026-10-16T09:12:00Z', lineManager: 5009,
    warnings: [
      { warningCode: 'UNIT_NO_D365_CODE', detail: 'Unit "Administration" has no D365 code, so it gives nothing.', sourceKind: 'MINDZER', sourceRef: 'unit 88', severity: 'FEWER_ROWS' },
      { warningCode: 'LINE_MANAGER_INACTIVE', detail: 'Their line manager (person 5009) is inactive in Mindzer.', sourceKind: 'MINDZER', sourceRef: 'person 5009', severity: 'INFO' },
    ],
  },
  { id: 5002, comp: 'R015', role: 1102, status: 'CAN_ENTER', approval: 'APPROVED', decided: '2026-10-16T08:00:00Z', lastSignIn: '2026-10-25T07:41:00Z' },
  { id: 5003, comp: 'R002', role: null, status: 'LEFT', approval: null, decided: null, lastSignIn: null, active: false },
  { id: 5004, comp: 'R002', role: 1103, status: 'NO_SIGN_IN', approval: 'APPROVED', decided: '2026-10-21T10:30:00Z', lastSignIn: null, lineManager: 5002 },
  {
    id: 5005, comp: 'R070', role: 1104, status: 'ROLE_NOT_SET', approval: null, decided: null, lastSignIn: null,
    warnings: [{ warningCode: 'ROLE_NOT_SET_UP', detail: 'Their HR role, Credit Controller - Finance, is not set up in MBI.', sourceKind: 'MBI', sourceRef: 'role 1104', severity: 'BLOCKS_ENTRY' }],
  },
  {
    id: 5006, comp: 'R015', role: null, status: 'NO_ROLE', approval: null, decided: null, lastSignIn: null,
    warnings: [{ warningCode: 'NO_ROLE', detail: 'No HR role in Mindzer. They cannot enter MBI until Mindzer gives them one.', sourceKind: 'MINDZER', sourceRef: null, severity: 'BLOCKS_ENTRY' }],
  },
  { id: 5007, comp: 'R002', role: 1103, status: 'WAITING', approval: 'WITHDRAWN', decided: '2026-10-21T15:05:00Z', lastSignIn: '2026-10-20T11:00:00Z', lineManager: 5002 },
  { id: 5008, comp: 'R015', role: 1102, status: 'SEES_ALL', approval: null, decided: null, lastSignIn: '2026-10-26T06:55:00Z', systemRole: 'SystemAdmin' },
  { id: 5009, comp: 'R015', role: 1102, status: 'DISABLED', approval: 'APPROVED', decided: '2026-10-02T09:00:00Z', lastSignIn: '2026-09-30T16:20:00Z', disabled: true },
  {
    id: 5012, comp: 'R002', role: 1103, status: 'WIDENED', approval: 'APPROVED', decided: '2026-10-14T10:00:00Z', lastSignIn: '2026-10-25T08:30:00Z', frozen: true,
    warnings: [
      { warningCode: 'UNIT_WITHOUT_TEAM', detail: 'Unit "Undefined Chem" holds none of their teams, so it gives nothing.', sourceKind: 'MINDZER', sourceRef: 'lstUsers.lstMainBusinessUnit_Ids: unit 1', severity: 'INFO',
        rowsJson: JSON.stringify({ Comp_Id: null, Reports: [{ Report_Slug: 'sales', Display_Name: 'Sales', Rows: 1204 }] }) },
      { warningCode: 'TEAM_NO_D365_CODE', detail: 'Team "Northern Accounts" has no D365 code, so it gives nothing.', sourceKind: 'MINDZER', sourceRef: 'lstBusinessTeams_ERP: team 77', severity: 'FEWER_ROWS' },
      { warningCode: 'TEAM_OUTSIDE_UNITS', detail: 'Team "Coastal" is not in any of their units.', sourceKind: 'MINDZER', sourceRef: 'lstUsers_BusinessTeams: team 81', severity: 'FEWER_ROWS',
        rowsJson: JSON.stringify({ Comp_Id: null, Reports: [{ Report_Slug: 'sales', Display_Name: 'Sales', Rows: 707 }] }) },
      { warningCode: 'TEAM_CODE_UNUSED', detail: 'Team "Kluber Desk" is linked to D365 team code KLB-1, which D365 does not use in R002, so it gives nothing. R015 uses it.', sourceKind: 'D365', sourceRef: 'D365: team code KLB-1, company R002: no rows; lstBusinessTeams_ERP links it to team 84', severity: 'FEWER_ROWS',
        rowsJson: JSON.stringify({ Comp_Id: 'R015', Reports: [{ Report_Slug: 'sales', Display_Name: 'Sales', Rows: 312 }] }) },
      { warningCode: 'LINE_MANAGER_INACTIVE', detail: 'Their line manager (person 5009) is inactive in Mindzer.', sourceKind: 'MINDZER', sourceRef: 'person 5009', severity: 'INFO' },
      { warningCode: 'FUNC_MANAGER_INACTIVE', detail: 'Their functional manager (person 5003) is inactive in Mindzer.', sourceKind: 'MINDZER', sourceRef: 'person 5003', severity: 'INFO' },
      { warningCode: 'COMPANY_CODE_UNUSED', detail: 'Company code MNZR82 is not used in D365 (it should probably be R082).', sourceKind: 'D365', sourceRef: 'MNZR82', severity: 'FEWER_ROWS' },
      { warningCode: 'NEW_MANAGER_LINK', detail: 'A new manager link this week gives them 300 new staff in 4 companies.', sourceKind: 'MINDZER', sourceRef: 'update 412', severity: 'INFO' },
    ],
  },
  {
    id: 5010, comp: 'R070', role: 1103, status: 'CAN_ENTER', approval: 'APPROVED', decided: '2026-10-18T12:00:00Z', lastSignIn: '2026-10-24T13:02:00Z',
    // Sales amounts off for them; their own Statement group stays, though the
    // Statement is not theirs: kept, with no effect (D53).
    own: { reports: { receivables: 'A' }, groups: { 2003: 'R', 2006: 'A' } },
    // One scope, three lines: two Coatings teams and the whole Lubricants unit (owner, 29 Oct 2026).
    extras: [{ id: 41, comp: 'R015', reports: ['sales'], groups: [2003], lines: [{ unit: 'COA', team: 'T22' }, { unit: 'COA', team: 'T23' }, { unit: 'LUB', team: null }] }],
  },
]

function demoAccessRow(p: DemoAccessPerson): AccessPersonRow {
  const role = DEMO_HR_ROLES.find((r) => r.id === p.role)
  const personal = Object.keys(p.own?.reports ?? {}).length + Object.keys(p.own?.groups ?? {}).length
  return {
    mindzerUserId: p.id,
    displayName: `Person ${p.id}`,
    email: `person${p.id}@redachem.com`,
    compId: p.comp,
    roleId: role?.id ?? null,
    roleName: role?.name ?? null,
    roleFunction: role?.fn ?? null,
    status: p.status,
    approval: p.approval,
    decidedUtc: p.decided,
    warningCount: p.warnings?.length ?? 0,
    personalChanges: personal,
    extraScopes: p.extras?.length ?? 0,
    // As 052: a role not set up in MBI gives no hierarchy and no phone.
    hierarchyMode: role?.setUp ? role.hierarchy : 0,
    mobileAllowed: role?.setUp ? role.phone : false,
    lastSignInUtc: p.lastSignIn,
    // The demo has no returns or freezes but the badges that say so.
    approvalInForce: p.approval === 'APPROVED' && p.status !== 'RETURNED' && p.status !== 'WIDENED',
  }
}

function demoHrRoleRow(r: DemoHrRole): AccessHrRoleRow {
  const holders = DEMO_ACCESS_PEOPLE.filter((p) => p.role === r.id && (p.active ?? true))
  return {
    roleId: r.id, roleName: r.name, roleFunction: r.fn, inSource: true, setUp: r.setUp,
    hierarchyMode: r.setUp ? r.hierarchy : null, mobileAllowed: r.setUp ? r.phone : null,
    reports: r.setUp ? r.reports.length : 0, columnGroups: r.setUp ? r.groups.length : 0,
    people: holders.length, approved: holders.filter((p) => p.approval === 'APPROVED').length,
    canEnter: holders.filter((p) => p.status === 'CAN_ENTER' || p.status === 'NO_SIGN_IN').length,
    version: r.setUp ? (r.version ?? '2026-10-12T08:00:00.000Z') : null, changedByEmail: r.setUp ? 'demo.user@redachem.com' : null,
  }
}

/**
 * What a role change would do, worked out the demo's way: every holder gains or
 * loses what the role's new state gives. The real procedure asks the access builder.
 */
function demoHrRoleSummary(r: DemoHrRole, body: AccessHrRoleBody, outcome: AccessHrRoleSummary['Outcome']): AccessHrRoleSummary {
  const holders = DEMO_ACCESS_PEOPLE.filter((p) => p.role === r.id && (p.active ?? true))
  const approved = holders.filter((p) => p.approval === 'APPROVED')
  const newReports = body.remove ? [] : body.reports
  const newGroups = body.remove ? [] : body.columnGroups
  const oldReports = r.setUp ? r.reports : []
  const oldGroups = r.setUp ? r.groups : []
  const repG = newReports.filter((x) => !oldReports.includes(x)).length
  const repL = oldReports.filter((x) => !newReports.includes(x)).length
  const figG = newGroups.filter((x) => !oldGroups.includes(x)).length
  const figL = oldGroups.filter((x) => !newGroups.includes(x)).length
  const newHier = body.hierarchy === 'type2' ? 2 : body.hierarchy === 'type1' ? 1 : 0
  // A role not set up gives hierarchy off and no phone; only a value that moves counts.
  const hierMoves = !body.remove && newHier !== (r.setUp ? r.hierarchy : 0)
  const phoneMoves = !body.remove && body.phone !== (r.setUp ? r.phone : false)
  const canBefore = r.setUp ? approved.length : 0
  const canAfter = body.remove ? 0 : approved.length
  const widened = repG + figG > 0 || canAfter > canBefore || (phoneMoves && body.phone)
  // The builder's effect has no phone: a phone-only change moves nobody's rows (Phone_Changed says it).
  const changed = repG + repL + figG + figL > 0 || hierMoves || canAfter !== canBefore
  return {
    Outcome: outcome, Version: r.version ?? (r.setUp ? '2026-10-12T08:00:00.000' : null),
    Holders: holders.length, Holders_Approved: approved.length,
    Can_Enter_Before: canBefore, Can_Enter_After: canAfter,
    People_Changed: changed ? holders.length + (newHier === 2 && widened ? 2 : 0) : 0,
    Entry_Gained: Math.max(0, canAfter - canBefore), Entry_Lost: Math.max(0, canBefore - canAfter),
    People_Widened: widened ? holders.length : 0, People_Narrowed: repL + figL > 0 || canAfter < canBefore ? holders.length : 0,
    Reports_Gained: repG * holders.length, Reports_Lost: repL * holders.length,
    Companies_Gained: 0, Column_Groups_Gained: figG > 0 ? holders.length : 0,
    Managers_Affected: newHier === 2 && widened ? 2 : 0,
    Hierarchy_Changed: hierMoves ? holders.length : 0, Phone_Changed: phoneMoves ? holders.length : 0,
    Kept_By_Personal_Setting: holders.filter((p) => p.own && Object.keys(p.own.reports ?? {}).some((k) => newReports.includes(k) !== oldReports.includes(k))).length,
    Widens: widened,
  }
}

/* The demo's Mindzer sync: an hourly run, one attempt Mindzer did not answer, and
   the newest held for review -- it would give person 5002 300 new staff. */
const DEMO_PULLS: AccessSyncPull[] = Array.from({ length: 12 }, (_, i) => {
  const id = 412 - i
  const started = new Date(Date.UTC(2026, 9, 26, 9 - i, 17)).toISOString()
  const outcome = i === 0 ? 'HELD' : i === 4 ? 'FETCH_FAILED' : i % 3 === 1 ? 'APPLIED' : 'NO_CHANGE'
  return {
    pullId: id, startedUtc: started, finishedUtc: started, durationMs: outcome === 'FETCH_FAILED' ? 3100 : 12_000 + i * 410,
    outcome, code: outcome === 'HELD' ? 'MANAGER_GAIN' : outcome === 'FETCH_FAILED' ? 'HTTP_401' : null,
    detail: outcome === 'HELD' ? 'One new manager link would give person 5002 300 new staff in 4 companies.' : outcome === 'FETCH_FAILED' ? 'Mindzer answered 401 (wrong key).' : null,
    fetchStatus: outcome === 'FETCH_FAILED' ? 401 : 200, fetchError: null,
    people: outcome === 'FETCH_FAILED' ? null : 2613 - i, activePeople: outcome === 'FETCH_FAILED' ? null : 2402 - i,
    rowsChanged: outcome === 'APPLIED' ? 4 : 0, accessChanged: outcome === 'APPLIED' ? 2 : 0,
  }
})
let demoAcceptance: { id: number; expires: string } | null = null

/* ?sync=stale: Mindzer's copy older than the limit, behind three updates that stopped
   at a check, so the admins' stale-data strip (05 section 7.2) can be seen. Kept for
   the tab's life, as ?model=v2 is. */
function demoSyncStale(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('sync') === 'stale') sessionStorage.setItem('mbi.demo.sync', 'stale')
    return sessionStorage.getItem('mbi.demo.sync') === 'stale'
  } catch {
    return false
  }
}

function demoPulls(): AccessSyncPull[] {
  if (!demoSyncStale()) return DEMO_PULLS
  const held = DEMO_PULLS[0]
  return DEMO_PULLS.map((p, i) =>
    i > 0 && i < 3
      ? { ...p, outcome: 'REFUSED', code: 'DUP_ACTIVE_EMAIL', detail: 'Two active people in Mindzer share one email.', people: held.people,
          activePeople: held.activePeople, rowsChanged: 0, accessChanged: 0 }
      : p,
  )
}

const DEMO_DOMAINS: AccessEmailDomain[] = [
  { emailDomain: 'redachem.com', activePeople: 9, approved: 5, signIns: 8, changedByEmail: 'demo.user@redachem.com', changeReason: 'The group domain.', changedUtc: '2026-10-10T08:00:00Z' },
  { emailDomain: 'mindzer.com', activePeople: 0, approved: 0, signIns: 1, changedByEmail: 'demo.user@redachem.com', changeReason: 'Mindzer staff who support MBI.', changedUtc: '2026-10-12T09:30:00Z' },
]

/** Unit and team lines an admin set on a person's main scope, as 052's EDITS section reads them. */
const DEMO_SCOPE_EDITS: Record<number, AccessScopeEdit[]> = {
  5010: [
    { editId: 71, effect: 'A', compId: 'R070', businessUnit: 'C1', anyUnit: false, businessTeam: null, anyTeam: true, inMainCompany: true,
      pausedUtc: null, pausedReason: null, changedByEmail: 'ziad.katabi@redachem.com', changeReason: 'Covers C1 while its manager is on leave.', changedUtc: '2026-10-21T09:12:00Z' },
    { editId: 72, effect: 'R', compId: 'R070', businessUnit: 'COA', anyUnit: false, businessTeam: 'T22', anyTeam: false, inMainCompany: true,
      pausedUtc: null, pausedReason: null, changedByEmail: 'ziad.katabi@redachem.com', changeReason: 'Key Accounts moved to the regional desk.', changedUtc: '2026-10-21T09:12:00Z' },
    { editId: 73, effect: 'A', compId: 'R070', businessUnit: null, anyUnit: true, businessTeam: 'T40', anyTeam: false, inMainCompany: true,
      pausedUtc: '2026-10-25T06:17:00Z', pausedReason: 'Mindzer moved them to another unit: check this line still applies.', changedByEmail: 'demo.user@redachem.com', changeReason: 'Trade-show leads.', changedUtc: '2026-10-08T14:40:00Z' },
  ],
  5012: [
    { editId: 81, effect: 'A', compId: 'R002', businessUnit: null, anyUnit: true, businessTeam: 'T16', anyTeam: false, inMainCompany: true,
      pausedUtc: null, pausedReason: null, changedByEmail: 'demo.user@redachem.com', changeReason: 'Shares the Gulf brokers desk.', changedUtc: '2026-10-26T11:05:00Z' },
  ],
}

/** A demo person's lines as 052's list says them. */
function demoScopeChange(p: DemoAccessPerson, edits: AccessScopeEdit[]): AccessProblemItem {
  const words = (e: AccessScopeEdit) => !e.anyTeam ? `team ${e.businessTeam}${!e.anyUnit ? ` (in unit ${e.businessUnit})` : ''}`
    : !e.anyUnit ? `unit ${e.businessUnit} (every team in it)` : 'every unit'
  // A paused remove still takes away (049); only a paused add waits.
  const live = (e: AccessScopeEdit) => e.inMainCompany && (e.effect === 'R' || !e.pausedUtc)
  const list = (effect: 'A' | 'R') => edits.filter((e) => live(e) && e.effect === effect).map(words).sort().join(', ')
  const idle = edits.filter((e) => !live(e)).length
  const detail = [list('A') && `Added: ${list('A')}`, list('R') && `Taken away: ${list('R')}`,
    idle > 0 && `${idle} ${idle === 1 ? 'line has' : 'lines have'} no effect now`].filter(Boolean).join(' · ')
  const at = edits.map((e) => e.changedUtc ?? '').sort().pop() || null
  return { category: 'MAIN_SCOPE_CHANGED', mindzerUserId: p.id, name: `Person ${p.id}`, compId: p.comp, code: null, detail, countOf: edits.length, atUtc: at }
}

const DEMO_PROBLEM_CATEGORIES: [string, string][] = [
  ['WAITING', 'people'], ['GAP_WARNINGS', 'people'], ['NO_HR_ROLE', 'people'], ['ROLES_NOT_SET_UP', 'roles'],
  ['CODES_NOT_IN_D365', 'codes'], ['PERSONAL_SETTINGS', 'people'], ['MAIN_SCOPE_CHANGED', 'people'], ['NEW_MANAGER_LINKS', 'links'],
  ['SIGN_IN_CONFLICTS', 'people'], ['SYSTEM_ROLE_LEFT', 'people'], ['HELD_UPDATES', 'updates'],
  ['SIGNED_IN_NOT_IN_MINDZER', 'sign-ins'], ['PAUSED', 'lines'], ['LEFT_OUT_BY_SYNC', 'people'],
  ['NO_SALESPERSON', 'companies'], ['UNKNOWN_SALESPERSON', 'companies'],
]

/* "Rows with no salesperson": the line over every company is not a company (052), so it is not counted. */
const isAllCompanies = (i: AccessProblemItem) => i.category === 'NO_SALESPERSON' && i.compId === null

function demoProblemItems(category: string): AccessProblemItem[] {
  const person = (p: DemoAccessPerson, code: string | null, detail: string | null, at: string | null = null): AccessProblemItem => ({
    category, mindzerUserId: p.id, name: `Person ${p.id}`, compId: p.comp, code, detail, countOf: null, atUtc: at,
  })
  switch (category) {
    case 'WAITING':
      return DEMO_ACCESS_PEOPLE.filter(demoWaiting).map((p) => person(p, demoStatusOf(p),
        p.lastSignIn ? 'Tried to sign in' : p.approval === 'WITHDRAWN' ? 'Approval taken back' : 'Waiting since they arrived', p.lastSignIn ?? p.decided))
    case 'GAP_WARNINGS':
      // As 052 sends it: no code, the person's codes as the detail ("A x2, B"), their number as the count.
      return DEMO_ACCESS_PEOPLE.filter((p) => (p.warnings ?? []).length > 0).map((p) => {
        const n = new Map<string, number>()
        for (const w of p.warnings!) n.set(w.warningCode, (n.get(w.warningCode) ?? 0) + 1)
        const codes = [...n].sort(([a], [b]) => (a < b ? -1 : 1)).map(([c, k]) => (k > 1 ? `${c} x${k}` : c)).join(', ')
        return { ...person(p, null, codes), countOf: p.warnings!.length }
      })
    case 'NO_HR_ROLE':
      return DEMO_ACCESS_PEOPLE.filter((p) => (p.active ?? true) && p.role === null).map((p) => person(p, 'NO_ROLE', 'Mindzer gives them no HR role.'))
    case 'ROLES_NOT_SET_UP':
      return DEMO_HR_ROLES.filter((r) => !r.setUp).map((r) => ({ category, mindzerUserId: null, name: `${r.name} - ${r.fn}`, compId: null,
        code: `role ${r.id}`, detail: 'Held by active people, not set up in MBI.', countOf: DEMO_ACCESS_PEOPLE.filter((p) => p.role === r.id).length, atUtc: null }))
    case 'CODES_NOT_IN_D365':
      return [{ category, mindzerUserId: null, name: 'Administration', compId: 'R002', code: 'unit 88', detail: 'Mindzer places people in this unit; it has no D365 code.', countOf: 1, atUtc: null }]
    case 'PERSONAL_SETTINGS':
      return DEMO_ACCESS_PEOPLE.filter((p) => p.own || (p.extras ?? []).length || (DEMO_SCOPE_EDITS[p.id] ?? []).length)
        .map((p) => person(p, null, 'Their own settings or an additional scope differ from the HR role.'))
    case 'MAIN_SCOPE_CHANGED':
      // Latest change first, as 052 orders them.
      return DEMO_ACCESS_PEOPLE.filter((p) => (DEMO_SCOPE_EDITS[p.id] ?? []).length)
        .map((p) => demoScopeChange(p, DEMO_SCOPE_EDITS[p.id]))
        .sort((a, b) => (b.atUtc ?? '').localeCompare(a.atUtc ?? ''))
    case 'HELD_UPDATES':
      return demoAcceptance ? [] : [{ category, mindzerUserId: null, name: 'Update 412', compId: null, code: 'MANAGER_GAIN', detail: DEMO_PULLS[0].detail, countOf: 1, atUtc: DEMO_PULLS[0].startedUtc }]
    // R-23 (17f): counted once a day from the fact rows, one line per company.
    case 'NO_SALESPERSON': {
      const at = '2026-10-27T00:40:00Z'
      return [
        { category, mindzerUserId: null, name: 'All companies', compId: null, code: null, countOf: 1_942, atUtc: at,
          detail: 'No salesperson: 12.7% of sales lines, 9.8% of receivables, 10.2% of payments, 27.8% of DSO rows, 17.7% of customers. Sales lines and payments: the last 12 months. When hierarchy is on, only people without hierarchy see these rows.' },
        { category, mindzerUserId: null, name: 'Redachem Gulf', compId: 'R002', code: null, countOf: 1_204, atUtc: at,
          detail: 'No salesperson: sales lines 1,120 of 8,410 (13.3%), receivables 84 of 902 (9.3%). Ask D365 to set one.' },
        { category, mindzerUserId: null, name: 'Redachem Asia', compId: 'R015', code: null, countOf: 738, atUtc: at,
          detail: 'No salesperson: sales lines 610 of 5,020 (12.2%), customers 128 of 690 (18.6%). Ask D365 to set one.' },
      ]
    }
    case 'UNKNOWN_SALESPERSON':
      return [{ category, mindzerUserId: null, name: 'Redachem Gulf', compId: 'R002', code: null, countOf: 512, atUtc: '2026-10-27T00:40:00Z',
        detail: 'A salesperson Mindzer does not know: sales lines 470, receivables 42 (3 salespeople). Under hierarchy these rows reach no manager. Ask HR or D365 to fix the salesperson.' }]
    default:
      return []
  }
}

/* The demo's D365 codes: three companies and their units' teams. R002 has enough teams, listed by
   code so their units interleave, to show the Team picker's unit headings scrolling (30 Oct 2026). */
const DEMO_ORG: Record<string, { units: [string, string][]; teams: [string, string, string][] }> = {
  R002: {
    units: [['LUB', 'Lubricants'], ['CHE', 'Chemicals'], ['MIN', 'Mining']],
    teams: [['T15', 'LUB', 'Gulf Brokers'], ['T16', 'LUB', 'Kluber'], ['T17', 'CHE', 'Building Chemicals'],
      ['T18', 'MIN', 'Mining Projects'], ['T19', 'LUB', 'Marine Lubricants'], ['T31', 'CHE', 'Central Province'],
      ['T32', 'CHE', 'Eastern Province'], ['T33', 'CHE', 'Western Province']],
  },
  R015: { units: [['COA', 'Coatings'], ['LUB', 'Lubricants']], teams: [['T22', 'COA', 'Key Accounts'], ['T23', 'COA', 'Distributors'], ['T40', 'LUB', 'Asia Lubes']] },
  R070: { units: [['COA', 'Coatings']], teams: [['T51', 'COA', 'Europe Direct']] },
}

function demoPersonSummary(p: DemoAccessPerson, outcome: string, gains: { reports: number; lost: number; widens: boolean }): AccessPersonEffectSummary {
  const can = p.status === 'CAN_ENTER' || p.status === 'NO_SIGN_IN' ? 1 : 0
  const changed = gains.reports + gains.lost > 0 || gains.widens ? 1 : 0
  return {
    Outcome: outcome, Version: '2026-10-18T11:59:00.000', Can_Enter_Before: can, Can_Enter_After: can,
    People_Changed: changed, Entry_Gained: 0, Entry_Lost: 0, People_Widened: gains.widens ? 1 : 0, People_Narrowed: gains.lost > 0 ? 1 : 0,
    Reports_Gained: gains.reports, Reports_Lost: gains.lost, Companies_Gained: 0, Column_Groups_Gained: 0, Managers_Affected: 0, Widens: gains.widens,
  }
}

/** Waiting (D37): active, not disabled, no system role, no approval in force. */
function demoWaiting(p: DemoAccessPerson): boolean {
  return (p.active ?? true) && !p.disabled && !p.systemRole && (p.approval !== 'APPROVED' || !!p.frozen)
}

/** The badge after a decision, by sec.fn_Person_Status_MBI's order. */
function demoStatusOf(p: DemoAccessPerson): AccessPersonStatus {
  const role = DEMO_HR_ROLES.find((r) => r.id === p.role)
  if (p.systemRole) return 'SEES_ALL'
  if (!(p.active ?? true)) return 'LEFT'
  if (p.disabled) return 'DISABLED'
  if (!role) return 'NO_ROLE'
  if (!role.setUp) return 'ROLE_NOT_SET'
  if (p.approval === 'APPROVED' && p.frozen) return 'WIDENED'
  if (p.approval === 'APPROVED') return p.lastSignIn ? 'CAN_ENTER' : 'NO_SIGN_IN'
  return 'WAITING'
}

const demoWarningsKey = (p: DemoAccessPerson) => `demo-${p.id}-${(p.warnings ?? []).map((w) => w.warningCode).join('.')}`

function demoApprovalRow(p: DemoAccessPerson): AccessApprovalRow {
  const role = DEMO_HR_ROLES.find((r) => r.id === p.role)
  const ws = p.warnings ?? []
  const rank = ['WIDER_ROWS', 'BLOCKS_ENTRY', 'NO_ROWS', 'FEWER_ROWS', 'INFO']
  const worst = ws.map((w) => w.severity).sort((a, b) => rank.indexOf(a) - rank.indexOf(b))[0] ?? null
  return {
    mindzerUserId: p.id, displayName: `Person ${p.id}`, email: `person${p.id}@redachem.com`, compId: p.comp,
    roleId: role?.id ?? null, roleName: role?.name ?? null, roleSetUp: role?.setUp ?? false,
    warningCount: ws.length, worstSeverity: worst, widens: ws.some((w) => w.severity === 'WIDER_ROWS'),
    lastSignInUtc: p.lastSignIn, signedIn: p.lastSignIn !== null,
    decision: p.approval === 'WITHDRAWN' ? 'WITHDRAWN' : null, decidedUtc: p.approval ? p.decided : null, returned: false,
  }
}

function demoApprovalCard(p: DemoAccessPerson): AccessApprovalCard {
  const page = demoAccessPage(p)
  const role = DEMO_HR_ROLES.find((r) => r.id === p.role)
  const person = page.person!
  // The main scope's lines as the builder makes them: Mindzer's placement less what an
  // admin took away, plus what one added (only lines that act: not paused, main company).
  const unit = p.comp === 'R002' ? 'LUB' : 'COA'
  const team = p.comp === 'R002' ? 'T15' : 'T22'
  const live = page.edits.filter((e) => e.inMainCompany && (e.effect === 'R' || !e.pausedUtc))
  const mainLines = [
    ...(live.some((e) => e.effect === 'R' && (e.anyTeam ? e.businessUnit === unit : e.businessTeam === team)) ? []
      : [{ lineSource: 'PLACEMENT', businessUnit: unit as string | null, anyUnit: false, businessTeam: team as string | null, anyTeam: false }]),
    ...live.filter((e) => e.effect === 'A').map((e) => ({ lineSource: 'A', businessUnit: e.businessUnit, anyUnit: e.anyUnit, businessTeam: e.businessTeam, anyTeam: e.anyTeam })),
  ]
  const sees = page.matrix
    .filter((c) => c.kind === 'REPORT' && c.value)
    .flatMap((c): AccessCardSees[] => {
      const extra = page.extras.find((x) => x.extraScopeId === c.scopeKey)
      // Only this report's groups, in this scope (D53).
      const groups = page.matrix.filter((f) => f.kind === 'GROUP' && f.value && f.scopeKey === c.scopeKey && f.reportSlug === c.code)
        .map((f) => f.label).sort()
      const base = { reportSlug: c.code, displayName: c.label, scopeKey: c.scopeKey, columnGroups: groups.join(', ') || null, rowsSource: c.code }
      if (c.scopeKey !== 0)
        return [{ ...base, lineSource: 'EXTRA', compId: extra?.compId ?? p.comp, businessUnit: null, anyUnit: true, businessTeam: null, anyTeam: true,
          rowsRecent: 120 + (p.id % 7) * 311 + c.code.length * 17 }]
      // Every line taken away: the report opens empty.
      if (mainLines.length === 0)
        return [{ ...base, lineSource: null, compId: null, businessUnit: null, anyUnit: null, businessTeam: null, anyTeam: null, rowsRecent: null }]
      return mainLines.map((l, k) => ({ ...base, ...l, compId: p.comp, rowsRecent: 120 + (p.id % 7) * 311 + c.code.length * 17 + k * 53 }))
    })
  return {
    person: {
      mindzerUserId: p.id, displayName: person.displayName, email: person.email, emailStatus: 'OK',
      isActive: person.isActive, isDisabled: person.isDisabled, inSource: person.inSource, excluded: false,
      compId: p.comp, entityName: person.entityName, roleId: person.roleId, roleName: person.roleName,
      roleFunction: person.roleFunction, roleSetUp: role?.setUp ?? false, hierarchyMode: role?.hierarchy ?? 0,
      mobileAllowed: role?.phone ?? false, lineManagerId: p.lineManager ?? null, funcManagerId: null,
      signedIn: p.lastSignIn !== null, lastSignInUtc: p.lastSignIn, approval: p.approval, decidedUtc: p.decided,
      decidedByEmail: p.approval ? 'demo.user@redachem.com' : null, approvedEmail: null, returned: false,
      entryNow: person.entryNow, warningCount: (p.warnings ?? []).length,
      blocksEntry: (p.warnings ?? []).some((w) => w.severity === 'BLOCKS_ENTRY'),
      widens: (p.warnings ?? []).some((w) => w.severity === 'WIDER_ROWS'),
      warningsKey: demoWarningsKey(p), decisionReason: person.decisionReason,
    },
    warnings: p.warnings ?? [],
    sees,
    inherits: { hierarchyMode: role?.hierarchy ?? 0, staffWithScopes: role?.hierarchy === 2 ? 4 : 0,
      inheritedLines: role?.hierarchy === 2 ? 6 : 0, staffWithProblems: role?.hierarchy === 2 ? 1 : 0 },
  }
}

function demoAccessPage(p: DemoAccessPerson): AccessPersonPage {
  const row = demoAccessRow(p)
  const role = DEMO_HR_ROLES.find((r) => r.id === p.role)
  const person: AccessPerson = {
    ...row,
    emailStatus: 'OK',
    isActive: p.active ?? true,
    isDisabled: p.disabled ?? false,
    inSource: p.active ?? true,
    entityName: { R002: 'Redachem Gulf', R015: 'Redachem Asia', R070: 'Redachem Europe' }[p.comp] ?? null,
    roleSetUp: role?.setUp ?? false,
    systemRole: p.systemRole ?? null,
    entryNow: p.status === 'CAN_ENTER' || p.status === 'NO_SIGN_IN' || p.status === 'SEES_ALL' ? 'ALLOWED'
      : p.status === 'NO_ROLE' ? 'DENIED_NO_HR_ROLE' : p.status === 'ROLE_NOT_SET' ? 'DENIED_ROLE_NOT_SET'
        : p.status === 'LEFT' ? 'DENIED_LEFT' : p.status === 'DISABLED' ? 'DENIED_DISABLED' : 'DENIED_NOT_APPROVED',
    decidedByEmail: p.approval ? 'demo.user@redachem.com' : null,
    decisionReason: p.approval === 'WITHDRAWN' ? 'Moved to a role that should not see sales yet.' : null,
    decisionWarningCount: p.approval ? 0 : null,
    returned: false,
    firstSeenUtc: '2026-10-15T06:00:00Z',
    signedIn: p.lastSignIn !== null,
    hierarchyFrom: role?.setUp ? 'ROLE' : 'NONE',
    mobileFrom: role?.setUp ? 'ROLE' : 'NONE',
    lineManagerId: p.lineManager ?? null,
    funcManagerId: null,
    staffDirect: DEMO_ACCESS_PEOPLE.filter((x) => x.lineManager === p.id).length,
    staffAll: DEMO_ACCESS_PEOPLE.filter((x) => x.lineManager === p.id).length,
    settingsVersion: p.own ? '2026-10-18T11:59:00.000Z' : null,
    // Their own sign-in, which demoTargetFor resolves to them.
    signInOid: p.lastSignIn !== null || p.found ? DEMO_OID_PREFIX + String(p.id) : null,
  }
  const matrix: AccessMatrixCell[] = []
  const inRoleReports = role?.setUp ? role.reports : []
  const inRoleGroups = role?.setUp ? role.groups : []
  for (const r of DEMO_ACCESS_REPORTS) {
    const own = p.own?.reports?.[r.slug]
    const inRole = inRoleReports.includes(r.slug)
    matrix.push({ kind: 'REPORT', code: r.slug, label: r.label, scopeKey: 0, reportSlug: r.slug, groupKey: null,
      value: own ? own === 'A' : inRole, source: own === 'A' ? 'PERSON_ON' : own === 'R' ? 'PERSON_OFF' : inRole ? 'ROLE' : 'ROLE_OFF' })
    for (const x of p.extras ?? [])
      matrix.push({ kind: 'REPORT', code: r.slug, label: r.label, scopeKey: x.id, reportSlug: r.slug, groupKey: null, value: x.reports.includes(r.slug), source: x.reports.includes(r.slug) ? 'GIVEN' : 'NOT_GIVEN' })
  }
  // Every column group, as 052 lists them; the screens show a group only where its report is in play.
  for (const g of DEMO_COLUMN_GROUPS) {
    const own = p.own?.groups?.[g.id]
    const inRole = inRoleGroups.includes(g.id)
    matrix.push({ kind: 'GROUP', code: String(g.id), label: g.name, scopeKey: 0, reportSlug: g.report, groupKey: `demo-${g.id}-${g.name}`,
      value: own ? own === 'A' : inRole, source: own === 'A' ? 'PERSON_ON' : own === 'R' ? 'PERSON_OFF' : inRole ? 'ROLE' : 'ROLE_OFF' })
    for (const x of p.extras ?? [])
      matrix.push({ kind: 'GROUP', code: String(g.id), label: g.name, scopeKey: x.id, reportSlug: g.report, groupKey: `demo-${g.id}-${g.name}`, value: x.groups.includes(g.id), source: x.groups.includes(g.id) ? 'GIVEN' : 'NOT_GIVEN' })
  }
  return {
    person,
    matrix,
    placement: [
      { kind: 'UNIT', mindzerId: 301, name: p.comp === 'R002' ? 'Lubricants' : 'Coatings', d365Code: p.comp === 'R002' ? 'LUB' : 'COA', unitId: null },
      { kind: 'TEAM', mindzerId: 412, name: p.comp === 'R002' ? 'Gulf Brokers' : 'Key Accounts', d365Code: p.comp === 'R002' ? 'T15' : 'T22', unitId: 301 },
    ],
    edits: DEMO_SCOPE_EDITS[p.id] ?? [],
    // A line each of the scope's units and teams, as 052 reads them; the first line's ID is the scope's.
    extras: (p.extras ?? []).flatMap((x) => (x.lines?.length ? x.lines : [{ unit: null, team: null }]).map((l, i) => ({
      extraScopeId: i === 0 ? x.id : x.id * 1000 + i, compId: x.comp,
      businessUnit: l.unit, anyUnit: l.unit === null, businessTeam: l.team, anyTeam: l.team === null,
      reports: x.reports.join(', '), columnGroupIds: x.groups.join(', ') || null, pausedUtc: null, pausedReason: null,
      changedByEmail: 'demo.user@redachem.com', changeReason: 'Covers the Asia accounts while the team is short.', version: '2026-10-18T11:59:00.000Z',
      columnGroups: x.groups.map((g) => DEMO_COLUMN_GROUPS.find((y) => y.id === g)?.name).filter(Boolean).sort().join(', ') || null,
      scopeGroup: x.id,
    }))),
    warnings: p.warnings ?? [],
  }
}

/** The demo's people on a report who see a column: anyone there, or anyone holding one of its groups. */
function demoColumnPeople(report: string, visibility: string, groups: number[]): number {
  const on = DEMO_ACCESS_PEOPLE.filter((p) => p.approval === 'APPROVED' && (p.active ?? true))
  const opens = (p: DemoAccessPerson) => {
    const role = DEMO_HR_ROLES.find((r) => r.id === p.role)
    const own = p.own?.reports?.[report]
    return own ? own === 'A' : !!role?.setUp && role.reports.includes(report)
  }
  const holds = (p: DemoAccessPerson, g: number) => {
    const role = DEMO_HR_ROLES.find((r) => r.id === p.role)
    const own = p.own?.groups?.[g]
    return own ? own === 'A' : !!role?.setUp && role.groups.includes(g)
  }
  if (visibility === 'HIDDEN') return 0
  if (visibility === 'EVERYONE') return on.filter(opens).length * 37
  return on.filter((p) => opens(p) && groups.some((g) => holds(p, g))).length * 37
}

/** One group of Column definition's second tab, with what uses it. */
function demoGroupRow(g: DemoColumnGroup): ColumnGroupRow {
  return {
    groupId: g.id, groupName: g.name, description: g.description,
    columnsN: DEMO_V2_COLUMNS.filter((c) => c.visibility === 'GROUPS' && c.groups.includes(g.id)).length,
    columnNames: demoGroupUnlocks(g.id),
    rolesN: DEMO_HR_ROLES.filter((r) => r.groups.includes(g.id)).length,
    personSettingsN: DEMO_ACCESS_PEOPLE.filter((p) => p.own?.groups?.[g.id]).length,
    extraScopesN: DEMO_ACCESS_PEOPLE.reduce((n, p) => n + (p.extras ?? []).filter((x) => x.groups.includes(g.id)).length, 0),
    peopleHolding: demoColumnPeople(g.report, 'GROUPS', [g.id]),
    version: g.version, changedByEmail: 'demo.user@redachem.com',
  }
}

/** 51627 in the demo: a group of a report the role or scope does not give, in the procedure's words. */
function demoStrayGroup(groupIds: number[], reports: string[], where: string): string | null {
  const g = groupIds.map((id) => DEMO_COLUMN_GROUPS.find((x) => x.id === id)).find((x) => x && !reports.includes(x.report))
  if (!g) return null
  const r = demoReportName(g.report)
  return `The column group "${g.name}" is for ${r}, which ${where}: tick ${r} or take the group off.`
}

/** 51627 for a person's own settings: a group new or changed outside their main scope's reports. */
function demoPersonStray(p: DemoAccessPerson, body: AccessPersonSettingsBody): string | null {
  const role = DEMO_HR_ROLES.find((r) => r.id === p.role)
  const main = new Set((role?.setUp ? role.reports : []).filter((s) => body.reports[s] !== 'off'))
  for (const [s, v] of Object.entries(body.reports)) if (v === 'on') main.add(s)
  for (const [k, v] of Object.entries(body.columnGroups)) {
    if (v === 'role') continue
    const id = Number(k)
    // Kept as saved, it may stay, doing nothing (052).
    if (p.own?.groups?.[id] === (v === 'on' ? 'A' : 'R')) continue
    const g = DEMO_COLUMN_GROUPS.find((x) => x.id === id)
    if (g && !main.has(g.report)) {
      const r = demoReportName(g.report)
      return `The column group "${g.name}" is for ${r}, which is not in their main scope: turn ${r} on first.`
    }
  }
  return null
}

export class DemoClient {
  /**
   * Under View As, returns the TARGET's roles and report list so the demo shows
   * the same narrowing the real platform does — a salesperson has no
   * Maintenance, no Access, and no Receivables unless they are on its list.
   *
   * Reproduced here rather than left as a stub because the demo is what a
   * reviewer is shown, and a demo where View As changes nothing outside the
   * report teaches exactly the wrong thing about the feature.
   */
  /**
   * WHO THIS SESSION IS — the demo's half of the substitution.
   *
   * Reads the SAME module the real client reads, so the demo goes through the
   * identical decision: if an oid is being acted as, everything below belongs
   * to that person and `actor` names the administrator. A demo that could not
   * show this would be a demo of the one feature nobody can otherwise see,
   * because a System Admin reviewing it passes through every gate.
   */
  me(): Promise<Me> {
    const target = demoTargetFor(actingAs())

    if (!target) {
      return delay({
        ...DEMO_ME,
        maintenance: demoMaintenance(DEMO_ME.reports, DEMO_ME.roles.includes('SystemAdmin')),
        actor: null,
        settings: demoSettingsFor(DEMO_ME.user),
        // ?model=v2 is the database running the new model: the reports answer as
        // it would, and Access offers its screens (Part 17).
        ...(demoV2()
          ? { permissionsModel: 'v2' as const, entry: { allowed: true, phone: 'not-a-phone' as const, adminReason: null, adminExplanation: null }, hrRole: null }
          : {}),
      })
    }

    const roles = demoRoles.filter((r) => r.email === target.email).map((r) => r.roleCode)
    const seesAllData = demoRoles.some((r) => r.email === target.email && r.seesAllData)
    const onList = demoAccess.some((a) => a.email === target.email)
    // A person from Access › People opens what their page says; the demo's sign-ins, as before.
    const person = DEMO_ACCESS_PEOPLE.find((x) => `person${x.id}@redachem.com` === target.email)
    const reports = person
      ? [...new Set(demoAccessPage(person).matrix.filter((c) => c.kind === 'REPORT' && c.value).map((c) => c.code))]
      : seesAllData || onList ? ['receivables', 'sales', 'material-planning', 'inventory-aging', 'statement-of-account'] : []

    /* THE ENTRY GATE, under the new model (05 6.2): a person from Access › People
       who may not come in is refused at the door, as the API refuses them, and the
       administrator signed in as them is told why (7.9). */
    const entryNow = demoV2() && person ? demoAccessPage(person).person?.entryNow ?? null : null
    if (entryNow && entryNow !== 'ALLOWED') {
      return delay({
        ...DEMO_ME,
        user: target.email,
        roles: [],
        seesAllData: false,
        canViewAsData: false,
        scopeNodeCount: 0,
        reports: [],
        targetIsRegistered: true,
        maintenance: demoMaintenance([], false),
        actor: { email: DEMO_ME.user, objectId: DEMO_OID_PREFIX + '0000' },
        settings: null,
        permissionsModel: 'v2',
        entry: { allowed: false, phone: 'not-a-phone', adminReason: entryNow, adminExplanation: DEMO_ENTRY_WHY[entryNow] ?? null },
        hrRole: null,
      })
    }

    return delay({
      ...DEMO_ME,
      // THE SESSION IS THEIRS, address included. That is the change from View
      // As, which kept `user` as the administrator because only a surface was
      // borrowed.
      user: target.email,
      roles,
      seesAllData,
      reports,
      targetIsRegistered: true,
      maintenance: demoMaintenance(reports, roles.includes('SystemAdmin')),
      // Login As follows the account, as the envelope's does (032 section 1c):
      // under the new model the System Master Reader may not use it (D7).
      canViewAsData: roles.some((r) => r === 'SystemAdmin' || r === 'SystemMasterAdmin' || r === 'SystemDevelopment'
        || (r === 'MasterViewer' && !demoV2())),
      // And the administrator is named here, and nowhere else.
      actor: { email: DEMO_ME.user, objectId: DEMO_OID_PREFIX + '0000' },
      // The TARGET's remembered currency, as the API answers under Login As.
      settings: demoSettingsFor(target.email),
      ...(demoV2()
        ? { permissionsModel: 'v2' as const, entry: { allowed: true, phone: 'not-a-phone' as const, adminReason: null, adminExplanation: null },
            // "Role - Function", as 053 writes Hr_Role_Label (deep review of 17f).
            hrRole: person?.role != null
              ? { id: person.role, label: [demoAccessRow(person).roleName, demoAccessRow(person).roleFunction].filter(Boolean).join(' - ') || null }
              : null }
        : {}),
    })
  }

  actAsCandidates(search?: string): Promise<ActAsCandidate[]> {
    const q = (search ?? '').trim().toLowerCase()
    const rows = DEMO_USERS
      .filter((u) => !q || u.email.toLowerCase().includes(q) || (u.displayName ?? '').toLowerCase().includes(q))
      .map<ActAsCandidate>((u, i) => {
        const oid = demoOidFor(u.email)
        return {
          oid,
          email: u.email,
          displayName: u.displayName,
          roles: demoRoles.filter((r) => r.email === u.email).map((r) => r.roleName).join(', ') || null,
          grantRows: u.grantRows,
          lastSeenUtc: oid ? daysFromNow(-(i + 1)) : null,
          canActAs: oid !== null,
        }
      })
    // New Joiner: signed in, on an access list, and in no permission row — the
    // one candidate here who is NOT in DEMO_USERS, because DEMO_USERS mirrors
    // the permission table and this person is precisely absent from it.
    const nj = DEMO_NO_GRANTS
    if (!q || nj.email.includes(q) || nj.displayName.toLowerCase().includes(q)) {
      rows.push({
        oid: nj.oid, email: nj.email, displayName: nj.displayName,
        roles: null, grantRows: 0, lastSeenUtc: daysFromNow(-1), canActAs: true,
      })
    }
    // ?model=v2 (S17): only sign-ins, each with its door and its count in reports;
    // a system role always gets in and opens every report.
    if (demoV2()) {
      return delay(rows.filter((r) => r.canActAs).map<ActAsCandidate>((r, i) => ({
        ...r,
        grantRows: r.roles ? 5 : r.grantRows === 0 ? 0 : 1 + (i % 4),
        entryState: r.roles ? 'ALLOWED' : r.grantRows === 0 ? 'DENIED_NOT_IN_HR' : i % 5 === 3 ? 'DENIED_NOT_APPROVED' : 'ALLOWED',
      })))
    }
    // Selectable first, then by name — the same ordering the procedure applies.
    rows.sort((a, b) => Number(b.canActAs) - Number(a.canActAs) || (a.displayName ?? '').localeCompare(b.displayName ?? ''))
    return delay(rows)
  }

  beginActAs(objectId: string): Promise<ActAsTarget> {
    const target = demoTargetFor(objectId)
    if (!target) {
      return Promise.reject(
        new ApiError({
          kind: 'forbidden',
          title: 'Cannot sign in as that person',
          explanation: 'That person is not an active user of this platform.',
          correlationId: 'demo-act-as',
        }),
      )
    }
    return delay({ oid: objectId, email: target.email, displayName: target.displayName })
  }

  /**
   * The standing filter bar's menus, built from the demo's own world so the
   * controls offer exactly what the demo can then narrow to.
   *
   * ORDER IS THE PAYLOAD, as it is on the real endpoint: the aging periods come
   * out in bucket order because the constant is written in bucket order, and
   * nothing downstream re-sorts. The salesperson kind is present here because
   * the demo signs in as a System Admin; against the real database a caller
   * without SLS_Mod receives no such rows and the page draws no such control.
   */
  receivablesFilterOptions(): Promise<RecvFilterRow[]> {
    const out: RecvFilterRow[] = []
    const add = (kind: RecvFilterRow['kind'], code: string, label = code) => {
      if (!out.some((o) => o.kind === kind && o.code === code)) out.push({ kind, code, label })
    }
    add('company', 'r002', 'REDAChem KSA')
    add('company', 'r010', 'REDAChem UAE')
    for (const u of DEMO_AGING) add('businessUnit', u.businessUnit)
    for (const t of DEMO_CHILD_NAMES.BusinessUnit) add('businessTeam', t)
    for (const s of DEMO_CHILD_NAMES.BusinessTeam) add('salesperson', s)
    for (const a of DEMO_AGING_PERIODS) add('agingPeriod', a)
    for (const t of DEMO_TRX_TYPES) add('trxType', t)
    for (const t of DEMO_RECV_TERMS) add('paymentTerms', t)
    for (const r of DEMO_RECV_REGIONS) add('region', r)
    return delay(out)
  }

  receivablesMatrix(
    path: MatrixPath = {},
    options: { viewAs?: string; includeClosed?: boolean; currency?: Currency; filters?: RecvFilters } = {},
  ): Promise<Answer<MatrixNode[]>> {
    // 3.75 is the fixture's rate, so the demo converts by the same factor the
    // tests assert against.
    const fx = demoFx(options.currency).div
    const w = demoPlatformWindow()
    if (w) return Promise.reject(demoRefusal(w))
    /* THE CODE IS CONVERTED WITH THE FIGURES. Every numeric field on the node
       is divided below; leaving currencyCode alone returned SAR beside dollars,
       which is the defect the served-currency gate exists to refuse in SQL --
       and the demo has to behave the same or it stops being a place to check
       the behaviour. Found by reading the rendered page, not the types. */
    const ccy = demoFx(options.currency).code
    return delay(answer(demoMatrix(path, options.viewAs, options.filters).map((n) => {
      if (fx === 1) return n
      const converted = Object.fromEntries(
        Object.entries(n).map(([k, v]) => [k, typeof v === 'number' ? Math.round(v / fx) : v]),
      ) as MatrixNode
      return { ...converted, currencyCode: ccy ?? converted.currencyCode }
    })))
  }

  receivablesTransactions(
    options: { page?: number; pageSize?: number; sortBy?: string; sortDesc?: boolean; viewAs?: string; currency?: Currency; filters?: RecvFilters } = {},
  ): Promise<Answer<TransactionPage>> {
    const size = options.pageSize ?? 100
    const page = options.page ?? 1
    const f = options.filters
    /* THIS METHOD IGNORED THE CURRENCY ENTIRELY until 23 Sep 2026 — it did not
       even accept the parameter, so By Transaction showed unconverted riyals in
       every mode while the matrix beside it converted. The two tabs of one
       report disagreed, in the demo that exists to let somebody notice exactly
       that. Found by reading the rendered page: 1,100 figures coded SAR under a
       control set to AED. */
    const fx = demoFx(options.currency)
    // Generated from the same deterministic seed as the matrix, so the demo
    // stays stable across reloads and the two tabs tell one story.
    const units = options.viewAs ? (DEMO_VIEW_AS_UNITS[options.viewAs] ?? ['FI']) : DEMO_AGING.map((r) => r.businessUnit)
    const all: TransactionRow[] = []
    for (const bu of units) {
      for (const bt of DEMO_CHILD_NAMES.BusinessUnit) {
        for (const cust of DEMO_CHILD_NAMES.Salesperson) {
          for (const doc of DEMO_CHILD_NAMES.Customer) {
            const seed = `${bu}/${bt}/${cust}/${doc}`
            const docNumber = `${bu}-${doc}`
            const sp = DEMO_CHILD_NAMES.BusinessTeam[Math.floor(seeded(seed + 'sp') * 3)]
            /* THE BAR, HONOURED. The dimensions first, then the document's own
               attributes through the same helper the matrix uses — so a filter
               moves the grid and the tiles above it by the same rule rather
               than by two implementations that agree until they do not.
               Customer is a substring here as it is in the procedure. */
            if (f?.bu && bu !== f.bu) continue
            if (f?.bt && bt !== f.bt) continue
            if (f?.sp && sp !== f.sp) continue
            if (f?.cust && !cust.toLowerCase().includes(f.cust.toLowerCase())) continue
            if (!demoDocPasses(seed, docNumber, f)) continue
            const a = demoDocAttrs(seed)
            const n = demoNode('Document', doc, seed, 90_000)
            all.push({
              currencyCode: 'SAR',
              businessUnit: bu,
              businessTeam: bt,
              salesperson: sp,
              customer: cust,
              // The ERP's own codes, not invented "Net 30" prose: the real menu
              // reads 030 Days / 060 Days / CASH / ADV, and a demo that spells
              // them differently is a demo nobody can match to a screenshot.
              paymentTerms: a.terms,
              docNumber,
              documentDate: new Date(2026, 0, 1 + Math.floor(seeded(seed + 'd') * 200)).toISOString(),
              daysOld: a.daysOld,
              dueDate: new Date(2026, 1, 1 + Math.floor(seeded(seed + 'u') * 200)).toISOString(),
              daysOverdue: a.daysOverdue,
              advancePayment: n.advancePayment,
              totalReceivables: n.totalReceivables,
              totalOverdue: n.totalOverdue,
              over30DaysOverdue: n.over30DaysOverdue,
              d0To30: n.d0To30, d31To60: n.d31To60, d61To90: n.d61To90,
              d91To120: n.d91To120, d121To180: n.d121To180,
              d181To365: n.d181To365, dOver365: n.dOver365,
            })
          }
        }
      }
    }

    if (options.sortBy) {
      const key = (options.sortBy.charAt(0).toLowerCase() + options.sortBy.slice(1)) as keyof TransactionRow
      const dir = options.sortDesc === false ? 1 : -1
      all.sort((a, b) => {
        const av = a[key], bv = b[key]
        if (av === null || bv === null) return 0
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        return String(av).localeCompare(String(bv)) * dir
      })
    }

    return delay(answer({
      total: all.length,
      page,
      pageSize: size,
      rows: all.slice((page - 1) * size, page * size).map((r) =>
        fx.div === 1
          ? r
          : ({
              ...Object.fromEntries(
                Object.entries(r).map(([k, v]) => [k, typeof v === 'number' && MONEY_COLS.has(k) ? Math.round(v / fx.div) : v]),
              ),
              currencyCode: fx.code ?? r.currencyCode,
            } as TransactionRow),
      ),
    }))
  }

  receivablesDecompose(
    options: { groupBy?: DecomposeLevel; bu?: string; bt?: string; sp?: string; customer?: string; viewAs?: string } = {},
  ): Promise<Answer<DecomposeResponse>> {
    const groupBy = options.groupBy ?? 'BusinessUnit'
    const units = options.viewAs
      ? (DEMO_VIEW_AS_UNITS[options.viewAs] ?? ['FI'])
      : DEMO_AGING.map((r) => r.businessUnit)

    const names =
      groupBy === 'BusinessUnit' ? units
      : groupBy === 'BusinessTeam' ? DEMO_CHILD_NAMES.BusinessUnit
      : groupBy === 'Salesperson' ? DEMO_CHILD_NAMES.BusinessTeam
      : groupBy === 'Customer' ? DEMO_CHILD_NAMES.Salesperson
      : DEMO_CHILD_NAMES.Customer

    const base = [options.bu, options.bt, options.sp, options.customer].filter(Boolean).join('/')
    const nodes = names
      .map((n) => {
        const node = demoNode('Document', n, `${base}/${n}/decomp`, 700_000)
        return {
          currencyCode: 'SAR',
          nodeKey: n,
          amount: node.totalReceivables,
          totalReceivables: node.totalReceivables,
          totalOverdue: node.totalOverdue,
          docCount: 1 + Math.floor(seeded(`${base}/${n}/c`) * 40),
        }
      })
      .sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0))

    return delay(answer({
      groupedBy: groupBy,
      availableFields: ['BusinessUnit', 'BusinessTeam', 'Salesperson', 'Customer', 'Document'] as DecomposeLevel[],
      nodes,
    }))
  }

  receivablesDso(
    options: { company?: string; bu?: string; bt?: string; viewAs?: string; filters?: RecvFilters } = {},
  ): Promise<Answer<DsoNode[]>> {
    const level = options.bt ? 'Salesperson' : options.bu ? 'BusinessTeam' : options.company ? 'BusinessUnit' : 'Company'
    let names =
      level === 'Company' ? ['REDAChem KSA', 'REDA Food', 'REDA Gulf']
      : level === 'BusinessUnit' ? (options.viewAs ? (DEMO_VIEW_AS_UNITS[options.viewAs] ?? ['FI']) : ['FI', 'LAB', 'IND'])
      : level === 'BusinessTeam' ? DEMO_CHILD_NAMES.BusinessUnit
      : DEMO_CHILD_NAMES.BusinessTeam
    /* FOUR OF THE TWELVE, matching the procedure, which in turn matches the
       view: dbo.D365_Salesperson_DSO carries no customer, document, aging
       period, payment terms or region, so the page draws only these on this
       tab and the demo must narrow by exactly the same four. */
    const f = options.filters
    const pick = level === 'BusinessUnit' ? f?.bu : level === 'BusinessTeam' ? f?.bt : level === 'Salesperson' ? f?.sp : undefined
    if (pick) names = names.filter((n) => n === pick)
    const base = [options.company, options.bu, options.bt].filter(Boolean).join('/')

    return delay(answer(
      names.map((n, i) => {
        const rec = Math.round(400_000 + seeded(`${base}/${n}/r`) * 3_000_000)
        // Every third leaf has no sales at all — the undefined-DSO case the
        // real procedure returns as NULL and the old app reported as 0 days.
        const noSales = level === 'Salesperson' && i % 3 === 2
        const sales = noSales ? 0 : Math.round(1_000_000 + seeded(`${base}/${n}/s`) * 9_000_000)
        return {
      nodeKey: n,
          groupedBy: level as DsoNode['groupedBy'],
          hasChildren: level !== 'Salesperson',
          receivables: rec,
          sales365: sales,
          dso: sales ? Math.round((rec / sales) * 365 * 100) / 100 : null,
        }
      }).sort((a, b) => (b.receivables ?? 0) - (a.receivables ?? 0)),
    ))
  }

  /* ------------------------------------------------------------------
     SALES — the merged sales report, demonstrated from one small invented
     fact list (DEMO_SALES_FACTS at the bottom of this file). Every tab
     aggregates the SAME rows, the discipline the real report gets from
     the database: the KPI head, the grid, the boards and the tree cannot
     disagree, because they are one dataset asked four ways.
     ------------------------------------------------------------------ */

  salesOverview(
    options: { period?: DemoSalesPeriod; from?: string; to?: string; currency?: Currency; viewAs?: string } & DemoSalesFilters = {},
  ): Promise<Answer<SalesOverview>> {
    const rows = demoSalesRows(options)
    const fx = demoFx(options.currency).div
    const { mtd, scale } = demoPeriodMode(options)
    const pick = (r: DemoSalesFact) => (mtd ? r.mtdSales : r.sales)
    const pickPY = (r: DemoSalesFact) => (mtd ? r.mtdSalesPY : r.salesPY)
    const gpOf = (r: DemoSalesFact) => (mtd ? r.mtdGp : r.gp)
    const gpPYOf = (r: DemoSalesFact) => (mtd ? r.mtdGpPY : r.gpPY)
    const sum = (f: (r: DemoSalesFact) => number) => rows.reduce((s, r) => s + f(r), 0) * scale
    const sales = sum(pick)
    const gp = sum(gpOf)
    return delay(answer({
      sales: sales / fx,
      salesPY: sum(pickPY) / fx,
      gp: gp / fx,
      gpPY: sum(gpPYOf) / fx,
      gpPct: sales ? gp / sales : null,
      gpPctPY: sum(pickPY) ? sum(gpPYOf) / sum(pickPY) : null,
      qty: Math.round(sum((r) => (mtd ? r.mtdQty : r.qty))),
      qtyPY: Math.round(sum((r) => (mtd ? r.mtdQtyPY : r.qtyPY))),
      customers: new Set(rows.map((r) => r.customer)).size,
      salespeople: new Set(rows.map((r) => r.sp)).size,
      items: new Set(rows.map((r) => r.product)).size,
      invoices: rows.length * 3,
      salesWK: rows.reduce((s, r) => s + r.wkSales, 0) / fx,
      /* No currency or unit: the real procedure does not return them either,
         and the demo has to have the same shape or it teaches the wrong one.
         The reason is measured and lives in 021's header. */
    }))
  }

  salesExplorer(
    options: {
      period?: DemoSalesPeriod
      from?: string
      to?: string
      dimension?: SalesExplorerDimension
      drill2?: SalesDrillLevel
      drill3?: SalesDrillLevel
      drill4?: SalesDrillLevel
      l1?: string
      l2?: string
      l3?: string
      search?: string
      currency?: Currency
      viewAs?: string
    } & DemoSalesFilters = {},
  ): Promise<Answer<SalesExplorerResponse>> {
    const dimension = options.dimension ?? 'BusinessUnit'
    const level = options.l1 && options.l2 && options.l3 ? 4 : options.l1 && options.l2 ? 3 : options.l1 ? 2 : 1
    /* The same fallback rule 021 applies, so the demo's chain -- and the one it
       echoes back -- is what the real procedure would have used. */
    const levels = resolveSalesDrill(dimension, options.drill2, options.drill3, options.drill4)
    const chain = levels.map((l) => DEMO_LEVEL_KEY[l]) as [DemoSalesKey, DemoSalesKey, DemoSalesKey, DemoSalesKey]
    const groupKey = chain[level - 1]
    const fx = demoFx(options.currency).div
    const { mtd, scale } = demoPeriodMode(options)

    let rows = demoSalesRows(options)
    if (options.l1) rows = rows.filter((r) => r[chain[0]] === options.l1)
    if (options.l2) rows = rows.filter((r) => r[chain[1]] === options.l2)
    if (options.l3) rows = rows.filter((r) => r[chain[2]] === options.l3)
    if (options.search) {
      const s = options.search.toLowerCase()
      /* At a country level a CODE also finds its country ("UAE", "KSA"),
         exactly -- the same rule 021 applies through plat.Country_Code_MBI. */
      const code = options.search.trim().toUpperCase()
      const byCode = groupKey === 'companyCountry' || groupKey === 'customerCountry'
      rows = rows.filter(
        (r) => r[groupKey].toLowerCase().includes(s) || (byCode && (DEMO_COUNTRY_CODES[r[groupKey]] ?? []).includes(code)),
      )
    }

    const groups = new Map<string, DemoSalesFact[]>()
    for (const r of rows) {
      const k = r[groupKey]
      groups.set(k, [...(groups.get(k) ?? []), r])
    }
    const out = [...groups.entries()]
      .map(([member, g]) => {
        const sum = (f: (r: DemoSalesFact) => number) => g.reduce((s, r) => s + f(r), 0) * scale
        const sales = sum((r) => (mtd ? r.mtdSales : r.sales))
        const salesPY = sum((r) => (mtd ? r.mtdSalesPY : r.salesPY))
        const gp = sum((r) => (mtd ? r.mtdGp : r.gp))
        const gpPY = sum((r) => (mtd ? r.mtdGpPY : r.gpPY))
        return {
          member,
          hasChildren: level < 4,
          totalRows: groups.size,
          sales: sales / fx,
          salesPY: salesPY / fx,
          gp: gp / fx,
          gpPY: gpPY / fx,
          gpDiff: (gp - gpPY) / fx,
          gpPct: sales ? gp / sales : null,
          gpPctPY: salesPY ? gpPY / salesPY : null,
          qty: sum((r) => (mtd ? r.mtdQty : r.qty)),
          qtyPY: sum((r) => (mtd ? r.mtdQtyPY : r.qtyPY)),
          // Per ROW, from this group's own facts — the table contract.
          ...demoGroupDims(g, demoFx(options.currency)),
        }
      })
      .sort((a, b) => (b.sales ?? 0) - (a.sales ?? 0))
    return delay(answer({ totalRows: groups.size, drillChain: out.length ? levels : null, rows: out }))
  }

  salesLeaderboard(
    options: { period?: DemoSalesPeriod; from?: string; to?: string; currency?: Currency; viewAs?: string } & DemoSalesFilters = {},
  ): Promise<Answer<SalesBoardRow[]>> {
    const rows = demoSalesRows(options)
    const fx = demoFx(options.currency).div
    const { mtd, scale } = demoPeriodMode(options)
    const boards: SalesBoardRow[] = []
    const dims: [string, (r: DemoSalesFact) => string][] = [
      ['customer', (r) => r.customer],
      ['item', (r) => r.product],
      ['salesperson', (r) => r.sp],
      ['supplier', (r) => r.supplier],
    ]
    for (const [dim, key] of dims) {
      for (const measure of ['sales', 'gp'] as const) {
        const pick = (r: DemoSalesFact) =>
          measure === 'sales' ? (mtd ? r.mtdSales : r.sales) : mtd ? r.mtdGp : r.gp
        const totals = new Map<string, number>()
        for (const r of rows) totals.set(key(r), (totals.get(key(r)) ?? 0) + pick(r))
        ;[...totals.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .forEach(([member, value], i) =>
            boards.push({ board: `${dim}-${measure}`, rank: i + 1, member, value: (value * scale) / fx }),
          )
      }
    }
    return delay(answer(boards))
  }

  salesTrend(
    options: { currency?: Currency; viewAs?: string } & DemoSalesFilters = {},
  ): Promise<Answer<SalesTrendRow[]>> {
    const rows = demoSalesRows(options)
    const fx = demoFx(options.currency).div
    const out: SalesTrendRow[] = []
    // A believable seasonal shape, deterministic, from the same facts: each
    // month is the yearly figure scaled by a fixed curve — August (this
    // month in the fixture's world) is partial, exactly as real data is.
    const curve = [0.7, 0.75, 0.85, 0.9, 1.0, 1.05, 0.95, 0.35, 0, 0, 0, 0]
    const curvePY = [0.65, 0.7, 0.8, 0.95, 0.9, 1.0, 0.9, 0.85, 0.9, 1.0, 1.1, 1.2]
    const sales = rows.reduce((s, r) => s + r.sales, 0) / 8
    const gp = rows.reduce((s, r) => s + r.gp, 0) / 8
    const salesPY = rows.reduce((s, r) => s + r.salesPY, 0) / 11
    const gpPY = rows.reduce((s, r) => s + r.gpPY, 0) / 11
    for (let m = 1; m <= 12; m++) {
      out.push({
        year: 2025,
        month: m,
        sales: Math.round((salesPY * curvePY[m - 1]) / fx),
        gp: Math.round((gpPY * curvePY[m - 1]) / fx),
        lines: 120 + m * 7,
      })
    }
    for (let m = 1; m <= 8; m++) {
      out.push({
        year: 2026,
        month: m,
        sales: Math.round((sales * curve[m - 1]) / fx),
        gp: Math.round((gp * curve[m - 1]) / fx),
        lines: 130 + m * 6,
      })
    }
    return delay(answer(out))
  }

  salesTree(
    options: {
      period?: DemoSalesPeriod
      from?: string
      to?: string
      measure?: 'sales' | 'gp'
      groupBy?: SalesDimension
      customer?: string
      product?: string
      supplier?: string
      path?: Partial<Record<SalesDimension, string[]>>
      topN?: number
      currency?: Currency
      viewAs?: string
    } & DemoSalesFilters = {},
  ): Promise<Answer<SalesTreeResponse>> {
    const groupBy = options.groupBy ?? 'BusinessUnit'
    const measure = options.measure ?? 'sales'
    const fx = demoFx(options.currency).div
    const { mtd, scale } = demoPeriodMode(options)
    let rows = demoSalesRows(options)
    if (options.customer) rows = rows.filter((r) => r.customer === options.customer)
    if (options.product) rows = rows.filter((r) => r.product === options.product)
    if (options.supplier) rows = rows.filter((r) => r.supplier === options.supplier)

    /* THE DRILL PATH AS SETS, mirroring the procedure: a step may hold several
       members. Membership rather than equality, and a one-member step takes the
       same branch — which is the property that makes a single click and a
       selection of one provably the same request here as well as in SQL. */
    for (const [dim, members] of Object.entries(options.path ?? {}) as [SalesDimension, string[]][]) {
      if (!members?.length) continue
      const col = DEMO_SALES_KEYS[dim]
      const set = new Set(members)
      rows = rows.filter((r) => set.has(r[col]))
    }

    const key = DEMO_SALES_KEYS[groupBy]
    const groups = new Map<string, DemoSalesFact[]>()
    for (const r of rows) groups.set(r[key], [...(groups.get(r[key]) ?? []), r])

    const nodes = [...groups.entries()]
      .map(([nodeKey, g]) => {
        const sum = (f: (r: DemoSalesFact) => number) => g.reduce((s, r) => s + f(r), 0) * scale
        const sales = sum((r) => (mtd ? r.mtdSales : r.sales)) / fx
        const gp = sum((r) => (mtd ? r.mtdGp : r.gp)) / fx
        return {
          availableMeasures: 'sales,gp',
          measure,
          groupedBy: groupBy,
          nodeKey,
          value: measure === 'gp' ? gp : sales,
          sales,
          gp,
          gpPct: sales ? gp / sales : null,
          lines: g.length * 40,
          // Per BAR, from this node's own facts.
          currencyCode: demoGroupDims(g, demoFx(options.currency)).currencyCode,
        }
      })
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
      .slice(0, options.topN ?? 20)
    return delay(answer({ measure, availableMeasures: ['sales', 'gp'], groupedBy: groupBy, nodes }))
  }

  /**
   * The posted ledger, invented. Each demo fact is exploded into a handful of
   * lines so paging, sorting and the totals have something to act on — and
   * every line is derived from the fact it came from, so the tab's totals
   * still agree with the rest of the demo report.
   */
  salesLines(
    options: {
      period?: DemoSalesPeriod
      from?: string
      to?: string
      docType?: string
      district?: string
      location?: string
      kluber?: string
      search?: string
      hideZeroQty?: boolean
      page?: number
      pageSize?: number
      sortBy?: string
      sortDesc?: boolean
      currency?: Currency
      viewAs?: string
    } & DemoSalesFilters = {},
  ): Promise<Answer<SalesLinesResponse>> {
    const fx = demoFx(options.currency).div
    const { scale } = demoPeriodMode(options)
    let lines = demoSalesLines(demoSalesRows(options), scale)

    if (options.docType) lines = lines.filter((l) => l.docType === options.docType)
    if (options.district) lines = lines.filter((l) => l.district === options.district)
    if (options.location) lines = lines.filter((l) => l.location === options.location)
    if (options.kluber) lines = lines.filter((l) => l.kluberCode === options.kluber)
    if (options.hideZeroQty !== false) lines = lines.filter((l) => (l.qty ?? 0) !== 0)
    if (options.search) {
      const s = options.search.toLowerCase()
      lines = lines.filter((l) =>
        [l.customer, l.product, l.itemNumber, l.docNumber, l.batch, l.customerNumber]
          .some((v) => (v ?? '').toLowerCase().includes(s)),
      )
    }

    const dir = options.sortDesc === false ? 1 : -1
    const key = options.sortBy ?? 'InvoiceDate'
    const pick = (l: SalesLine): string | number =>
      key === 'Qty' ? (l.qty ?? 0)
      : key === 'Sales' ? (l.sales ?? 0)
      : key === 'Cost' ? (l.cost ?? 0)
      : key === 'Charges' ? (l.charges ?? 0)
      : key === 'Gp' ? (l.gp ?? 0)
      : key === 'GpPct' ? (l.gpPct ?? 0)
      : key === 'Customer' ? (l.customer ?? '')
      : key === 'Product' ? (l.product ?? '')
      : key === 'Salesperson' ? (l.salesperson ?? '')
      : key === 'Supplier' ? (l.supplier ?? '')
      : key === 'DocNumber' ? (l.docNumber ?? '')
      : key === 'Batch' ? (l.batch ?? '')
      : key === 'District' ? (l.district ?? '')
      : key === 'DocType' ? (l.docType ?? '')
      : (l.invoiceDate ?? '')
    lines = [...lines].sort((a, b) => {
      const x = pick(a), y = pick(b)
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir
      return String(x).localeCompare(String(y)) * dir
    })

    const sum = (f: (l: SalesLine) => number | null) =>
      lines.reduce((s, l) => s + (f(l) ?? 0), 0)
    const totalSales = sum((l) => l.sales)
    const totalGp = sum((l) => l.gp)

    const page = options.page && options.page > 0 ? options.page : 1
    const size = options.pageSize && options.pageSize > 0 ? options.pageSize : 100
    const rows = lines.slice((page - 1) * size, page * size).map((l) => ({
      ...l,
      qty: l.qty,
      sales: l.sales === null ? null : l.sales / fx,
      cost: l.cost === null ? null : l.cost / fx,
      charges: l.charges === null ? null : l.charges / fx,
      gp: l.gp === null ? null : l.gp / fx,
      // Converted amounts ARE dollars, so the line's label follows the toggle.
      currency: demoFx(options.currency).code ?? l.currency,
    }))

    return delay(answer({
      totalRows: lines.length,
      page,
      pageSize: size,
      totals: {
        qty: sum((l) => l.qty),
        sales: totalSales / fx,
        cost: sum((l) => l.cost) / fx,
        charges: sum((l) => l.charges) / fx,
        gp: totalGp / fx,
        gpPct: totalSales ? totalGp / totalSales : null,
      },
      rows,
    }))
  }

  salesLineFilterOptions(): Promise<SalesLineFilterRow[]> {
    const out: SalesLineFilterRow[] = []
    const add = (kind: SalesLineFilterRow['kind'], code: string) => {
      if (code && !out.some((o) => o.kind === kind && o.code === code)) {
        out.push({ kind, code, label: code })
      }
    }
    for (const l of demoSalesLines(DEMO_SALES_FACTS, 1)) {
      add('docType', l.docType ?? '')
      add('district', l.district ?? '')
      add('location', l.location ?? '')
      add('kluber', l.kluberCode ?? '')
    }
    out.sort((a, b) => a.kind.localeCompare(b.kind) || (a.label ?? '').localeCompare(b.label ?? ''))
    return delay(out)
  }

  /* ------------------------------------------------------------------
     DEVELOPER MODE (028), in memory.

     The switches live in a module-level object so the demo behaves like the
     real thing: flip one on the Access screen, go to Sales, click a column
     header, watch the section appear. A demo where the toggles do nothing
     teaches that the feature does nothing.

     One signed-in person here, so per-user and platform-wide look identical --
     which is exactly why the real per-user behaviour is proved in the fixture
     (two System Admins, one switch each) rather than trusted to this file.

     The definitions below are the SAME TEXT the database seed carries, for
     the two measures most worth showing. Deliberately not all eighteen: the
     demo exists to show the mechanism working, and pasting the whole seed
     here would be a second copy to keep in step with the first.
     ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------
     GRID LAYOUTS, in the demo, kept in localStorage.

     THE DEMO HAS TO ACTUALLY SURVIVE A RELOAD, because that is the entire
     feature. An in-memory stand-in would render the same menus and lose
     everything on F5 — which is precisely the behaviour this replaced, so
     a reviewer would come away believing it had not worked. localStorage
     is the closest honest thing to a per-person row without a database.

     Wrapped in try/catch throughout: it throws in private-mode Safari and
     wherever storage is denied, and a stored column order must never be
     able to break the page it decorates. CollapsibleStats takes the same
     precaution for the same reason.
     ------------------------------------------------------------------ */
  gridLayouts(): Promise<StoredGridLayout[]> {
    return delay(Object.entries(demoLayouts()).map(([gridId, layout]) => ({
      gridId,
      layout,
      schemaVersion: 1,
      changedUtc: new Date().toISOString(),
    })))
  }

  saveGridLayout(gridId: string, layout: string): Promise<void> {
    const all = demoLayouts()
    all[gridId] = layout
    writeDemoLayouts(all)
    return delay(undefined as void)
  }

  forgetGridLayout(gridId: string): Promise<void> {
    const all = demoLayouts()
    delete all[gridId]
    writeDemoLayouts(all)
    return delay(undefined as void)
  }

  /** 036's settings in the demo: remembered for whoever the session is, the target under Login As. */
  saveReaderSetting(_key: 'currency', value: string): Promise<void> {
    const who = demoTargetFor(actingAs())?.email ?? DEMO_ME.user
    const all = demoSettings()
    all[who] = value
    try {
      localStorage.setItem(DEMO_SETTINGS_KEY, JSON.stringify(all))
    } catch {
      // A preference we cannot persist is not worth throwing over.
    }
    return delay(undefined as void)
  }

  /* Named views in the demo, on the same localStorage shelf and for the same
     reason: the feature IS surviving a reload, so a stand-in that lost them on
     F5 would demonstrate the opposite of what was built. The id counter is
     stored with them so ids stay stable across reloads too — a menu whose
     entries change id on refresh would make Delete hit the wrong one. */
  readerViews(report: string): Promise<StoredReaderView[]> {
    return delay(demoViews().filter((v) => v.report === report).map(({ report: _r, ...v }) => v))
  }

  saveReaderView(options: {
    report: string; page: string; name: string; view: string; makeDefault?: boolean
  }): Promise<{ viewId: number }> {
    const all = demoViews()
    // The name is the identity on that tab, exactly as the procedure has it.
    const existing = all.find(
      (v) => v.report === options.report && v.pageSlug === options.page && v.name === options.name,
    )
    if (options.makeDefault) {
      for (const v of all) {
        if (v.report === options.report && v.pageSlug === options.page) v.isDefault = false
      }
    }
    if (existing) {
      existing.view = options.view
      existing.isDefault = options.makeDefault ? true : existing.isDefault
      existing.changedUtc = new Date().toISOString()
      writeDemoViews(all)
      return delay({ viewId: existing.viewId })
    }
    const viewId = all.reduce((n, v) => Math.max(n, v.viewId), 0) + 1
    all.push({
      viewId, report: options.report, pageSlug: options.page, name: options.name,
      isDefault: options.makeDefault ?? false, view: options.view,
      schemaVersion: 1, changedUtc: new Date().toISOString(),
    })
    writeDemoViews(all)
    return delay({ viewId })
  }

  renameReaderView(viewId: number, name: string): Promise<void> {
    const all = demoViews()
    const target = all.find((v) => v.viewId === viewId)
    if (!target) return delay(undefined as void)
    // The same clash the database refuses: one name per person per tab.
    const taken = all.some(
      (v) => v.viewId !== viewId && v.report === target.report && v.pageSlug === target.pageSlug && v.name === name,
    )
    if (taken) return Promise.reject(new Error('DENIED_NAME_TAKEN'))
    target.name = name
    writeDemoViews(all)
    return delay(undefined as void)
  }

  deleteReaderView(viewId: number): Promise<void> {
    writeDemoViews(demoViews().filter((v) => v.viewId !== viewId))
    return delay(undefined as void)
  }

  setDefaultReaderView(viewId: number, isDefault = true): Promise<void> {
    const all = demoViews()
    const target = all.find((v) => v.viewId === viewId)
    if (target) {
      if (isDefault) {
        for (const v of all) {
          if (v.report === target.report && v.pageSlug === target.pageSlug) v.isDefault = false
        }
      }
      target.isDefault = isDefault
      writeDemoViews(all)
    }
    return delay(undefined as void)
  }

  devFlags(): Promise<DevFlag[]> {
    return delay(
      (Object.keys(DEMO_DEV_FLAGS) as DevFlagKey[]).map((key) => ({
        key,
        enabled: DEMO_DEV_FLAGS[key],
        changedUtc: DEMO_DEV_FLAGS[key] ? '2026-09-10T09:00:00Z' : null,
        changedBy: null,
      })),
    )
  }

  setDevFlag(body: SetDevFlagRequest): Promise<{ outcome: string }> {
    DEMO_DEV_FLAGS[body.key] = body.enabled
    // Turning the master off leaves the three sub-switches where they were,
    // exactly as the database does -- so turning it back on restores the
    // reader's previous choice rather than resetting it.
    return delay({ outcome: 'CHANGED' })
  }

  measureDoc(report: string, surface: string, key: string): Promise<MeasureDoc | null> {
    if (!DEMO_DEV_FLAGS.dev_mode) return delay(null)
    const doc = DEMO_MEASURE_DOCS.find(
      (d) => d.surface === surface && d.key === key,
    )
    if (!doc || report !== 'sales') return delay(null)
    // The SERVER strips what the flags do not permit. Reproduced here rather
    // than filtered in the component, because the component must never be the
    // thing deciding what may be seen.
    return delay({
      ...doc,
      sources: DEMO_DEV_FLAGS.dev_show_source ? doc.sources : [],
      formula: DEMO_DEV_FLAGS.dev_show_math ? doc.formula : null,
      procName: DEMO_DEV_FLAGS.dev_show_query ? doc.procName : null,
      sourceFile: DEMO_DEV_FLAGS.dev_show_query ? doc.sourceFile : null,
    })
  }

  /**
   * The manifest, DERIVED from the same list the panel reads rather than
   * written out a second time.
   *
   * That is the whole point of doing it this way here: in the demo, "documented"
   * and "clickable" cannot drift apart, so the handful of headers this build
   * offers are exactly the handful it can explain. A second hard-coded list
   * would eventually disagree with the first, and the demo would then show the
   * bug this feature exists to remove.
   */
  measureRefs(report: string): Promise<MeasureRef[]> {
    if (!DEMO_DEV_FLAGS.dev_mode || report !== 'sales') return delay([])
    return delay(
      DEMO_MEASURE_DOCS.map((d) => ({
        surface: d.surface,
        key: d.key,
        kind: DEMO_MEASURE_KINDS[`${d.surface}/${d.key}`] ?? 'measure',
      })),
    )
  }

  /**
   * The currencies a reader may restate figures into.
   *
   * REAL RATES, D365's Global ones as the clone's exchange-rate export held
   * them on 7 Oct 2026 — not invented ones. A demo that offered AED at a
   * round 4.0 would let a conversion bug look right, and the whole point of
   * this screen is that the number is believable.
   *
   * Since 045 the platform reads these LIVE from D365's Global rate type
   * rather than from a copy made at deploy (Global only on the D365 team's
   * word, 7 Oct 2026), so ZAR is offered: its old "dispute" was a company
   * rate against a hand-kept override, and Global holds one rate for it. No
   * offered currency carries a note — 045 writes one only for a currency it
   * withholds, and the reader never sends a withheld one. (QAR used to carry
   * "reviewed 23 Sep 2026", a review that never happened.)
   */
  currencyRates(): Promise<CurrencyRate[]> {
    return delay([...DEMO_CURRENCY_RATES])
  }

  salesFilterOptions(): Promise<SalesFilterRow[]> {
    const rows = DEMO_SALES_FACTS
    const out: SalesFilterRow[] = []
    const add = (kind: SalesFilterRow['kind'], code: string, label: string) => {
      if (!out.some((o) => o.kind === kind && o.code === code)) out.push({ kind, code, label })
    }
    for (const r of rows) {
      add('company', r.company, r.companyName)
      add('businessUnit', r.bu, r.bu)
      add('businessTeam', r.bt, r.bt)
      add('salesperson', r.sp, r.sp)
      add('supplier', r.supplier, r.supplier)
      add('region', demoRegion(r.customer), demoRegion(r.customer))
    }
    // The custom-range Year picker — the demo's world has two years of data.
    add('year', '2025', '2025')
    add('year', '2026', '2026')
    out.sort((a, b) => a.kind.localeCompare(b.kind) || (a.label ?? '').localeCompare(b.label ?? ''))
    return delay(out)
  }

  permissionUsers(search?: string): Promise<PermissionUser[]> {
    const q = (search ?? "").toLowerCase()
    return delay(q ? DEMO_USERS.filter((u) => (u.email + u.displayName).toLowerCase().includes(q)) : DEMO_USERS)
  }

  permissionUserDetail(email: string): Promise<PermissionGrant[]> {
    return delay(DEMO_GRANTS[email] ?? [
      { company: "r002", companyName: "REDAChem KSA", businessUnit: "ALL", businessTeam: "ALL", breadth: "Entire company", userClass: "BU-MGR" },
    ])
  }

  permissionAnomalies(): Promise<PermissionAnomaly[]> {
    return delay(DEMO_ANOMALIES)
  }

  /* ------------------------------------------------------------------
     ACCESS ADMINISTRATION, in memory.

     The demo mutates a module-level array so the screen behaves like the
     real one — grant, see it appear, revoke, see it go. The guards are
     reproduced too (last admin, self-demotion, reason required for a
     bypass role): the demo is what a reviewer is shown, and a demo where
     the safety rails are absent teaches that they do not exist.

     Nothing here is a control. Every one of these rules is enforced in
     the database, and this client never speaks to it.
     ------------------------------------------------------------------ */

  /**
   * TWO reports, so the picker is exercised rather than merely present.
   *
   * The second is deliberately role-scoped with no columns and nobody on its
   * access list: it is the shape that makes both administration screens show an
   * empty state, and a demo where every option looks the same would not have
   * caught a screen that only works for the first one.
   */
  adminReports(): Promise<AdminReport[]> {
    return delay([
      {
        slug: 'receivables',
        displayName: 'Receivables',
        dataDomain: 'receivables',
        scopingModel: 'data-scoped',
        requiredPermission: 'AR_Mod',
        peopleWithAccess: demoAccess.length,
        governedColumns: demoColumns.length,
      },
      {
        slug: 'sales',
        displayName: 'Sales',
        dataDomain: 'sales',
        scopingModel: 'data-scoped',
        requiredPermission: 'SLS_Mod',
        peopleWithAccess: demoAccess.length,
        governedColumns: 41,
      },
      {
        slug: 'material-planning',
        displayName: 'Material Planning',
        dataDomain: 'planning',
        scopingModel: 'data-scoped',
        requiredPermission: 'Inv_Mod',
        peopleWithAccess: demoAccess.length,
        governedColumns: 67,
      },
      {
        slug: 'permissions',
        displayName: 'User Permissions',
        dataDomain: 'admin',
        scopingModel: 'role-scoped',
        requiredPermission: null,
        peopleWithAccess: 0,
        governedColumns: 0,
      },
    ])
  }

  reportAccess(reportSlug: string): Promise<ReportAccessEntry[]> {
    // Only the receivables fixture has an access list; the admin report is
    // role-scoped, which is exactly the empty case the screen must explain.
    return delay(reportSlug === 'receivables' ? [...demoAccess] : [])
  }

  grantReportAccess(body: GrantAccessRequest): Promise<GrantAccessResult> {
    const wanted = [...new Set(
      body.emails.split(/[\n,]/).map((e) => e.trim()).filter(Boolean),
    )]
    const fresh = wanted.filter((e) => !demoAccess.some((a) => a.email === e))
    const unknownEmails = wanted.filter((e) => !DEMO_USERS.some((u) => u.email === e))

    for (const email of fresh) {
      demoAccess.push({
        email,
        displayName: DEMO_USERS.find((u) => u.email === email)?.displayName ?? null,
        grantedBy: 'demo.user@redachem.com',
        grantedUtc: new Date().toISOString(),
        reason: body.reason ?? null,
        knownToD365: DEMO_USERS.some((u) => u.email === email),
      })
    }

    return delay({
      requested: wanted.length,
      added: fresh.length,
      alreadyHad: wanted.length - fresh.length,
      unknownToPermissionData: unknownEmails.length,
      unknownEmails,
    })
  }

  revokeReportAccess(emails: string): Promise<{ removed: number }> {
    const wanted = new Set(emails.split(/[\n,]/).map((e) => e.trim()).filter(Boolean))
    const before = demoAccess.length
    demoAccess = demoAccess.filter((a) => !wanted.has(a.email))
    return delay({ removed: before - demoAccess.length })
  }

  /* ------------------------------------------------------------------
     THE THREE REPORT PAGES DEMO MODE DOES NOT SERVE.

     Trends, Week Comparison and Salesperson Review need twelve weeks of
     plausible movement, two reconciling week-ends, and a printable
     per-salesperson roll-up. Inventing those convincingly is real work, and
     invented figures that LOOK considered are worse than none: a reviewer
     reasons from them.

     Until 6 Aug 2026 these methods simply did not exist, and `new DemoClient()
     as Api` told TypeScript to stop checking — so the three tabs threw "not a
     function" at runtime and rendered the failure panel with a message about
     an unexpected error. The cast is now gone (see api/index.ts), which means
     this file must implement the whole interface, and these are the honest
     implementation: refuse, and say exactly why.

     A named refusal is also what the UI already knows how to render.
     ------------------------------------------------------------------ */
  /* ---------------------------------------------------- material planning
     Real demo data rather than a refusal: these are the screens the
     documentation build photographs, and every one of them aggregates from
     DEMO_MP_ITEMS and DEMO_MP_POS so no two tabs can disagree with each
     other. The same two definitions of "open" the procedures carry are
     carried here: file flag = 0 on the tiles and per item, line status 1-2
     on the Purchase Orders screen. Signed quantity sold on the trend and the
     summary, absolute on the performance table, the grid and the bar chart.
     ------------------------------------------------------------------- */

  /** The item-dimension filters every screen shares. */
  #mpItemOk(i: DemoMpItem, o: { company?: string; bu?: string; itemGroup?: string; supplier?: string }) {
    return (!o.company || i.company === o.company)
      && (!o.bu || i.bu === o.bu)
      && (!o.itemGroup || i.group === o.itemGroup)
      && (!o.supplier || i.supplier === o.supplier)
  }

  /** A purchase order line reaches the item dimension through its item. */
  #mpPoOk(p: DemoMpPo, o: { company?: string; bu?: string; itemGroup?: string; supplier?: string }) {
    if (o.company && p.company !== o.company) return false
    if (!o.bu && !o.itemGroup && !o.supplier) return true
    const i = DEMO_MP_ITEMS.find((x) => x.company === p.company && x.item === p.item)
    return !!i && this.#mpItemOk(i, { bu: o.bu, itemGroup: o.itemGroup, supplier: o.supplier })
  }

  /**
   * The unit or currency a GROUP is in: the value when the group has exactly
   * one, null when it has several, plus the count so the screen can say
   * "mixed" instead of naming one member's unit over a sum of many.
   */
  #mpOne(values: (string | null)[]) {
    const set = new Set(values.filter((v) => !!v))
    return { value: set.size === 1 ? [...set][0] as string : null, count: set.size }
  }

  /** The card definition of open: file flag = 0. */
  #mpOpenByFlag(p: DemoMpPo) { return p.fileClosed === 0 }

  /**
   * Quantity ordered in a given year, by the order-page definition (line
   * status 1-3, approved). The Company Overview columns the manager asked for
   * are all built from this, so PO YTD and PO PY cannot drift apart.
   */
  #mpPoQty(year: number, where: (p: DemoMpPo) => boolean) {
    return DEMO_MP_POS
      .filter((p) => p.poYear === year && [1, 2, 3].includes(p.lineStatus) && p.approval >= 40 && where(p))
      .reduce((a, p) => a + p.qty, 0)
  }

  materialPlanningFilters(_options: { viewAs?: string } = {}): Promise<MpFilterRow[]> {
    const rows: MpFilterRow[] = []
    const seen = new Set<string>()
    const add = (kind: MpFilterRow['kind'], code: string | null, label?: string | null) => {
      if (!code) return
      const key = kind + '|' + code
      if (seen.has(key)) return
      seen.add(key)
      rows.push({ kind, code, label: label ?? code })
    }
    for (const i of DEMO_MP_ITEMS) {
      add('company', i.company, i.companyName)
      add('businessUnit', i.bu)
      add('itemGroup', i.group)
      add('warehouse', i.warehouse)
      add('supplier', i.supplier)
    }
    for (const p of DEMO_MP_POS) {
      add('modeOfDelivery', p.mode)
      add('supplierCategory', p.category)
      add('shipmentStatus', p.shipmentStatus)
      add('orderProgress', p.orderProgress)
      add('orderStatus', p.orderStatus)
      add('year', String(p.poYear))
    }
    add('year', '2026'); add('year', '2025')
    rows.sort((a, b) => a.kind.localeCompare(b.kind) || (a.label ?? '').localeCompare(b.label ?? ''))
    return delay(rows)
  }

  materialPlanningOverview(
    options: { company?: string; bu?: string; itemGroup?: string; supplier?: string; currency?: Currency; viewAs?: string } = {},
  ): Promise<Answer<MpOverview>> {
    const { usd, scale, code } = demoFx(options.currency)
    const thisYear = new Date().getFullYear()
    const items = DEMO_MP_ITEMS.filter((i) => this.#mpItemOk(i, options))
    const open = DEMO_MP_POS.filter((p) => this.#mpOpenByFlag(p) && this.#mpPoOk(p, options))
    const below = items.filter((i) => i.minInv > 0 && (i.onHand ?? 0) < i.minInv)
    const units = this.#mpOne(items.map((i) => i.unit))
    const currencies = this.#mpOne(items.map((i) => mpCur(i.company)))
    const cost = items.reduce<number | null>((a, i) => (i.cost === null ? a : (a ?? 0) + i.cost), null)
    return delay(answer({
      freshThrough: '2026-08-04',
      onHand: items.reduce((a, i) => a + (i.onHand ?? 0), 0),
      onOrder: items.reduce((a, i) => a + (i.onOrder ?? 0), 0),
      reserved: items.reduce((a, i) => a + (i.reserved ?? 0), 0),
      openPoQty: open.reduce((a, p) => a + p.qty, 0),
      openPoCount: new Set(open.map((p) => p.po)).size,
      // Signed: a credit note subtracts, as the Power BI card's SUM does.
      ytdQtySold: items.reduce((a, i) => a + i.sold, 0),
      belowMinItems: below.length,
      belowMinShortfall: below.reduce((a, i) => a + (i.minInv - (i.onHand ?? 0)), 0),
      inventoryValue: mpMoney(cost, usd, scale),
      // The manager's four summary cards, which replace the Company Overview
      // filters. Item count counts items in reach, INCLUDING the empty one -
      // an item that exists with no stock is still an item you carry.
      itemCount: items.length,
      openSoQty: items.reduce<number | null>((a, i) => (i.openSo === null ? a : (a ?? 0) + i.openSo), null),
      poQtyYtd: this.#mpPoQty(thisYear, (p) => this.#mpPoOk(p, options)),
      poQtyPY: this.#mpPoQty(thisYear - 1, (p) => this.#mpPoOk(p, options)),
      // Every tile is a rollup over the whole selection, so both are usually
      // "mixed" until the reader filters down to one company.
      unit: units.value,
      unitCount: units.count,
      currencyCode: code ?? currencies.value,
      currencyCount: usd ? 1 : currencies.count,
      // The demo signs in as a System Admin, who bypasses the column mask, so
      // both are true here. The interesting case the real procedure covers —
      // cost visible but none recorded — is already reachable in the demo data,
      // because #mpOne leaves `cost` null for items that carry none.
      canSeeCost: true,
      canSeeQtySold: true,
    }))
  }

  materialPlanningBreakdown(
    options: { kind: MpBreakdownKind; year?: number; company?: string; bu?: string; itemGroup?: string
               supplier?: string; currency?: Currency; viewAs?: string },
  ): Promise<Answer<MpBreakdownRow[]>> {
    const { usd, scale } = demoFx(options.currency)
    const items = DEMO_MP_ITEMS.filter((i) => this.#mpItemOk(i, options))
    const groups = new Map<string, { label: string; value: number | null; valuePY: number | null }>()
    if (options.kind === 'cost-by-unit') {
      for (const i of items) {
        const g = groups.get(i.bu) ?? { label: i.buName, value: null, valuePY: null }
        if (i.cost !== null) g.value = (g.value ?? 0) + i.cost
        groups.set(i.bu, g)
      }
      return delay(answer([...groups.entries()]
        .map(([code, g]) => ({ label: g.label, code, value: mpMoney(g.value, usd, scale), valuePY: null }))
        .sort((a, b) => (b.value ?? -1) - (a.value ?? -1))))
    }
    // Absolute, as the Power BI bar chart's measures are.
    for (const i of items) {
      const g = groups.get(i.company) ?? { label: i.companyName, value: 0, valuePY: 0 }
      g.value = (g.value ?? 0) + Math.abs(i.sold)
      g.valuePY = (g.valuePY ?? 0) + Math.abs(i.soldPY)
      groups.set(i.company, g)
    }
    return delay(answer([...groups.entries()]
      .map(([code, g]) => ({ label: g.label, code, value: g.value, valuePY: g.valuePY }))
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))))
  }

  /**
   * Stock on hand at whichever grain the bookmark navigator asked for. ONE
   * shape at every grain, exactly as the procedure does it: at a coarser grain
   * the columns that no longer identify anything come back null rather than
   * carrying an arbitrary member's value, and `groupCode`/`groupLabel` say
   * what was grouped. The demo has one warehouse per item, so 'item' and
   * 'product' agree here where in production they would not.
   */
  materialPlanningStock(
    options: { company?: string; bu?: string; itemGroup?: string; warehouse?: string; supplier?: string
               search?: string; includeEmpty?: boolean; sortBy?: MpStockSort; groupBy?: MpStockGroupBy
               currency?: Currency; page?: number; pageSize?: number; sortDesc?: boolean; viewAs?: string } = {},
  ): Promise<Answer<MpStockRow[]>> {
    const { usd, scale, code } = demoFx(options.currency)
    const term = options.search?.toLowerCase()
    const grain = options.groupBy ?? 'item'
    const empty = (i: DemoMpItem) => !i.onHand && !i.onOrder && !i.reserved && i.value === null
    const matched = DEMO_MP_ITEMS.filter((i) =>
      this.#mpItemOk(i, options) &&
      (!options.warehouse || i.warehouse === options.warehouse) &&
      (options.includeEmpty || !empty(i)) &&
      (!term || i.item.toLowerCase().includes(term) || i.product.toLowerCase().includes(term)))

    // A sum that stays NULL when every contributing value is null, so a column
    // nobody recorded reads as an em dash rather than a confident zero.
    const sum = (xs: DemoMpItem[], pick: (i: DemoMpItem) => number | null) =>
      xs.reduce<number | null>((a, i) => (pick(i) === null ? a : (a ?? 0) + (pick(i) as number)), null)
    const zero = (xs: DemoMpItem[], pick: (i: DemoMpItem) => number | null) =>
      xs.reduce((a, i) => a + (pick(i) ?? 0), 0)

    const groupKey = (i: DemoMpItem) =>
      grain === 'company' ? i.company
        : grain === 'bu' ? `${i.company}|${i.bu}`
        : grain === 'bt' ? `${i.company}|${i.bu}|${i.bt}`
        : grain === 'product' ? `${i.company}|${i.bu}|${i.bt}|${i.item}`
        : `${i.company}|${i.bu}|${i.bt}|${i.item}|${i.warehouse ?? ''}`

    const buckets = new Map<string, DemoMpItem[]>()
    for (const i of matched) {
      const k = groupKey(i)
      const b = buckets.get(k)
      if (b) b.push(i)
      else buckets.set(k, [i])
    }

    const atItem = grain === 'item' || grain === 'product'
    const atBt = atItem || grain === 'bt'
    const atBu = atBt || grain === 'bu'

    const rows = [...buckets.values()].map((g) => {
      const head = g[0]
      const onHand = sum(g, (i) => i.onHand)
      const onOrder = sum(g, (i) => i.onOrder)
      const openSo = sum(g, (i) => i.openSo)
      const pallets = g.reduce<number | null>((a, i) =>
        (i.pallet === null || i.onHand === null ? a : (a ?? 0) + Math.ceil(i.onHand / i.pallet)), null)
      const units = this.#mpOne(g.map((i) => i.unit))
      const currencies = this.#mpOne(g.map((i) => mpCur(i.company)))
      const orders = new Set(DEMO_MP_POS
        .filter((p) => g.some((i) => p.item === i.item && p.company === i.company) && this.#mpOpenByFlag(p))
        .map((p) => p.po))
      return {
        totalRows: buckets.size,
        company: head.company,
        companyName: head.companyName,
        itemNumber: atItem ? head.item : null,
        productName: atItem ? head.product : null,
        itemGroup: atItem ? head.group : null,
        businessUnit: atBu ? head.bu : null,
        businessUnitName: atBu ? head.buName : null,
        businessTeam: atBt ? head.bt : null,
        warehouse: grain === 'item' ? head.warehouse : null,
        onHand,
        physicalInv: sum(g, (i) => i.physical),
        onOrder,
        reserved: sum(g, (i) => i.reserved),
        minStock: zero(g, (i) => i.minStock),
        // A missing pallet size gives a null count, never a zero.
        pallets,
        inventoryCost: mpMoney(sum(g, (i) => i.cost), usd),
        inventoryValue: mpMoney(sum(g, (i) => i.value), usd),
        // A unit cost only means anything for one item; above that it would be
        // an average of unlike things, so it is not offered.
        unitCost: atItem ? mpMoney(head.unitCost, usd, scale) : null,
        minInventory: zero(g, (i) => i.minInv),
        // The old Power BI IPQV2: reserved - on hand + on order.
        position: onHand === null ? null : zero(g, (i) => i.reserved) - onHand + (onOrder ?? 0),
        // NULL above item grain: the count is per item, and rolling it up
        // would double-count an order that spans two of them.
        openPoCount: atItem ? orders.size : 0,
        palletSize: atItem ? head.pallet : null,
        lastMovement: g.reduce<string | null>((a, i) => (i.lastMove && (!a || i.lastMove > a) ? i.lastMove : a), null),
        daysSinceMovement: g.reduce<number | null>((a, i) => (i.days === null ? a : a === null ? i.days : Math.min(a, i.days)), null),
        businessTeamName: atBt ? head.team : null,
        openSoQty: openSo,
        // The manager's Difference. POSITIVE means short - the opposite sign
        // from `position` beside it, which ADDS what is on order.
        shortfall: openSo === null ? null : openSo - ((onHand ?? 0) + (onOrder ?? 0)),
        groupCode: grain === 'company' ? head.company
          : grain === 'bu' ? head.bu
          : grain === 'bt' ? head.bt
          : head.item,
        groupLabel: grain === 'company' ? head.companyName
          : grain === 'bu' ? head.buName
          : grain === 'bt' ? head.team
          : head.product,
        supplier: atItem ? head.supplier : null,
        unit: units.value,
        unitCount: units.count,
        currencyCode: code ?? currencies.value,
        currencyCount: usd ? 1 : currencies.count,
      }
    })

    const key = (r: MpStockRow) =>
      options.sortBy === 'pallets' ? (r.pallets ?? -1)
        : options.sortBy === 'movement' ? (r.daysSinceMovement ?? -1)
        : options.sortBy === 'item' ? 0
        : (r.onHand ?? -1)
    rows.sort((a, b) => key(b) - key(a) || (a.groupCode ?? '').localeCompare(b.groupCode ?? ''))
    const size = options.pageSize ?? 100
    const from = ((options.page ?? 1) - 1) * size
    return delay(answer(this.#mpSort(rows, options.sortBy, options.sortDesc).slice(from, from + size)))
  }

  /**
   * THE DEMO SORTS WHERE THE SERVER SORTS, so the demo build shows the feature
   * rather than a row of headings that do nothing.
   *
   * Matching is on the LOWERCASED field name, which bridges the two
   * conventions the procedures use without a table of pairs: the purchase and
   * shipment grids name their columns after their output aliases ('PoNumber'),
   * the stock grid after lowercase keys of its own ('dayssincemovement'), and
   * both land on the row's camelCase property once case is discarded. The
   * three legacy dropdown values are the only ones that name nothing, so they
   * are the only aliases written out.
   *
   * Applied AFTER the rows are built and BEFORE the page is cut, which is the
   * order the server works in — sorting the page instead would reproduce the
   * bug the whole server-side sort exists to avoid.
   */
  #mpSort<T extends object>(rows: T[], sortBy?: string, sortDesc?: boolean): T[] {
    if (!sortBy) return rows
    const ALIAS: Record<string, string> = {
      onhand: 'onHand', pallets: 'pallets', movement: 'daysSinceMovement', item: 'itemNumber',
    }
    const want = (ALIAS[sortBy.toLowerCase()] ?? sortBy).toLowerCase()
    const key = Object.keys(rows[0] ?? {}).find((k) => k.toLowerCase() === want)
    if (!key) return rows
    const desc = sortDesc !== false
    const dir = desc ? -1 : 1
    return [...rows].sort((a, b) => {
      const av = (a as Record<string, unknown>)[key]
      const bv = (b as Record<string, unknown>)[key]
      // The house rule, mirrored: a masked figure is unknown, so it sorts LAST
      // whichever way the arrow points — never treated as the smallest number.
      if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1
      if (bv === null || bv === undefined) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
      return String(av).localeCompare(String(bv)) * dir
    })
  }

  materialPlanningPurchaseOrders(
    options: { status?: MpOrderStatus; approvalStatus?: number; company?: string; supplier?: string
               modeOfDelivery?: string; supplierCategory?: string; bu?: string; itemGroup?: string
               search?: string; currency?: Currency; page?: number; pageSize?: number
               sortBy?: string; sortDesc?: boolean; viewAs?: string } = {},
  ): Promise<Answer<MpPurchaseOrderRow[]>> {
    const { usd, scale, code } = demoFx(options.currency)
    const status = options.status ?? 'open'
    const term = options.search?.toLowerCase()
    const all = DEMO_MP_POS.filter((p) =>
      (status === 'all'
        || (status === 'open' && [1, 2].includes(p.lineStatus))
        || (status === 'review' && [1, 2].includes(p.lineStatus) && p.approval === 10)
        || (status === 'closed' && p.lineStatus === 3)
        || (status === 'cancelled' && p.lineStatus === 4)) &&
      (options.approvalStatus === undefined || p.approval === options.approvalStatus) &&
      (!options.modeOfDelivery || p.mode === options.modeOfDelivery) &&
      (!options.supplierCategory || p.category === options.supplierCategory) &&
      this.#mpPoOk(p, options) &&
      (!term || p.po.toLowerCase().includes(term) || p.item.toLowerCase().includes(term)
        || p.itemName.toLowerCase().includes(term)))
    all.sort((a, b) => b.poDate.localeCompare(a.poDate) || a.po.localeCompare(b.po))
    const size = options.pageSize ?? 100
    const from = ((options.page ?? 1) - 1) * size
    const totals = demoPoTotals(all, usd, scale, code)
    const built = all.map((p) => ({
      totalRows: all.length,
      company: p.company, poNumber: p.po, lineNumber: p.line,
      itemId: p.item, itemName: p.itemName, supplier: p.supplier, site: p.site,
      currencyCode: code ?? p.currency, poDate: p.poDate, deliveryDate: p.delivery,
      etaDate: p.eta, grnDate: p.grn,
      lineStatusCode: p.lineStatus, approvalCode: p.approval,
      orderStatus: p.orderStatus, shipmentProgress: p.progress, fileClosed: p.fileClosed,
      quantity: p.qty,
      // Kept null on purpose: unknown is not zero.
      stillDue: p.stillDue,
      unitPrice: mpMoney(p.price, usd, scale), priceBasis: p.basis,
      lineAmount: mpMoney(p.amount, usd, scale),
      etdDate: p.etd, shipDate: p.ship, shipDate2: p.ship2, readinessDate: p.readiness,
      deliveryTerms: p.terms, mawb: p.mawb, modeOfDelivery: p.mode, responsible: p.responsible,
      orderProgress: p.orderProgress, shipmentStatus: p.shipmentStatus, supplierCategory: p.category,
      unit: p.unit,
      ...totals,
    }))
    return delay(answer(this.#mpSort(built, options.sortBy, options.sortDesc).slice(from, from + size)))
  }

  /**
   * The Shipment Tracker and, under `view`, the manager's two new screens.
   * The date rule is the ONLY thing that differs between them, which is why
   * all three read one list here as they read one procedure on the server.
   *
   * 'late' is an ETA already past with NO goods receipt. The manager's note
   * says "ETA < Today && Shipment Status = Received", but Received is an order
   * status, not a shipment status, and that literal condition matches nothing
   * in production - so the rule is read as what it plainly means.
   */
  materialPlanningShipments(
    options: { view?: MpShipmentView; company?: string; supplier?: string; modeOfDelivery?: string
               shipmentStatus?: string; orderProgress?: string; orderStatus?: string
               bu?: string; itemGroup?: string; supplierCategory?: string; search?: string; currency?: Currency
               page?: number; pageSize?: number; sortBy?: string; sortDesc?: boolean; viewAs?: string } = {},
  ): Promise<Answer<MpShipmentRow[]>> {
    const { usd, scale, code } = demoFx(options.currency)
    const term = options.search?.toLowerCase()
    const view = options.view ?? 'tracker'
    const today = mpDay(0)
    const soon = mpDay(30)
    const inView = (p: DemoMpPo) =>
      view === 'arriving-soon' ? (p.eta >= today && p.eta <= soon && [1, 2].includes(p.lineStatus))
        : view === 'late' ? (p.eta < today && p.grn === null)
        // The Power BI page's own predicate, verbatim.
        : (p.lineStatus === 1 && p.approval === 40 && p.orderStatus === 'Open Order')
    const all = DEMO_MP_POS.filter((p) =>
      inView(p) &&
      (!options.modeOfDelivery || p.mode === options.modeOfDelivery) &&
      (!options.shipmentStatus || p.shipmentStatus === options.shipmentStatus) &&
      (!options.orderProgress || p.orderProgress === options.orderProgress) &&
      (!options.orderStatus || p.orderStatus === options.orderStatus) &&
      (!options.supplierCategory || p.category === options.supplierCategory) &&
      this.#mpPoOk(p, options) &&
      (!term || p.po.toLowerCase().includes(term) || p.item.toLowerCase().includes(term)
        || p.itemName.toLowerCase().includes(term) || (p.mawb ?? '').toLowerCase().includes(term)))
    all.sort((a, b) => (b.readiness ?? '').localeCompare(a.readiness ?? '') || a.po.localeCompare(b.po))
    const size = options.pageSize ?? 100
    const from = ((options.page ?? 1) - 1) * size
    const totals = demoPoTotals(all, usd, scale, code)
    const built = all.map((p) => ({
      totalRows: all.length,
      company: p.company, poNumber: p.po, lineNumber: p.line,
      itemId: p.item, itemName: p.itemName, supplier: p.supplier,
      poDate: p.poDate, readinessDate: p.readiness, etdDate: p.etd, etaDate: p.eta,
      shipDate: p.ship, shipDate2: p.ship2, grnDate: p.grn,
      mawb: p.mawb, modeOfDelivery: p.mode, orderProgress: p.orderProgress, shipmentStatus: p.shipmentStatus,
      currencyCode: code ?? p.currency, orderStatus: p.orderStatus, responsible: p.responsible,
      lineStatusCode: p.lineStatus, quantity: p.qty, stillDue: p.stillDue,
      unitPrice: mpMoney(p.price, usd, scale), priceBasis: p.basis,
      lineAmount: mpMoney(p.amount, usd, scale),
      orderer: p.orderer, deliveryTerms: p.terms, site: p.site, approvalCode: p.approval,
      unit: p.unit,
      ...totals,
    }))
    return delay(answer(this.#mpSort(built, options.sortBy, options.sortDesc).slice(from, from + size)))
  }

  materialPlanningSalesVsLy(
    options: { year?: number; company?: string; bu?: string; itemGroup?: string; supplier?: string
               currency?: Currency; viewAs?: string } = {},
  ): Promise<Answer<MpSalesMonth[]>> {
    const { usd, scale } = demoFx(options.currency)
    const year = options.year ?? 2026
    const items = DEMO_MP_ITEMS.filter((i) => this.#mpItemOk(i, options))
    // Signed, so a credit note subtracts here as it does on the summary.
    const base = items.reduce((a, i) => a + i.sold, 0)
    const basePY = items.reduce((a, i) => a + i.soldPY, 0)
    // A visible seasonal dip, so the chart has something to say.
    const curve = [0.9, 0.95, 1.05, 1.1, 1.15, 1.0, 0.7, 0.75, 1.1, 1.2, 1.15, 1.05]
    return delay(answer(curve.map((f, n) => ({
      monthNumber: n + 1,
      monthStart: year + '-' + String(n + 1).padStart(2, '0') + '-01',
      sales: mpMoney(Math.round(base * f * 11 / 12), usd, scale),
      salesPY: mpMoney(Math.round(basePY * f * 11 / 12), usd, scale),
      qty: Math.round(base * f / 12),
      qtyPY: Math.round(basePY * f / 12),
    }))))
  }

  materialPlanningSalesPerformance(
    options: { year?: number; company?: string; bu?: string; itemGroup?: string; supplier?: string
               search?: string; page?: number; pageSize?: number; sortBy?: string; sortDesc?: boolean; viewAs?: string } = {},
  ): Promise<Answer<MpSalesPerformanceRow[]>> {
    const term = options.search?.toLowerCase()
    // Absolute, as the Power BI "Sales Qty Abs" measures are.
    const all = DEMO_MP_ITEMS.filter((i) =>
      this.#mpItemOk(i, options) && (i.sold !== 0 || i.soldPY !== 0) &&
      (!term || i.item.toLowerCase().includes(term) || i.product.toLowerCase().includes(term)))
      .map((i) => {
        const qty = Math.abs(i.sold), py = Math.abs(i.soldPY)
        return { i, qty, py }
      })
      .sort((a, b) => b.qty - a.qty || a.i.item.localeCompare(b.i.item))
    const size = options.pageSize ?? 100
    const from = ((options.page ?? 1) - 1) * size
    const built = all.map(({ i, qty, py }) => ({
      totalRows: all.length,
      company: i.company, companyName: i.companyName,
      itemNumber: i.item, productName: i.product, businessTeam: i.team, supplier: i.supplier,
      qtySold: qty, qtySoldPY: py, qtyDiff: qty - py,
      variancePct: py === 0 ? null : Math.round(((qty - py) / py) * 1e6) / 1e6,
      unit: i.unit,
      // The demo signs in as a System Admin, who bypasses the column mask.
      canSeeQtySold: true,
    }))
    return delay(answer(this.#mpSort(built, options.sortBy, options.sortDesc).slice(from, from + size)))
  }

  materialPlanningItemStatus(
    options: { company?: string; bu?: string; itemGroup?: string; supplier?: string; search?: string
               year?: number; includeEmpty?: boolean; currency?: Currency
               page?: number; pageSize?: number; sortBy?: string; sortDesc?: boolean; viewAs?: string } = {},
  ): Promise<Answer<MpItemStatusRow[]>> {
    const { usd, scale, code } = demoFx(options.currency)
    const term = options.search?.toLowerCase()
    const openFor = (i: DemoMpItem) => DEMO_MP_POS.filter((p) => p.item === i.item && p.company === i.company && this.#mpOpenByFlag(p))
    const empty = (i: DemoMpItem) =>
      !i.onHand && !i.onOrder && !i.reserved && i.value === null && openFor(i).length === 0 && i.sold === 0 && i.soldPY === 0
    const all = DEMO_MP_ITEMS.filter((i) =>
      this.#mpItemOk(i, options) &&
      (options.includeEmpty || !empty(i)) &&
      (!term || i.item.toLowerCase().includes(term) || i.product.toLowerCase().includes(term)))
    all.sort((a, b) => (b.onHand ?? -1) - (a.onHand ?? -1) || a.item.localeCompare(b.item))
    const size = options.pageSize ?? 100
    const from = ((options.page ?? 1) - 1) * size
    const built = all.map((i) => {
      const open = openFor(i)
      const qty = Math.abs(i.sold), py = Math.abs(i.soldPY)
      const year = options.year ?? new Date().getFullYear()
      const sameItem = (p: DemoMpPo) => p.item === i.item && p.company === i.company
      const poY = this.#mpPoQty(year, sameItem), poPY = this.#mpPoQty(year - 1, sameItem)
      return {
        totalRows: all.length,
        company: i.company, itemNumber: i.item, productName: i.product,
        businessUnit: i.bu, itemGroup: i.group,
        onHand: i.onHand, onOrder: i.onOrder, minStock: i.minStock,
        // Against the REAL minimum, and null when none is set.
        belowMinimum: i.minInv > 0 ? (i.onHand ?? 0) - i.minInv : null,
        inventoryValue: mpMoney(i.value, usd, scale),
        // No `|| null`: no open line IS zero, and null on this wire means masked.
        poQty: open.reduce((a, p) => a + p.qty, 0),
        qtySold: qty,
        supplier: i.supplier, minInventory: i.minInv, reserved: i.reserved,
        openPoCount: new Set(open.map((p) => p.po)).size,
        qtySoldPY: py, qtyDiff: qty - py,
        inventoryCost: mpMoney(i.cost, usd, scale),
        // The Company Overview column order the manager wrote out: PO YTD,
        // PO PY, their difference, then sold, open SOs, sold PY, and the
        // second difference - sold plus open SOs, less last year.
        poQtyYtd: poY,
        poQtyPY: poPY,
        poQtyDiff: poY - poPY,
        openSoQty: i.openSo,
        netSoldDiff: qty + (i.openSo ?? 0) - py,
        // One item in one company: both are single by construction.
        unit: i.unit,
        currencyCode: code ?? mpCur(i.company),
      }
    })
    return delay(answer(this.#mpSort(built, options.sortBy, options.sortDesc).slice(from, from + size)))
  }

  materialPlanningItemSummary(
    options: { company?: string; bu?: string; itemGroup?: string; supplier?: string; item?: string
               year?: number; currency?: Currency; viewAs?: string } = {},
  ): Promise<Answer<MpItemSummary>> {
    const { usd, scale, code } = demoFx(options.currency)
    const year = options.year ?? 2026
    const items = DEMO_MP_ITEMS.filter((i) => this.#mpItemOk(i, options) && (!options.item || i.item === options.item))
    // The page's selected-year predicate, for BOTH years.
    const pos = DEMO_MP_POS.filter((p) =>
      [1, 2, 3].includes(p.lineStatus) && [40, 50].includes(p.approval) &&
      this.#mpPoOk(p, options) && (!options.item || p.item === options.item))
    const poY = pos.filter((p) => p.poYear === year), poPY = pos.filter((p) => p.poYear === year - 1)
    const sum = (xs: (number | null)[]) => xs.reduce<number>((a, v) => a + (v ?? 0), 0)
    const money = (xs: (number | null)[]) => xs.reduce<number | null>((a, v) => (v === null ? a : (a ?? 0) + v), null)
    const physical = sum(items.map((i) => i.physical)), onOrder = sum(items.map((i) => i.onOrder))
    const poQtyY = sum(poY.map((p) => p.qty)), poQtyPY = sum(poPY.map((p) => p.qty))
    const poValY = money(poY.map((p) => p.amount)), poValPY = money(poPY.map((p) => p.amount))
    // Signed, as the page's own year measures were.
    const salY = sum(items.map((i) => i.sold)), salPY = sum(items.map((i) => i.soldPY))
    const salValY = salY * 11, salValPY = salPY * 11
    const inv = money(items.map((i) => i.cost))
    const sumUnits = this.#mpOne(items.map((i) => i.unit))
    const sumCurrencies = this.#mpOne(items.map((i) => mpCur(i.company)))
    return delay(answer({
      year,
      onHand: sum(items.map((i) => i.onHand)),
      physicalInv: physical,
      onOrder,
      totalQty: physical + onOrder,
      reserved: sum(items.map((i) => i.reserved)),
      poQtyYear: poQtyY, poQtyPY, poQtyDiff: poQtyY - poQtyPY,
      poValueYear: mpMoney(poValY, usd, scale), poValuePY: mpMoney(poValPY, usd, scale),
      poValueDiff: poValY === null && poValPY === null ? null : mpMoney((poValY ?? 0) - (poValPY ?? 0), usd, scale),
      salesQtyYear: salY, salesQtyPY: salPY, salesQtyDiff: salY - salPY,
      salesValueYear: mpMoney(salValY, usd, scale), salesValuePY: mpMoney(salValPY, usd, scale),
      salesValueDiff: mpMoney(salValY - salValPY, usd, scale),
      inventoryValue: mpMoney(inv, usd, scale),
      unit: sumUnits.value,
      unitCount: sumUnits.count,
      currencyCode: code ?? sumCurrencies.value,
      currencyCount: usd ? 1 : sumCurrencies.count,
    }))
  }

  /**
   * The On Order tooltip: which open orders make up that number. Ordered by
   * ETA with nulls last, because "when does this arrive" is the question the
   * tooltip exists to answer.
   */
  materialPlanningItemOrders(
    options: { item: string; company?: string; currency?: Currency; viewAs?: string },
  ): Promise<MpItemOrderRow[]> {
    const { usd, scale, code } = demoFx(options.currency)
    // Still commercially live, NOT open-by-file-flag: see the procedure. The
    // flag is null on half of production, which left this panel empty beside
    // a real On Order figure.
    const rows = DEMO_MP_POS
      .filter((p) => p.item === options.item && [1, 2].includes(p.lineStatus)
        && (!options.company || p.company === options.company))
      .sort((a, b) => (a.eta || '9999').localeCompare(b.eta || '9999') || a.po.localeCompare(b.po))
      .map((p) => ({
        poNumber: p.po, lineNumber: p.line, company: p.company, supplier: p.supplier,
        quantity: p.qty, stillDue: p.stillDue, etaDate: p.eta,
        lineAmount: mpMoney(p.amount, usd, scale),
        unit: p.unit,
        currencyCode: code ?? p.currency,
      }))
    return delay(rows)
  }

  /* ---------------------------------------------------------- inventory aging
     The same rules as 040, on fourteen invented lots: ages and expiry counted
     from TODAY, drop-ship in no age bucket, a 1900 expiry counted as expired,
     days only on lot rows, unit cost = value / quantity. The demo signs in as
     a System Admin, so nothing is masked here; the fixture proves the masks. */

  #invOk(l: DemoInvLot, o: { company?: string; bu?: string; bt?: string; warehouse?: string; search?: string }) {
    const term = o.search?.trim().toLowerCase()
    return (!o.company || l.company === o.company)
      && (!o.bu || l.bu === o.bu)
      && (!o.bt || l.bt === o.bt)
      && (!o.warehouse || l.warehouse === o.warehouse)
      && (!term || l.item.toLowerCase().includes(term) || l.product.toLowerCase().includes(term)
        || l.lot.toLowerCase().includes(term))
  }

  /** The sorter, as #mpSort: on the lowercased field name, unknowns last either way. */
  #invSort<T extends object>(rows: T[], sortBy?: string, sortDesc?: boolean): T[] {
    if (!sortBy) return rows
    const want = sortBy.toLowerCase()
    const key = Object.keys(rows[0] ?? {}).find((k) => k.toLowerCase() === want)
    if (!key) return rows
    const dir = sortDesc !== false ? -1 : 1
    return [...rows].sort((a, b) => {
      const av = (a as Record<string, unknown>)[key]
      const bv = (b as Record<string, unknown>)[key]
      if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1
      if (bv === null || bv === undefined) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
      return String(av).localeCompare(String(bv)) * dir
    })
  }

  inventoryAgingFilters(_options: { viewAs?: string } = {}): Promise<InvFilterRow[]> {
    const rows: InvFilterRow[] = []
    const seen = new Set<string>()
    const add = (kind: InvFilterRow['kind'], code: string | null, label?: string | null) => {
      if (!code) return
      const key = kind + '|' + code
      if (seen.has(key)) return
      seen.add(key)
      rows.push({ kind, code, label: label ?? code })
    }
    for (const l of DEMO_INV_LOTS) {
      add('company', l.company, l.companyName)
      add('businessUnit', l.bu)
      add('businessTeam', l.bt)
      add('warehouse', l.warehouse)
    }
    rows.sort((a, b) => a.kind.localeCompare(b.kind) || (a.label ?? '').localeCompare(b.label ?? ''))
    return delay(rows)
  }

  inventoryAgingOverview(
    options: { company?: string; bu?: string; bt?: string; warehouse?: string; search?: string; currency?: Currency
               ageBand?: InvAgeBand; viewAs?: string } = {},
  ): Promise<Answer<InvOverview>> {
    const { usd, scale, code } = demoFx(options.currency)
    const lots = DEMO_INV_LOTS.filter((l) => this.#invOk(l, options)).map(invFacts)
      .filter((x) => !options.ageBand || x.age === options.ageBand)
    const sum = (xs: typeof lots, pick: (x: (typeof lots)[number]) => number) => xs.reduce((a, x) => a + pick(x), 0)
    const v2 = demoV2()
    const noMoney = (x: (typeof lots)[number]) => v2 && x.l.company === DEMO_V2_NO_MONEY
    const cash = (xs: typeof lots) =>
      xs.length > 0 && xs.every(noMoney) ? null : mpMoney(sum(xs.filter((x) => !noMoney(x)), (x) => x.l.value), usd, scale)
    const partly = (xs: typeof lots) => xs.some(noMoney) && xs.some((x) => !noMoney(x))
    const dated = lots.filter((x) => !x.drop && x.daysOld !== null && x.daysOld >= 0)
    const over = (n: number) => dated.filter((x) => (x.daysOld as number) >= n)
    const expired = lots.filter((x) => x.daysToExpire !== null && x.daysToExpire < 0)
    const within = (n: number) => lots.filter((x) => x.daysToExpire !== null && x.daysToExpire >= 0 && x.daysToExpire <= n)
    const drop = lots.filter((x) => x.drop)
    const units = this.#mpOne(lots.map((x) => x.l.unit))
    const currencies = this.#mpOne(lots.map((x) => x.l.currency))
    const partial = ([
      ['value', lots], ['valueOver180', over(181)], ['valueOver240', over(241)], ['valueOver365', over(366)],
      ['valueDrop', drop], ['expiredValue', expired], ['within90Value', within(90)], ['within180Value', within(180)],
    ] as const).filter(([, xs]) => partly(xs)).map(([name]) => name).join(',') || null
    return delay(answer({
      partial,
      lots: lots.length,
      items: new Set(lots.map((x) => `${x.l.company}|${x.l.item}`)).size,
      companies: new Set(lots.map((x) => x.l.company)).size,
      quantity: sum(lots, (x) => x.l.qty),
      value: cash(lots),
      qtyOver180: sum(over(181), (x) => x.l.qty),
      qtyOver240: sum(over(241), (x) => x.l.qty),
      qtyOver365: sum(over(366), (x) => x.l.qty),
      valueOver180: cash(over(181)),
      valueOver240: cash(over(241)),
      valueOver365: cash(over(366)),
      qtyDrop: sum(drop, (x) => x.l.qty),
      valueDrop: cash(drop),
      unknownAgeLots: lots.filter((x) => x.age === 'unknown').length,
      expiredLots: expired.length,
      expiredQty: sum(expired, (x) => x.l.qty),
      expiredValue: cash(expired),
      within90Lots: within(90).length,
      within90Qty: sum(within(90), (x) => x.l.qty),
      within90Value: cash(within(90)),
      within180Lots: within(180).length,
      within180Qty: sum(within(180), (x) => x.l.qty),
      within180Value: cash(within(180)),
      noExpiryLots: lots.filter((x) => x.l.expiry === null).length,
      unit: units.value,
      unitCount: units.count,
      currencyCode: code ?? currencies.value,
      currencyCount: usd ? 1 : currencies.count,
      canSeeCost: true,
    }))
  }

  inventoryAgingMatrix(
    options: { groupBy?: InvGroupBy; company?: string; bu?: string; bt?: string; warehouse?: string; search?: string
               currency?: Currency; sortBy?: string; sortDesc?: boolean; page?: number; pageSize?: number
               pathCompany?: string; pathUnit?: string; pathUnitNone?: boolean
               pathTeam?: string; pathTeamNone?: boolean; pathItem?: string
               ageBand?: InvAgeBand; viewAs?: string } = {},
  ): Promise<Answer<InvMatrixRow[]>> {
    const { usd, scale, code } = demoFx(options.currency)
    const grain = options.groupBy ?? 'company'
    // The drill path narrows exactly as 040's does, the blank member included.
    const onPath = (l: DemoInvLot) =>
      (!options.pathCompany || l.company === options.pathCompany)
      && (options.pathUnitNone ? l.bu === null : !options.pathUnit || l.bu === options.pathUnit)
      && (options.pathTeamNone ? !l.bt : !options.pathTeam || l.bt === options.pathTeam)
      && (!options.pathItem || l.item === options.pathItem)
    const lots = DEMO_INV_LOTS.filter((l) => this.#invOk(l, options) && onPath(l)).map(invFacts)
      .filter((x) => !options.ageBand || x.age === options.ageBand)
    const keyOf = (x: (typeof lots)[number]) =>
      grain === 'company' ? x.l.company
        : grain === 'bu' ? `${x.l.company}|${x.l.bu ?? ''}`
        : grain === 'bt' ? `${x.l.company}|${x.l.bu ?? ''}|${x.l.bt}`
        : grain === 'product' ? `${x.l.company}|${x.l.item}`
        : grain === 'productwh' ? `${x.l.company}|${x.l.item}|${x.l.warehouse}`
        : `${x.l.company}|${x.l.item}|${x.l.lot}|${x.l.warehouse}`
    const groups = new Map<string, typeof lots>()
    for (const x of lots) groups.set(keyOf(x), [...(groups.get(keyOf(x)) ?? []), x])

    const atItem = grain === 'product' || grain === 'productwh' || grain === 'lot'
    const atWarehouse = grain === 'productwh' || grain === 'lot'
    const rows: InvMatrixRow[] = [...groups.values()].map((g) => {
      const head = g[0].l
      const qIn = (band: InvAgeBand) => g.filter((x) => x.age === band).reduce((a, x) => a + x.l.qty, 0)
      const vIn = (band: InvAgeBand) => mpMoney(g.filter((x) => x.age === band).reduce((a, x) => a + x.l.value, 0), usd, scale)
      const qty = g.reduce((a, x) => a + x.l.qty, 0)
      const value = g.reduce((a, x) => a + x.l.value, 0)
      const units = this.#mpOne(g.map((x) => x.l.unit))
      const currencies = this.#mpOne(g.map((x) => x.l.currency))
      const arrivals = g.map((x) => x.l.arrival).filter((d): d is string => !!d).sort()
      const expiries = g.map((x) => x.l.expiry).filter((d): d is string => !!d).sort()
      return {
        totalRows: groups.size,
        groupCode: grain === 'company' ? head.company : grain === 'bu' ? head.bu : grain === 'bt' ? head.bt
          : grain === 'product' || grain === 'productwh' ? head.item : head.lot,
        groupLabel: grain === 'company' ? head.companyName : grain === 'bu' ? head.bu : grain === 'bt' ? head.bt
          : grain === 'product' || grain === 'productwh' ? head.product : head.lot,
        company: head.company,
        companyName: head.companyName,
        businessUnit: grain === 'company' ? null : head.bu,
        businessTeam: grain === 'company' || grain === 'bu' ? null : head.bt,
        itemNumber: atItem ? head.item : null,
        productName: atItem ? head.product : null,
        lot: grain === 'lot' ? head.lot : null,
        warehouse: atWarehouse ? head.warehouse : null,
        lots: g.length,
        quantity: qty,
        value: mpMoney(value, usd, scale),
        unitCost: (atItem || units.count === 1) && qty !== 0 ? mpMoney(value / qty, usd, scale) : null,
        q0_30: qIn('0_30'), q31_60: qIn('31_60'), q61_90: qIn('61_90'), q91_120: qIn('91_120'),
        q121_180: qIn('121_180'), q181_240: qIn('181_240'), q241_365: qIn('241_365'), qOver365: qIn('over365'),
        qDrop: qIn('drop'), qUnknown: qIn('unknown'),
        v0_30: vIn('0_30'), v31_60: vIn('31_60'), v61_90: vIn('61_90'), v91_120: vIn('91_120'),
        v121_180: vIn('121_180'), v181_240: vIn('181_240'), v241_365: vIn('241_365'), vOver365: vIn('over365'),
        vDrop: vIn('drop'), vUnknown: vIn('unknown'),
        oldestArrival: arrivals[0] ?? null,
        earliestExpiry: expiries[0] ?? null,
        daysOld: grain === 'lot' ? g[0].daysOld : null,
        daysToExpire: grain === 'lot' ? g[0].daysToExpire : null,
        unit: units.value,
        unitCount: units.count,
        currencyCode: code ?? currencies.value,
        currencyCount: usd ? 1 : currencies.count,
        description: atItem ? head.description : null,
      }
    })
    rows.sort((a, b) => (b.value ?? 0) - (a.value ?? 0) || (a.groupCode ?? '').localeCompare(b.groupCode ?? ''))
    const size = options.pageSize ?? 100
    const from = ((options.page ?? 1) - 1) * size
    const shown = demoV2()
      ? rows.map((r) => (r.company !== DEMO_V2_NO_MONEY ? r : {
          ...r, value: null, unitCost: null,
          v0_30: null, v31_60: null, v61_90: null, v91_120: null, v121_180: null,
          v181_240: null, v241_365: null, vOver365: null, vDrop: null, vUnknown: null,
        }))
      : rows
    return delay(answer(this.#invSort(shown, options.sortBy, options.sortDesc).slice(from, from + size)))
  }

  inventoryAgingLots(
    options: { window?: InvWindow; company?: string; bu?: string; bt?: string; warehouse?: string; search?: string
               currency?: Currency; sortBy?: string; sortDesc?: boolean; page?: number; pageSize?: number
               ageBand?: InvAgeBand; viewAs?: string } = {},
  ): Promise<Answer<InvLotRow[]>> {
    const { usd, scale, code } = demoFx(options.currency)
    const w = options.window ?? 'all'
    // The three past 180 days are BANDS, as in 040: 181-240, 241-365, 366+.
    const between = (d: number | null, lo: number, hi: number) => d !== null && d >= lo && d <= hi
    const inWindow = (x: ReturnType<typeof invFacts>) =>
      w === 'all' ? true
        : w === 'expired' ? x.daysToExpire !== null && x.daysToExpire < 0
        : w === 'noexpiry' ? x.l.expiry === null
        : w === 'months6to8' ? between(x.daysToExpire, 181, 240)
        : w === 'months8to12' ? between(x.daysToExpire, 241, 365)
        : w === 'over12months' ? x.daysToExpire !== null && x.daysToExpire > 365
        : between(x.daysToExpire, 0, w === 'within30' ? 30 : w === 'within90' ? 90 : 180)
    const lots = DEMO_INV_LOTS.filter((l) => this.#invOk(l, options)).map(invFacts).filter(inWindow)
      .filter((x) => !options.ageBand || x.age === options.ageBand)
    const rows: InvLotRow[] = lots.map((x) => ({
      totalRows: lots.length,
      company: x.l.company, companyName: x.l.companyName, businessUnit: x.l.bu, businessTeam: x.l.bt,
      itemNumber: x.l.item, productName: x.l.product, description: x.l.description, supplier: x.l.supplier,
      lot: x.l.lot, warehouse: x.l.warehouse, unit: x.l.unit,
      mfgDate: x.l.mfg, arrivalDate: x.l.arrival, expiryDate: x.l.expiry, bestBefore: null, shelfAdvice: null,
      daysOld: x.daysOld, daysToExpire: x.daysToExpire, ageBand: x.age, expiryBand: x.expiryBand,
      quantity: x.l.qty, unitCost: mpMoney(x.l.cost, usd, scale), value: mpMoney(x.l.value, usd, scale),
      currencyCode: code ?? x.l.currency,
    }))
    // The screen's own order: soonest expiry first on an expiry window, the
    // biggest value first otherwise — as the procedure orders them.
    const expiryFirst = w !== 'all' && w !== 'noexpiry'
    const sorted = options.sortBy
      ? this.#invSort(rows, options.sortBy, options.sortDesc)
      : expiryFirst
        ? [...rows].sort((a, b) => (a.expiryDate ?? '').localeCompare(b.expiryDate ?? ''))
        : [...rows].sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
    const size = options.pageSize ?? 100
    const from = ((options.page ?? 1) - 1) * size
    const page = sorted.slice(from, from + size)
    return delay(answer(demoV2()
      ? page.map((r) => (r.company === DEMO_V2_NO_MONEY ? { ...r, value: null, unitCost: null } : r))
      : page))
  }

  /** As 043: every company with a customer the reader can see, and how many. */
  statementCompanies(): Promise<StatementCompany[]> {
    return delay(Object.entries(DEMO_STMT_COMPANIES)
      .map(([code, co]): StatementCompany => ({
        company: code, companyName: co.name, customers: DEMO_STMT_CUSTOMERS.filter((c) => c.company === code).length,
      }))
      .filter((c) => c.customers > 0)
      .sort((x, y) => (x.companyName ?? '').localeCompare(y.companyName ?? '')))
  }

  statementCustomers(search: string, companies: string[] = []): Promise<StatementCustomer[]> {
    const term = search.trim().toLowerCase()
    const ticked = new Set(companies.map((c) => c.trim().toUpperCase()))
    // As 043: found by an account in a ticked company, then listed with all of them.
    const hits = new Set(DEMO_STMT_CUSTOMERS
      .filter((c) => ticked.size === 0 || ticked.has(c.company))
      .filter((c) => !term || c.account.toLowerCase().includes(term) || c.name.toLowerCase().includes(term))
      .map((c) => c.party))
    const rows = DEMO_STMT_CUSTOMERS
      .filter((c) => hits.has(c.party))
      .sort((x, y) => x.name.localeCompare(y.name) || x.party.localeCompare(y.party) || x.company.localeCompare(y.company))
      .map((c): StatementCustomer => ({
        totalMatches: hits.size, party: c.party, company: c.company,
        companyName: DEMO_STMT_COMPANIES[c.company].name, customerAccount: c.account, customerName: c.name,
      }))
    return delay(rows)
  }

  statement(party: string, companies: string[], kind: StatementKind, from?: string, to?: string): Promise<Answer<StatementResponse>> {
    const today = isoDay(stmtDay(0))
    // A missing or inverted period is the default, as in the procedure.
    const [f, t] = !from || !to || from > to ? [`${today.slice(0, 4)}-01-01`, today] : [from, to]
    const asked = new Set(companies.map((c) => c.trim().toUpperCase()))
    const accounts = DEMO_STMT_CUSTOMERS.filter((c) => c.party === party && asked.has(c.company))
      .sort((x, y) => x.company.localeCompare(y.company))
    if (accounts.length === 0) {
      // The API's 404, as the real client decodes it.
      return Promise.reject(new ApiError({ kind: 'not-found' }))
    }
    const headers: StatementHeader[] = accounts.map((a) => {
      const co = DEMO_STMT_COMPANIES[a.company]
      // As 043: the limit minus the balance at To, every currency in the company's own.
      const owed = DEMO_STMT
        .filter((r) => r.company === a.company && r.account === a.account && isoDay(stmtDay(r.date)) <= t)
        .reduce((n, r) => n + r.amount * (r.currency === co.currency ? 1 : co.rates[r.currency] ?? 1), 0)
      return {
        company: a.company, companyName: co.name, customerAccount: a.account,
        customerName: a.name, paymentTerms: a.terms, homeCurrency: co.currency,
        party, kind, fromDate: `${f}T00:00:00`, toDate: `${t}T00:00:00`, today: `${today}T00:00:00`,
        companyAddress: co.address, companyPhone: co.phone, companyFax: co.fax, customerAddress: a.address,
        creditLimit: a.creditLimit, creditAvailable: a.creditLimit - owed,
      }
    })
    const lines: StatementLine[] = []
    for (const a of accounts) {
      const co = DEMO_STMT_COMPANIES[a.company]
      const rows = DEMO_STMT
        .filter((r) => r.company === a.company && r.account === a.account)
        .map((r) => ({ ...r, day: isoDay(stmtDay(r.date)), value: kind === 'open' ? r.amount - r.settled : r.amount }))
        .filter((r) => r.amount !== 0 && r.day <= t && r.value !== 0)
      const currencies = [...new Set(rows.map((r) => r.currency))]
        .sort((x, y) => Number(y === co.currency) - Number(x === co.currency) || x.localeCompare(y))
      for (const ccy of currencies) {
        const mine = rows.filter((r) => r.currency === ccy)
        const opening = mine.filter((r) => r.day < f).reduce((n, r) => n + r.value, 0)
        const period = mine.filter((r) => r.day >= f).sort((x, y) =>
          x.day.localeCompare(y.day)
          || (x.due ?? -1e9) - (y.due ?? -1e9)
          || (x.type === 15 ? x.payRef ?? '' : '').localeCompare(y.type === 15 ? y.payRef ?? '' : '')
          || x.voucher.localeCompare(y.voucher)
          || x.amount - y.amount)
        if (opening === 0 && period.length === 0) continue
        let bal = opening
        lines.push({ company: a.company, currency: ccy, rowKind: 'opening', seq: 0, lineDate: `${f}T00:00:00`,
          salesMan: null, document: null, salesOrder: null, reference: null, dueDate: null, overdueDays: null,
          originalAmount: null, debit: null, credit: null, balance: opening })
        period.forEach((r, i) => {
          bal += r.value
          const due = r.due === null ? null : stmtDay(r.due)
          lines.push({
            company: a.company, currency: ccy, rowKind: 'line', seq: i + 1, lineDate: stmtDay(r.date),
            salesMan: co.salesMan,
            document: r.type === 30 ? r.voucher : r.invoice ?? r.voucher,
            salesOrder: r.type === 2 ? r.salesOrder : null,
            reference: r.type === 2 ? r.customerPo : r.type === 15 ? r.payRef : null,
            dueDate: due,
            overdueDays: due === null ? null : Math.round((Date.parse(`${t}T00:00:00Z`) - Date.parse(due)) / 86_400_000),
            originalAmount: kind === 'open' ? r.amount : null,
            debit: r.value > 0 ? r.value : null, credit: r.value < 0 ? -r.value : null, balance: bal,
          })
        })
        lines.push({ company: a.company, currency: ccy, rowKind: 'closing', seq: period.length + 1,
          lineDate: `${t}T00:00:00`, salesMan: null, document: null, salesOrder: null, reference: null,
          dueDate: null, overdueDays: null, originalAmount: null,
          debit: period.reduce((n, r) => n + (r.value > 0 ? r.value : 0), 0),
          credit: period.reduce((n, r) => n + (r.value < 0 ? -r.value : 0), 0),
          balance: bal })
      }
    }
    return delay(answer({ headers, lines }))
  }

  receivablesWeeks(): Promise<Answer<WeekComparison>> {
    return Promise.reject(new ApiError({ kind: 'error', title: NOT_IN_DEMO('Week Comparison') }))
  }

  receivablesTrend(): Promise<Answer<WeekTrend>> {
    return Promise.reject(new ApiError({ kind: 'error', title: NOT_IN_DEMO('Trends') }))
  }

  salespersonReview(): Promise<Answer<SalespersonReview>> {
    return Promise.reject(new ApiError({ kind: 'error', title: NOT_IN_DEMO('Salesperson Review') }))
  }

  accessPeople(filter: AccessPeopleFilter = {}): Promise<AccessPeople> {
    const s = filter.search?.trim().toLowerCase()
    const rows = DEMO_ACCESS_PEOPLE.map(demoAccessRow).filter((r) =>
      (filter.left || r.status !== 'LEFT')
      && (!filter.status || r.status === filter.status)
      && (!filter.company || r.compId === filter.company)
      && (filter.role === undefined || r.roleId === filter.role)
      && (!filter.warnings || r.warningCount > 0)
      && (!filter.personal || r.personalChanges + r.extraScopes > 0)
      && (!filter.approved || !!r.approvalInForce)
      && (!s || [r.displayName, r.email, r.compId, r.roleName].some((v) => v?.toLowerCase().includes(s))))
    // A page of 2,000 as the server sends them; the demo's few people fit on the first.
    const page = Math.max(1, filter.page ?? 1)
    return delay({ people: rows.slice((page - 1) * 2000, page * 2000), activeTotal: DEMO_ACCESS_PEOPLE.filter((p) => p.active ?? true).length,
      listed: rows.length })
  }

  accessPerson(id: number): Promise<AccessPersonPage> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    if (!p) return Promise.reject(new ApiError({ kind: 'error', title: 'No such person in Mindzer.' }))
    return delay(demoAccessPage(p))
  }

  accessHrRoles(): Promise<AccessHrRoles> {
    return delay({ roles: DEMO_HR_ROLES.map(demoHrRoleRow).sort((a, b) => b.people - a.people || a.roleId - b.roleId) })
  }

  accessHrRole(id: number): Promise<AccessHrRolePage> {
    const r = DEMO_HR_ROLES.find((x) => x.id === id)
    if (!r) return Promise.reject(new ApiError({ kind: 'not-found' }))
    const row = demoHrRoleRow(r)
    return delay({
      role: { roleId: r.id, roleName: r.name, roleFunction: r.fn, inSource: true, setUp: r.setUp,
        hierarchyMode: row.hierarchyMode, mobileAllowed: row.mobileAllowed, version: row.version,
        changedByEmail: row.changedByEmail, changeReason: r.setUp ? 'Set up for the go-live wave.' : null, people: row.people },
      reports: DEMO_ACCESS_REPORTS.map((x) => ({ slug: x.slug, displayName: x.label, allowed: r.setUp && r.reports.includes(x.slug) })),
      columnGroups: DEMO_COLUMN_GROUPS.map((g) => ({ groupId: g.id, groupName: g.name, description: g.description,
        reportSlug: g.report, reportName: demoReportName(g.report), allowed: r.setUp && r.groups.includes(g.id), unlocks: demoGroupUnlocks(g.id),
        groupKey: `demo-${g.id}-${g.name}` })),
    })
  }

  accessHrRolePeople(id: number): Promise<AccessPeople> {
    return this.accessPeople({ role: id })
  }

  /** The demo's holders and their rows: invented counts that behave as 052's do (D49). */
  accessHrRoleHierarchyPreview(id: number): Promise<AccessHierarchyPreview> {
    const r = DEMO_HR_ROLES.find((x) => x.id === id)
    if (!r) return Promise.reject(new ApiError({ kind: 'not-found' }))
    const holders = DEMO_ACCESS_PEOPLE.filter((p) => p.role === id && (p.active ?? true))
    const under = (m: number): number[] => DEMO_ACCESS_PEOPLE.filter((p) => p.lineManager === m)
      .flatMap((p) => [p.id, ...under(p.id)])
    const salesperson = new Set(['receivables', 'sales', 'statement-of-account'])
    const rows: AccessHierarchyPreviewRow[] = holders
      .map((p) => ({ p, staff: under(p.id) }))
      .sort((a, b) => b.staff.length - a.staff.length || a.p.id - b.p.id)
      .flatMap(({ p, staff }) => r.reports.map((slug) => {
        const now = 400 + (p.id % 9) * 173 + slug.length * 31
        const applies = salesperson.has(slug)
        const type1 = applies ? Math.round(now * (0.08 + staff.length * 0.11)) : now
        // Only the reports hierarchy cuts inherit staff rows; planning and stock never change.
        const type2 = applies ? type1 + staff.length * 260 : now
        return {
          mindzerUserId: p.id, displayName: `Person ${p.id}`, compId: p.comp,
          staffDirect: DEMO_ACCESS_PEOPLE.filter((s) => s.lineManager === p.id).length, staffAll: staff.length,
          reportSlug: slug, reportName: demoReportName(slug), hierarchyApplies: applies,
          rowsNow: now, rowsType1: Math.min(type1, now), rowsType2: type2,
          modeNow: r.hierarchy, ownSetting: false,
        }
      }))
    return delay({ preview: rows, holders: holders.length, countedUtc: '2026-10-28T00:40:00Z' })
  }

  accessHrRoleHistory(id: number, before?: string): Promise<AccessHistory> {
    const r = DEMO_HR_ROLES.find((x) => x.id === id)
    if (!r) return Promise.reject(new ApiError({ kind: 'not-found' }))
    // The roles set up when the demo starts were set up once, for the go-live wave; then
    // whatever this session saved or removed.
    const first = DEMO_ROLES_SET_UP_AT_START.get(id)
    const seed: AccessHistory['history'] = first ? [{
      occurredUtc: '2026-10-12T09:00:00Z', kind: 'ROLE', what: 'ROLE_SAVED', detail: 'Saved: 0 people changed, 0 gained entry, 0 lost it',
      objectKey: `role:${id}`, beforeJson: '{"Set_Up":0,"Hierarchy_Mode":null,"Mobile_Allowed":null,"Reports":null,"Column_Groups":null}',
      afterJson: first, actorEmail: 'demo.user@redachem.com', reason: 'Set up for the go-live wave.' }] : []
    const rows = [...(demoRoleHistory[id] ?? []), ...seed].filter((x) => !before || x.occurredUtc < before)
    return delay({ history: rows.slice(0, 100) })
  }

  accessHrRolePreview(id: number, body: AccessHrRoleBody): Promise<AccessHrRoleEffect> {
    const r = DEMO_HR_ROLES.find((x) => x.id === id)
    if (!r) return Promise.reject(new ApiError({ kind: 'not-found' }))
    const stray = demoStrayGroup(body.columnGroups, body.reports, 'this role does not give')
    if (stray) return Promise.reject(new ApiError({ kind: 'error', title: stray }))
    return delay({ outcome: 'PREVIEW', summary: demoHrRoleSummary(r, body, 'PREVIEW') })
  }

  accessHrRoleSave(id: number, body: AccessHrRoleBody): Promise<AccessHrRoleEffect> {
    const r = DEMO_HR_ROLES.find((x) => x.id === id)
    if (!r) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (!body.reason?.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    if ((body.expectedVersion ?? null) !== (demoHrRoleRow(r).version ?? null))
      return Promise.reject(new ApiError({ kind: 'error', title: 'Someone else changed this role since you opened it. Reload to see their change.', reason: 'CHANGED_SINCE_READ' }))
    const stray = demoStrayGroup(body.columnGroups, body.reports, 'this role does not give')
    if (stray) return Promise.reject(new ApiError({ kind: 'error', title: stray }))
    const summary = demoHrRoleSummary(r, body, 'SAVED')
    r.setUp = true
    r.reports = [...body.reports]
    r.groups = [...body.columnGroups]
    r.hierarchy = body.hierarchy === 'type2' ? 2 : body.hierarchy === 'type1' ? 1 : 0
    r.phone = body.phone
    r.version = new Date().toISOString()
    for (const p of DEMO_ACCESS_PEOPLE) if (p.role === r.id) p.status = demoStatusOf(p)
    ;(demoRoleHistory[id] ??= []).unshift({
      occurredUtc: r.version, kind: 'ROLE', what: 'ROLE_SAVED',
      detail: `Saved: ${summary.People_Changed} people changed, ${summary.Entry_Gained} gained entry, ${summary.Entry_Lost} lost it`,
      objectKey: `role:${id}`, beforeJson: null, afterJson: null, actorEmail: 'demo.user@redachem.com', reason: body.reason ?? null })
    return delay({ outcome: 'SAVED', summary: { ...summary, Version: r.version } })
  }

  accessHrRoleRemove(id: number, expectedVersion: string | null, reason: string): Promise<AccessHrRoleEffect> {
    const r = DEMO_HR_ROLES.find((x) => x.id === id)
    if (!r) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (!reason.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    if ((expectedVersion ?? null) !== (demoHrRoleRow(r).version ?? null))
      return Promise.reject(new ApiError({ kind: 'error', title: 'Someone else changed this role since you opened it. Reload to see their change.', reason: 'CHANGED_SINCE_READ' }))
    const summary = demoHrRoleSummary(r, { expectedVersion, reports: [], columnGroups: [], columnGroupKeys: {}, hierarchy: 'off', phone: false, remove: true }, 'REMOVED')
    // Its reports and groups go with it, as 052 deletes them: a removed role holds no group.
    r.setUp = false
    r.reports = []
    r.groups = []
    r.version = undefined
    for (const p of DEMO_ACCESS_PEOPLE) if (p.role === r.id) p.status = demoStatusOf(p)
    ;(demoRoleHistory[id] ??= []).unshift({
      occurredUtc: new Date().toISOString(), kind: 'ROLE', what: 'ROLE_REMOVED',
      detail: `No longer used in MBI: ${summary.People_Changed} people changed, ${summary.Entry_Gained} gained entry, ${summary.Entry_Lost} lost it`,
      objectKey: `role:${id}`, beforeJson: null, afterJson: null, actorEmail: 'demo.user@redachem.com', reason })
    return delay({ outcome: 'REMOVED', summary })
  }

  accessProblems(): Promise<AccessProblems> {
    return delay({ categories: DEMO_PROBLEM_CATEGORIES.map(([category, countsWhat]) => ({
      category, countsWhat, items: demoProblemItems(category).filter((i) => !isAllCompanies(i)).length })) })
  }

  accessProblemItems(category: string, page = 1): Promise<AccessProblemItems> {
    const items = demoProblemItems(category)
    return delay({ items: items.slice((page - 1) * 1000, page * 1000), total: items.filter((i) => !isAllCompanies(i)).length })
  }

  accessSync(): Promise<AccessSync> {
    const stale = demoSyncStale()
    const pulls = demoPulls()
    const held = demoAcceptance ? null : pulls[0]
    const good = pulls.find((p) => p.outcome === 'APPLIED' || p.outcome === 'NO_CHANGE')!
    const applied = pulls.find((p) => p.outcome === 'APPLIED')!
    return delay({
      status: {
        state: held ? 'HELD' : stale ? 'STALE' : 'UP_TO_DATE', nowUtc: new Date().toISOString(), staleAfterMinutes: 180,
        lastAttemptId: pulls[0].pullId, lastAttemptUtc: pulls[0].startedUtc, lastAttemptOutcome: pulls[0].outcome,
        lastAttemptCode: pulls[0].code, lastGoodId: good.pullId, lastGoodUtc: good.startedUtc, minutesSinceGood: stale ? 200 : 83,
        people: good.people, activePeople: good.activePeople, peopleBefore: (good.people ?? 0) - 1, activePeopleBefore: (good.activePeople ?? 0) - 1,
        lastAppliedUtc: applied.startedUtc, lastAppliedRows: applied.rowsChanged,
        heldPullId: held?.pullId ?? null, heldUtc: held?.startedUtc ?? null, heldCode: held?.code ?? null, heldDetail: held?.detail ?? null,
        heldCanAccept: !!held, acceptanceId: demoAcceptance?.id ?? null, acceptanceExpiresUtc: demoAcceptance?.expires ?? null,
        codeListCountedUtc: '2026-10-26T02:00:00Z', weeklyFiguresThrough: '2026-10-25', weeklyFiguresBuiltUtc: '2026-10-20T02:30:00Z',
        // sec.Sync_Setting_MBI's defaults (046).
        limitsJson: '{"Max_Drop_Pct":10,"Vanished_Ids_Min":25,"Vanished_Ids_Pct":2.0,"Role_Remap_Min":25,"Role_Remap_Pct":2.0,"Effect_Drop_Min":10,"Effect_Drop_Pct":2.0,"Effect_Entries_Drop_Pct":10.0,"Left_Surge_Min":15,"Left_Surge_Pct":1.5,"Widen_Person_Staff":20,"Widen_Count":25,"Link_Hot_Code_Pct":1.0}',
      },
      pulls,
    })
  }

  accessHeldPull(pullId: number): Promise<AccessHeldPull> {
    const p = demoPulls().find((x) => x.pullId === pullId && (x.outcome === 'HELD' || x.outcome === 'REFUSED'))
    if (!p) return Promise.reject(new ApiError({ kind: 'not-found' }))
    return delay({
      pull: { pullId: p.pullId, startedUtc: p.startedUtc, outcome: p.outcome, code: p.code, detail: p.detail, people: p.people,
        activePeople: p.activePeople, changes: 3, deliveryKept: true, overtaken: false, canAccept: !demoAcceptance,
        acceptanceId: demoAcceptance?.id ?? null, acceptedByEmail: demoAcceptance ? 'demo.user@redachem.com' : null, acceptanceExpiresUtc: demoAcceptance?.expires ?? null },
      changes: [
        { changeKind: 'MANAGER_LINK', mindzerId: 5002, name: 'Person 5002', detail: 'Becomes the line manager of team Gulf Brokers: 300 new staff in R002, R015, R040, R090.' },
        { changeKind: 'HOT_TEAM_LINK', mindzerId: 412, name: 'Gulf Brokers', detail: 'Re-pointed to D365 team T15.' },
        { changeKind: 'NEW_PERSON', mindzerId: 5011, name: 'Person 5011', detail: 'New in Mindzer.' },
      ],
    })
  }

  accessAcceptPull(pullId: number, reason: string): Promise<AccessAcceptResult> {
    if (!reason.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    if (pullId !== DEMO_PULLS[0].pullId) return Promise.reject(new ApiError({ kind: 'error', title: 'Only a held update can be accepted.' }))
    if (demoAcceptance) return delay({ outcome: 'ALREADY', acceptanceId: demoAcceptance.id, expiresUtc: demoAcceptance.expires })
    demoAcceptance = { id: 7, expires: new Date(Date.now() + 86_400_000).toISOString() }
    return delay({ outcome: 'ACCEPTED', acceptanceId: demoAcceptance.id, expiresUtc: demoAcceptance.expires })
  }

  accessEmailDomains(): Promise<AccessEmailDomains> {
    return delay({ domains: [...DEMO_DOMAINS] })
  }

  accessEmailDomainPreview(domain: string, remove: boolean): Promise<AccessEmailDomainEffect> {
    const d = DEMO_DOMAINS.find((x) => x.emailDomain === domain.trim().toLowerCase())
    // The procedure's own keys (sec.usp_Admin_Set_Email_Domain_MBI's summary).
    return delay({ outcome: 'PREVIEW', summary: {
      Outcome: 'PREVIEW', Email_Domain: domain.trim().toLowerCase(), Listed_Before: !!d,
      Active_People: d?.activePeople ?? 0, Approved: d?.approved ?? 0, Can_Enter_Now: d?.approved ?? 0, Sign_Ins: d?.signIns ?? 0,
      Stops_At_Next_Update: remove ? (d?.activePeople ?? 0) : 0,
    } })
  }

  accessEmailDomainSave(domain: string, remove: boolean, reason: string): Promise<AccessEmailDomainEffect> {
    const name = domain.trim().toLowerCase()
    if (!reason.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(name)) return Promise.reject(new ApiError({ kind: 'error', title: 'A domain is letters, digits, dots and hyphens, such as redachem.com.' }))
    const i = DEMO_DOMAINS.findIndex((x) => x.emailDomain === name)
    if (remove) {
      if (i < 0) return Promise.reject(new ApiError({ kind: 'not-found' }))
      DEMO_DOMAINS.splice(i, 1)
      return delay({ outcome: 'REMOVED', summary: null })
    }
    if (i >= 0) return delay({ outcome: 'UNCHANGED', summary: null })
    DEMO_DOMAINS.push({ emailDomain: name, activePeople: 0, approved: 0, signIns: 0, changedByEmail: 'demo.user@redachem.com', changeReason: reason, changedUtc: new Date().toISOString() })
    return delay({ outcome: 'ADDED', summary: null })
  }

  accessStaff(id: number): Promise<AccessStaff> {
    const staff = DEMO_ACCESS_PEOPLE.filter((p) => p.lineManager === id).map((p) => ({
      staffUserId: p.id, displayName: `Person ${p.id}`, compId: p.comp, edge: 'L' as const, status: p.status,
      hasStaff: DEMO_ACCESS_PEOPLE.some((x) => x.lineManager === p.id),
    }))
    return delay({ staff, directTotal: staff.length, allTotal: staff.length })
  }

  accessHistory(id: number, kind?: string): Promise<AccessHistory> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    if (!p) return Promise.reject(new ApiError({ kind: 'not-found' }))
    type Row = AccessHistory['history'][number]
    const all: (Row | null)[] = [
      p.decided ? { occurredUtc: p.decided, kind: 'APPROVAL', what: p.approval === 'WITHDRAWN' ? 'WITHDRAWN' : 'APPROVED',
        detail: p.approval === 'WITHDRAWN' ? 'Approval taken back.' : `Approved. ${(p.warnings ?? []).length} warnings were showing.`,
        objectKey: null, beforeJson: null, afterJson: null, actorEmail: 'demo.user@redachem.com', reason: p.approval === 'WITHDRAWN' ? 'Moved to a role that should not see sales yet.' : null } : null,
      { occurredUtc: '2026-10-15T11:00:00Z', kind: 'MINDZER', what: 'ROLE_CHANGED', detail: 'Mindzer update: HR role changed.', objectKey: null, beforeJson: null, afterJson: null, actorEmail: 'Mindzer', reason: null },
      p.lastSignIn ? { occurredUtc: p.lastSignIn, kind: 'SIGN_IN', what: 'SIGNED_IN', detail: 'Signed in to MBI.', objectKey: null, beforeJson: null, afterJson: null, actorEmail: null as string | null, reason: null } : null,
      { occurredUtc: '2026-10-15T06:00:00Z', kind: 'MINDZER', what: 'ARRIVED', detail: 'First seen in Mindzer.', objectKey: null, beforeJson: null, afterJson: null, actorEmail: 'Mindzer', reason: null },
    ]
    const rows = all.filter((r): r is Row => r !== null && (!kind || r.kind === kind))
      .sort((a, b) => b.occurredUtc.localeCompare(a.occurredUtc))
    return delay({ history: rows })
  }

  accessOrgCodes(company?: string): Promise<AccessOrgCodes> {
    if (!company) return delay({ codes: Object.keys(DEMO_ORG).map((c) => ({ kind: 'COMPANY' as const, compId: c, unitCode: null, teamCode: null, rowsRecent: 1000, mindzerName: { R002: 'Redachem Gulf', R015: 'Redachem Asia', R070: 'Redachem Europe' }[c] ?? null })) })
    const org = DEMO_ORG[company]
    if (!org) return delay({ codes: [] })
    return delay({ codes: [
      ...org.units.map(([u, name]) => ({ kind: 'UNIT' as const, compId: company, unitCode: u, teamCode: null, rowsRecent: 500, mindzerName: name })),
      ...org.teams.map(([tm, u, name]) => ({ kind: 'TEAM' as const, compId: company, unitCode: u, teamCode: tm, rowsRecent: 200, mindzerName: name })),
    ] })
  }

  accessPersonSettingsPreview(id: number, body: AccessPersonSettingsBody): Promise<AccessPersonEffect> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    if (!p) return Promise.reject(new ApiError({ kind: 'not-found' }))
    const stray = demoPersonStray(p, body)
    if (stray) return Promise.reject(new ApiError({ kind: 'error', title: stray }))
    const on = Object.values(body.reports).filter((v) => v === 'on').length
    const off = Object.values(body.reports).filter((v) => v === 'off').length
    return delay({ outcome: 'PREVIEW', summary: demoPersonSummary(p, 'PREVIEW', { reports: on, lost: off, widens: on > 0 || body.lines.some((l) => l.effect === 'add') }) })
  }

  accessPersonSettingsSave(id: number, body: AccessPersonSettingsBody): Promise<AccessPersonEffect> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    if (!p) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (!body.reason?.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    const stray = demoPersonStray(p, body)
    if (stray) return Promise.reject(new ApiError({ kind: 'error', title: stray }))
    const own: { reports: Record<string, 'A' | 'R'>; groups: Record<number, 'A' | 'R'> } = { reports: {}, groups: {} }
    for (const [k, v] of Object.entries(body.reports)) if (v !== 'role') own.reports[k] = v === 'on' ? 'A' : 'R'
    for (const [k, v] of Object.entries(body.columnGroups)) if (v !== 'role') own.groups[Number(k)] = v === 'on' ? 'A' : 'R'
    p.own = own
    // The lines as sent: one kept as it was keeps its history; a new one is stamped now.
    const was = DEMO_SCOPE_EDITS[id] ?? []
    const now = new Date().toISOString()
    // A new line's ID is past every one there was: never a key twice (review of 18d).
    let next = Math.max(900, ...was.map((e) => e.editId))
    DEMO_SCOPE_EDITS[id] = body.lines.map((l) => {
      const effect = l.effect === 'add' ? 'A' as const : 'R' as const
      const compId = l.company ?? p.comp
      const same = was.find((e) => e.effect === effect && e.compId === compId && e.businessUnit === l.unit && e.businessTeam === l.team)
      return same ? { ...same, pausedUtc: l.resume ? null : same.pausedUtc, pausedReason: l.resume ? null : same.pausedReason }
        : { editId: ++next, effect, compId, businessUnit: l.unit, anyUnit: l.unit === null, businessTeam: l.team, anyTeam: l.team === null,
            inMainCompany: compId === p.comp, pausedUtc: null, pausedReason: null, changedByEmail: 'demo.user@redachem.com',
            changeReason: body.reason ?? null, changedUtc: now }
    })
    return delay({ outcome: 'SAVED', summary: demoPersonSummary(p, 'SAVED', { reports: 0, lost: 0, widens: false }) })
  }

  accessExtraScopePreview(id: number, body: AccessExtraScopeBody): Promise<AccessPersonEffect> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    if (!p) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (!body.remove && body.company === p.comp)
      return Promise.reject(new ApiError({ kind: 'error', title: `${p.comp} is their main company. Use Personal settings to add units or teams there.` }))
    const stray = body.remove ? null : demoStrayGroup(body.columnGroups, body.reports, 'this additional scope does not give')
    if (stray) return Promise.reject(new ApiError({ kind: 'error', title: stray }))
    return delay({ outcome: 'PREVIEW', summary: demoPersonSummary(p, 'PREVIEW', { reports: body.remove ? 0 : body.reports.length, lost: body.remove ? 1 : 0, widens: !body.remove && body.reports.length > 0 }) })
  }

  accessExtraScopeAdd(id: number, body: AccessExtraScopeBody): Promise<AccessPersonEffect> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    if (!p) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (!body.reason?.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    const sid = 40 + Math.floor(Math.random() * 1000)
    const stray = demoStrayGroup(body.columnGroups, body.reports, 'this additional scope does not give')
    if (stray) return Promise.reject(new ApiError({ kind: 'error', title: stray }))
    p.extras = [...(p.extras ?? []), { id: sid, comp: body.company ?? '', reports: body.reports, groups: body.columnGroups, lines: body.lines }]
    return delay({ outcome: 'SAVED', savedId: sid, summary: demoPersonSummary(p, 'SAVED', { reports: 0, lost: 0, widens: false }) })
  }

  accessExtraScopeSave(id: number, scopeId: number, body: AccessExtraScopeBody): Promise<AccessPersonEffect> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    // Any line of the scope names it, as 052's writer takes it.
    const x = p?.extras?.find((e) => e.id === scopeId || e.id === Math.floor(scopeId / 1000))
    if (!p || !x) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (!body.reason?.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    const stray = demoStrayGroup(body.columnGroups, body.reports, 'this additional scope does not give')
    if (stray) return Promise.reject(new ApiError({ kind: 'error', title: stray }))
    x.reports = body.reports
    x.groups = body.columnGroups
    if (body.lines) x.lines = body.lines
    return delay({ outcome: 'SAVED', summary: demoPersonSummary(p, 'SAVED', { reports: 0, lost: 0, widens: false }) })
  }

  accessExtraScopeRemove(id: number, scopeId: number, _expectedVersion: string | null, reason: string): Promise<AccessPersonEffect> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    const scope = p?.extras?.find((e) => e.id === scopeId || e.id === Math.floor(scopeId / 1000))
    if (!p || !scope) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (!reason.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    p.extras = (p.extras ?? []).filter((e) => e !== scope)
    return delay({ outcome: 'REMOVED', summary: demoPersonSummary(p, 'REMOVED', { reports: 0, lost: 1, widens: false }) })
  }

  accessSignInLink(id: number, body: { signInEmail?: string; unlink: boolean; reason: string; preview: boolean }): Promise<AccessSignInLinkEffect> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    if (!p) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (!body.reason.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    if (!body.unlink && !body.signInEmail?.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'No such sign-in: nobody has signed in to MBI with that email.' }))
    const outcome = body.preview ? 'PREVIEW' : body.unlink ? 'UNLINKED' : 'LINKED'
    if (!body.preview) p.lastSignIn = body.unlink ? null : (p.lastSignIn ?? new Date().toISOString())
    if (!body.preview) p.status = demoStatusOf(p)
    return delay({ outcome, summary: { Outcome: outcome, Approved: p.approval === 'APPROVED', Can_Enter_After: !body.unlink && p.approval === 'APPROVED' } })
  }

  /** D51 in the demo: everybody in Mindzer has an account to find, except leavers and placeholders. */
  accessFindAccount(id: number): Promise<{ outcome: string; objectId: string }> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    if (!p) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (p.status === 'LEFT' || p.status === 'PLACEHOLDER')
      return Promise.reject(new ApiError({ kind: 'error', title: `No Microsoft account in this company has the address person${p.id}@redachem.com.`, reason: 'NOT_FOUND' }))
    const outcome = p.lastSignIn !== null || p.found ? 'ALREADY_LINKED' : 'LINKED'
    p.found = true
    return delay({ outcome, objectId: DEMO_OID_PREFIX + String(p.id) })
  }

  accessApprovals(filter: AccessApprovalFilter): Promise<AccessApprovals> {
    const waiting = DEMO_ACCESS_PEOPLE.filter(demoWaiting)
    const tried = waiting.filter((p) => p.lastSignIn !== null)
    const setUp = filter.roleSetUp ?? 'yes'
    const like = (filter.search ?? '').trim().toLowerCase()
    const rows = (filter.view === 'tried' ? tried : waiting)
      .map(demoApprovalRow)
      .filter((r) =>
        (setUp === 'any' || r.roleSetUp === (setUp === 'yes'))
        && (!filter.company || r.compId === filter.company)
        && (filter.role === undefined || r.roleId === filter.role)
        && (!like || (r.displayName ?? '').toLowerCase().includes(like) || (r.email ?? '').toLowerCase().includes(like))
        && (!filter.problem || (filter.problem === 'NONE' ? r.warningCount === 0 : r.worstSeverity === filter.problem)))
      .sort((a, b) => (b.lastSignInUtc ?? '').localeCompare(a.lastSignInUtc ?? '') || a.mindzerUserId - b.mindzerUserId)
    // A page at a time, as the procedure answers (owner, 27 Oct 2026).
    const top = filter.top ?? 500
    const page = Math.max(1, filter.page ?? 1)
    return delay({ people: rows.slice((page - 1) * top, page * top), tried: tried.length, waiting: waiting.length, listed: rows.length })
  }

  accessApprovalCard(id: number): Promise<AccessApprovalCard> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    if (!p) return Promise.reject(new ApiError({ kind: 'not-found' }))
    return delay(demoApprovalCard(p))
  }

  accessApprove(id: number, warningsKey: string): Promise<AccessDecision> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    if (!p) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (warningsKey !== demoWarningsKey(p))
      return Promise.reject(new ApiError({ kind: 'error', title: 'The warnings changed while you were looking. Review them again.', reason: 'WARNINGS_CHANGED' }))
    if (p.approval === 'APPROVED' && !p.frozen) return Promise.reject(new ApiError({ kind: 'error', title: 'Already approved.' }))
    p.approval = 'APPROVED'
    p.frozen = false
    p.decided = new Date().toISOString()
    p.status = demoStatusOf(p)
    return delay({ outcome: 'APPROVED', built: true })
  }

  accessWithdraw(id: number, reason: string): Promise<AccessDecision> {
    const p = DEMO_ACCESS_PEOPLE.find((x) => x.id === id)
    if (!p) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (!reason.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    if (p.approval !== 'APPROVED') return Promise.reject(new ApiError({ kind: 'error', title: 'Not approved, so there is nothing to withdraw.' }))
    p.approval = 'WITHDRAWN'
    p.decided = new Date().toISOString()
    p.status = demoStatusOf(p)
    return delay({ outcome: 'WITHDRAWN', built: true })
  }

  /* Column definition under the new model (060, D53). */
  columnDefinition(reportSlug: string): Promise<ColumnDefinition> {
    const groups = DEMO_COLUMN_GROUPS.filter((g) => g.report === reportSlug)
    const columns = DEMO_V2_COLUMNS.filter((c) => c.report === reportSlug)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.columnName.localeCompare(b.columnName))
      .map((c) => {
        const gs = c.groups.map((g) => DEMO_COLUMN_GROUPS.find((x) => x.id === g)).filter((g): g is DemoColumnGroup => !!g)
          .sort((a, b) => a.name.localeCompare(b.name))
        return {
          reportColumnId: c.id, columnName: c.columnName, displayName: c.displayName, isRequired: c.isRequired, sortOrder: c.sortOrder,
          visibility: (c.isRequired ? 'REQUIRED' : c.visibility) as ColumnVisibility,
          groupIds: c.visibility === 'GROUPS' && gs.length ? gs.map((g) => g.id).join(', ') : null,
          groupNames: c.visibility === 'GROUPS' && gs.length ? gs.map((g) => g.name).join(', ') : null,
          peopleSeeing: demoColumnPeople(reportSlug, c.isRequired ? 'EVERYONE' : c.visibility, c.groups),
          version: c.isRequired ? null : c.version,
        }
      })
    return delay({
      columns,
      groups: groups.slice().sort((a, b) => a.name.localeCompare(b.name)).map((g) => demoGroupRow(g)),
      columnCount: columns.length, hiddenCount: columns.filter((c) => c.visibility === 'HIDDEN').length,
      groupCount: groups.length, outcome: 'ALLOWED',
    })
  }

  columnSettingPreview(reportColumnId: number, body: ColumnSettingBody): Promise<ColumnSettingEffect> {
    return this.#columnSetting(reportColumnId, body, true)
  }

  columnSettingSave(reportColumnId: number, body: ColumnSettingBody): Promise<ColumnSettingEffect> {
    return this.#columnSetting(reportColumnId, body, false)
  }

  #columnSetting(reportColumnId: number, body: ColumnSettingBody, dryRun: boolean): Promise<ColumnSettingEffect> {
    const c = DEMO_V2_COLUMNS.find((x) => x.id === reportColumnId)
    if (!c) return Promise.reject(new ApiError({ kind: 'error', title: 'No such report column.' }))
    if (c.isRequired)
      return Promise.reject(new ApiError({ kind: 'error', title: 'This column is required by the report and is always shown. Hiding a grouping column breaks every page.' }))
    if (body.groupIds.some((g) => DEMO_COLUMN_GROUPS.find((x) => x.id === g)?.report !== c.report))
      return Promise.reject(new ApiError({ kind: 'error', title: 'A column can be gated only by column groups of its own report, each named once.' }))
    if (body.visibility === 'GROUPS' && body.groupIds.length === 0)
      return Promise.reject(new ApiError({ kind: 'error', title: 'Choose at least one column group, or set the column to No one or Everyone.' }))
    if (!dryRun && !body.reason?.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    if (!dryRun && (body.expectedVersion ?? null) !== (c.version ?? null))
      return Promise.reject(new ApiError({ kind: 'error', title: 'Someone else changed this column since you opened it. Reload to see their change.', reason: 'CHANGED_SINCE_READ' }))
    const names = (ids: number[]) => ids.map((g) => DEMO_COLUMN_GROUPS.find((x) => x.id === g)?.name ?? String(g)).sort().join(', ') || null
    const groupsAfter = body.visibility === 'GROUPS' ? body.groupIds : []
    const summary = {
      Outcome: dryRun ? 'PREVIEW' : 'CHANGED', Column_Name: c.columnName,
      Visibility_Before: c.visibility, Visibility_After: body.visibility,
      Groups_Before: c.visibility === 'GROUPS' ? names(c.groups) : null, Groups_After: body.visibility === 'GROUPS' ? names(groupsAfter) : null,
      People_Before: demoColumnPeople(c.report, c.visibility, c.groups), People_After: demoColumnPeople(c.report, body.visibility, groupsAfter),
      Version: c.version,
    }
    if (!dryRun) {
      c.visibility = body.visibility
      c.groups = [...groupsAfter]
      c.version = body.visibility === 'HIDDEN' ? null : new Date().toISOString()
      summary.Version = c.version
    }
    return delay({ outcome: summary.Outcome, summary })
  }

  columnGroup(groupId: number): Promise<ColumnGroupPage> {
    const g = DEMO_COLUMN_GROUPS.find((x) => x.id === groupId)
    if (!g) return Promise.reject(new ApiError({ kind: 'not-found' }))
    return delay({
      group: { groupId: g.id, reportSlug: g.report, reportName: demoReportName(g.report), groupName: g.name, description: g.description,
        version: g.version, changedByEmail: 'demo.user@redachem.com', changeReason: 'Set up with the managers, 28 Oct 2026.' },
      columns: DEMO_V2_COLUMNS.filter((c) => c.visibility === 'GROUPS' && c.groups.includes(g.id))
        .map((c) => ({ reportColumnId: c.id, columnName: c.columnName, displayName: c.displayName })),
      roles: DEMO_HR_ROLES.filter((r) => r.groups.includes(g.id)).map((r) => ({ roleId: r.id, roleName: r.name, roleFunction: r.fn })),
      people: DEMO_ACCESS_PEOPLE.filter((p) => p.own?.groups?.[g.id]).map((p) => ({ mindzerUserId: p.id, displayName: `Person ${p.id}`, effect: p.own!.groups![g.id] })),
      scopes: DEMO_ACCESS_PEOPLE.flatMap((p) => (p.extras ?? []).filter((x) => x.groups.includes(g.id)).map((x) => ({
        extraScopeId: x.id, mindzerUserId: p.id, displayName: `Person ${p.id}`, compId: x.comp, businessUnit: null, businessTeam: null }))),
    })
  }

  columnGroupCreate(body: ColumnGroupBody): Promise<ColumnGroupEffect> {
    const report = body.reportSlug ?? ''
    const name = body.name.trim()
    if (!DEMO_ACCESS_REPORTS.some((r) => r.slug === report)) return Promise.reject(new ApiError({ kind: 'error', title: 'No such report.' }))
    if (!name) return Promise.reject(new ApiError({ kind: 'error', title: 'A column group needs a name.' }))
    if (!body.reason.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    if (DEMO_COLUMN_GROUPS.some((g) => g.report === report && g.name.toLowerCase() === name.toLowerCase()))
      return Promise.reject(new ApiError({ kind: 'error', title: `This report already has a column group called "${name}".` }))
    const g: DemoColumnGroup = { id: demoNextGroupId++, report, name, description: body.description?.trim() || null, version: new Date().toISOString() }
    DEMO_COLUMN_GROUPS.push(g)
    return delay({ outcome: 'CREATED', savedId: g.id, summary: { Outcome: 'CREATED', Group_Id: g.id, Report_Slug: report, Version: g.version } })
  }

  columnGroupSave(groupId: number, body: ColumnGroupBody): Promise<ColumnGroupEffect> {
    const g = DEMO_COLUMN_GROUPS.find((x) => x.id === groupId)
    if (!g) return Promise.reject(new ApiError({ kind: 'not-found' }))
    const name = body.name.trim()
    if (!name) return Promise.reject(new ApiError({ kind: 'error', title: 'A column group needs a name.' }))
    if (!body.reason.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    if ((body.expectedVersion ?? null) !== g.version)
      return Promise.reject(new ApiError({ kind: 'error', title: 'Someone else changed this column group since you opened it. Reload to see their change.', reason: 'CHANGED_SINCE_READ' }))
    if (DEMO_COLUMN_GROUPS.some((x) => x.id !== g.id && x.report === g.report && x.name.toLowerCase() === name.toLowerCase()))
      return Promise.reject(new ApiError({ kind: 'error', title: `This report already has a column group called "${name}".` }))
    const description = body.description?.trim() || null
    if (name === g.name && description === g.description)
      return delay({ outcome: 'UNCHANGED', savedId: g.id, summary: { Outcome: 'UNCHANGED', Group_Id: g.id, Report_Slug: g.report, Version: g.version } })
    g.name = name
    g.description = description
    g.version = new Date().toISOString()
    return delay({ outcome: 'SAVED', savedId: g.id, summary: { Outcome: 'SAVED', Group_Id: g.id, Report_Slug: g.report, Version: g.version } })
  }

  columnGroupDelete(groupId: number, expectedVersion: string, reason: string): Promise<ColumnGroupEffect> {
    const g = DEMO_COLUMN_GROUPS.find((x) => x.id === groupId)
    if (!g) return Promise.reject(new ApiError({ kind: 'not-found' }))
    if (!reason.trim()) return Promise.reject(new ApiError({ kind: 'error', title: 'A reason is required.' }))
    if (expectedVersion !== g.version)
      return Promise.reject(new ApiError({ kind: 'error', title: 'Someone else changed this column group since you opened it. Reload to see their change.', reason: 'CHANGED_SINCE_READ' }))
    const row = demoGroupRow(g)
    if (row.columnsN + row.rolesN + row.personSettingsN + row.extraScopesN > 0)
      return Promise.reject(new ApiError({ kind: 'error', reason: 'COLUMN_GROUP_IN_USE',
        title: `"${g.name}" is still used: ${row.columnsN} column(s), ${row.rolesN} HR role(s), ${row.personSettingsN} personal setting(s) and ${row.extraScopesN} additional scope(s). Take it off those first.` }))
    DEMO_COLUMN_GROUPS.splice(DEMO_COLUMN_GROUPS.indexOf(g), 1)
    return delay({ outcome: 'DELETED', summary: { Outcome: 'DELETED', Group_Id: g.id, Report_Slug: g.report, Group_Name: g.name } })
  }

  columnGovernance(reportSlug: string): Promise<ColumnGovernance> {
    // A role-scoped report registers no columns, so it has no mask to show.
    return delay(
      reportSlug === 'receivables'
        ? { columns: [...demoColumns], catalogue: DEMO_CATALOGUE }
        : { columns: [], catalogue: DEMO_CATALOGUE },
    )
  }

  setColumnPermissions(body: SetColumnPermissionsRequest): Promise<ColumnPermissionChange> {
    const col = demoColumns.find((c) => c.reportColumnId === body.reportColumnId)
    if (!col) return Promise.reject(new ApiError({ kind: 'error', title: 'No such report column.' }))

    // Reproduces the database's refusal (51122), because it is the rule most
    // likely to be met: hiding a grouping column does not restrict data, it
    // breaks every page.
    if (col.isRequired) {
      return Promise.reject(new ApiError({
        kind: 'error',
        title: 'This column is required by the report and cannot be restricted. Hiding a grouping column breaks every page.',
      }))
    }

    const holders = (codes: string[]) =>
      // Approximate: the real count is DISTINCT people across the codes, and
      // this cannot know the overlap. Enough to make the screen behave.
      codes.length === 0
        ? 0
        : Math.max(...codes.map((c) => DEMO_CATALOGUE.find((p) => p.permissionCode === c)?.peopleHolding ?? 0))

    const before = col.permissions ? col.permissions.split(', ') : []
    const peopleBefore = holders(before)
    const peopleAfter = holders(body.permissions)

    col.permissions = body.permissions.length ? body.permissions.join(', ') : null
    col.status = body.permissions.length ? 'mapped' : 'UNMAPPED - hidden from everyone'
    demoColumns = [...demoColumns]

    return delay({
      columnName: col.columnName,
      before: before.length ? before.join(', ') : '(nobody)',
      after: body.permissions.length ? body.permissions.join(', ') : '(nobody)',
      peopleBefore,
      peopleAfter,
      change: peopleAfter - peopleBefore,
    })
  }

  roleHolders(): Promise<RoleHolder[]> {
    // ?model=v2: the new model's names, and its System Master Admin.
    if (!demoV2()) return delay([...demoRoles])
    return delay([DEMO_MASTER_ADMIN, ...demoRoles.map((r) => (r.roleCode === 'MasterViewer' ? { ...r, roleName: 'System Master Reader' } : r))])
  }

  setPlatformRole(body: SetRoleRequest): Promise<{ outcome: string }> {
    const meta = DEMO_ROLE_META[body.roleCode]

    // ?model=v2: 017's rules under the new model, in its words. The demo user is a
    // System Admin, not the System Master Admin.
    if (demoV2()) {
      const refuse = (title: string) => Promise.reject(new ApiError({ kind: 'error', title }))
      if (body.roleCode === 'SystemMasterAdmin') return refuse('System Master Admin is given and removed only by a reviewed database script, never on this screen.')
      if (body.roleCode === 'SystemAdmin') return refuse('Only the System Master Admin may give or remove System Admin.')
      const who = body.email.toLowerCase()
      if (who === 'demo.user@redachem.com') return refuse('You cannot change your own system role. Ask another administrator.')
      if (!body.grant && !body.reason?.trim()) return refuse('A reason is required when removing a system role.')
      if (body.grant && [DEMO_MASTER_ADMIN, ...demoRoles].some((r) => r.email.toLowerCase() === who && r.roleCode !== body.roleCode)) {
        return refuse('This person already holds another system role. A person holds one: remove that one first.')
      }
    }

    if (body.grant) {
      if (meta.seesAllData && !body.reason?.trim()) {
        return Promise.reject(new ApiError({
          kind: 'error',
          title: 'A reason is required when granting a role that sees all data.',
        }))
      }
      if (!demoRoles.some((r) => r.email === body.email && r.roleCode === body.roleCode)) {
        demoRoles.push({
          email: body.email,
          displayName: DEMO_USERS.find((u) => u.email === body.email)?.displayName ?? null,
          roleCode: body.roleCode,
          roleName: meta.roleName,
          seesAllData: meta.seesAllData,
          grantedBy: 'demo.user@redachem.com',
          grantedUtc: new Date().toISOString(),
          reason: body.reason ?? null,
        })
      }
      return delay({ outcome: 'GRANTED' })
    }

    if (body.roleCode === 'SystemAdmin') {
      const admins = demoRoles.filter((r) => r.roleCode === 'SystemAdmin').length
      if (admins <= 1) {
        return Promise.reject(new ApiError({
          kind: 'error',
          title: 'That is the last System Admin. Appoint another before removing this one.',
        }))
      }
      if (body.email === 'demo.user@redachem.com') {
        return Promise.reject(new ApiError({
          kind: 'error',
          title: 'You cannot remove your own System Admin role. Ask another System Admin.',
        }))
      }
    }

    demoRoles = demoRoles.filter((r) => !(r.email === body.email && r.roleCode === body.roleCode))
    return delay({ outcome: 'REVOKED' })
  }

  /**
   * The News feed.
   *
   * Filtered here by the demo user's own report list, mirroring what
   * plat.fn_Openable_Reports_MBI does in the real read. That matters for a demo: a
   * reviewer switching View As to a salesperson should watch the Sales card
   * disappear, because "the feed is scoped" is one of the things this page is
   * for. A demo that showed every card would teach the opposite.
   */
  news(includePast?: boolean, viewAs?: string): Promise<NewsItem[]> {
    const now = Date.now()

    // View As means BE them. Reproduced here rather than ignored because the
    // demo is what a reviewer is shown, and a demo where borrowing an identity
    // narrows the sidebar but not the front page teaches exactly the wrong
    // thing about the feature. Same rule as the real procedure: the target's
    // access list, the target's bypass, the target's role for past updates.
    const seesAll = viewAs
      ? demoRoles.some((r) => r.email === viewAs && r.seesAllData)
      : DEMO_ME.seesAllData
    const onList = viewAs ? demoAccess.some((a) => a.email === viewAs) : true
    const reports = viewAs
      ? seesAll || onList
        ? ['receivables', 'sales']
        : []
      : DEMO_ME.reports
    const isAdmin = viewAs
      ? demoRoles.some((r) => r.email === viewAs && r.roleCode === 'SystemAdmin')
      : DEMO_ME.roles.includes('SystemAdmin')

    const visible = demoNews
      .filter((n) => reports.includes(n.reportSlug))
      .filter((n) =>
        includePast && isAdmin
          ? true
          : !n.isRetired && new Date(n.expiresUtc).getTime() > now,
      )
      .map((n) => ({ ...n, isExpired: new Date(n.expiresUtc).getTime() <= now }))
      .sort((a, b) => b.publishedUtc.localeCompare(a.publishedUtc))
    return delay(visible)
  }

  publishNews(body: PublishNewsRequest): Promise<{ newsId: number }> {
    const newsId = Math.max(0, ...demoNews.map((n) => n.newsId)) + 1
    const published = new Date()
    const expires = new Date(published.getTime() + (body.durationDays ?? 30) * 86_400_000)
    demoNews = [
      {
        newsId,
        reportSlug: body.reportSlug,
        reportDisplayName: demoReportName(body.reportSlug),
        headline: body.headline,
        summary: body.summary,
        details: body.details ?? null,
        publishedUtc: published.toISOString(),
        expiresUtc: expires.toISOString(),
        isRetired: false,
        isExpired: false,
      },
      ...demoNews,
    ]
    return delay({ newsId })
  }

  updateNews(newsId: number, body: UpdateNewsRequest): Promise<{ newsId: number }> {
    // Deliberately does NOT touch publishedUtc or expiresUtc, exactly as
    // usp_Update_Report_News leaves them: a correction is not a re-publish.
    demoNews = demoNews.map((n) =>
      n.newsId === newsId
        ? {
            ...n,
            reportSlug: body.reportSlug,
            reportDisplayName: demoReportName(body.reportSlug),
            headline: body.headline,
            summary: body.summary,
            details: body.details ?? null,
          }
        : n,
    )
    return delay({ newsId })
  }

  retireNews(newsId: number): Promise<{ newsId: number; removed: boolean }> {
    // Soft, like the real one — the row stays so "show past updates" can find it.
    demoNews = demoNews.map((n) => (n.newsId === newsId ? { ...n, isRetired: true } : n))
    return delay({ newsId, removed: true })
  }

  maintenanceStatus(): Promise<MaintenanceStatus> {
    return delay(demoStatus())
  }

  setMaintenance(body: SetMaintenanceRequest): Promise<{ scope: string; expectedEndUtc: string }> {
    const expectedEndUtc = new Date(Date.now() + body.durationMinutes * 60_000).toISOString()
    // Upsert by scope, like usp_Set_Maintenance: re-setting the same scope
    // AMENDS its window rather than opening a second one (test M6).
    demoWindows = [
      ...demoWindows.filter((w) => w.scope !== body.scope),
      {
        scope: body.scope,
        message: body.message || 'The service is temporarily unavailable.',
        expectedEndUtc,
        allowReadGrace: body.allowReadGrace,
      },
    ]
    return delay({ scope: body.scope, expectedEndUtc })
  }

  clearMaintenance(scope: string): Promise<{ scope: string; windowsClosed: number }> {
    const before = demoWindows.length
    // Only the named scope. Clearing the platform window must not lift a
    // report's, which is what the real procedure does and what M9 asserts.
    demoWindows = demoWindows.filter((w) => w.scope !== scope)
    return delay({ scope, windowsClosed: before - demoWindows.length })
  }
}

/* ===========================================================================
   THE SALES DEMO FACTS - one small invented dataset every sales tab reads.

   Twelve rows, deterministic, fixture-flavoured. Yearly and MTD figures are
   plain columns exactly like the real table's pre-computed periods, so the
   demo exercises the same shape the procedures serve. Amounts are visibly
   invented; the companies are the fixture's.
   =========================================================================== */

interface DemoSalesFact {
  company: string
  companyName: string
  /** The selling company's country and the customer's, as the NAMES the
      procedure returns -- never codes. Derived below, not typed per row. */
  companyCountry: string
  customerCountry: string
  bu: string
  bt: string
  sp: string
  customer: string
  product: string
  itemNo: string
  supplier: string
  wkSales: number
  sales: number
  salesPY: number
  gp: number
  gpPY: number
  qty: number
  qtyPY: number
  mtdSales: number
  mtdSalesPY: number
  mtdGp: number
  mtdGpPY: number
  mtdQty: number
  mtdQtyPY: number
}

interface DemoSalesFilters {
  /** Multi-select, like the real filter bar: empty means every company. */
  company?: string[]
  bu?: string
  bt?: string
  sp?: string
  sup?: string[]
  reg?: string
  cust?: string
}

type DemoSalesPeriod = 'ytd' | 'mtd' | 'custom'

/**
 * The demo facts carry no invoice dates, so a custom range is approximated
 * from the SPAN the client asked for: a whole year reads as the yearly
 * figures, a month as the MTD figures, a week as roughly a quarter of the
 * month. The real report computes all of this from Invoice_Date per row.
 */
function demoPeriodMode(options: { period?: DemoSalesPeriod; from?: string; to?: string }): {
  mtd: boolean
  scale: number
} {
  if (options.period !== 'custom' || !options.from || !options.to)
    return { mtd: options.period === 'mtd', scale: 1 }
  const days = (Date.parse(options.to) - Date.parse(options.from)) / 86_400_000
  if (days > 45) return { mtd: false, scale: 1 }
  if (days > 10) return { mtd: true, scale: 1 }
  return { mtd: true, scale: 0.28 }
}

type DemoSalesKey =
  | 'companyName' | 'companyCountry' | 'customerCountry'
  | 'bu' | 'bt' | 'sp' | 'customer' | 'product' | 'itemNo' | 'supplier'

/** Which fact field each explorer/tree dimension groups on. Company groups the
    NAME, matching 021 (a code is not what a reader recognises). */
const DEMO_SALES_KEYS: Record<SalesDimension, DemoSalesKey> = {
  Company: 'companyName',
  BusinessUnit: 'bu',
  BusinessTeam: 'bt',
  Salesperson: 'sp',
  Customer: 'customer',
  Product: 'product',
  Supplier: 'supplier',
}

/** Which fact field each drill LEVEL reads (30 Sep 2026: levels 2 and 3 are the
    reader's choice, so the chain is built per request from this). */
const DEMO_LEVEL_KEY: Record<SalesDrillLevel, DemoSalesKey> = {
  Country: 'companyCountry', CustomerCountry: 'customerCountry', Company: 'companyName',
  BusinessUnit: 'bu', BusinessTeam: 'bt', Salesperson: 'sp', Customer: 'customer',
  Product: 'product', Supplier: 'supplier', Item: 'itemNo',
}

/* THE DEMO'S COUNTRIES, derived so they can never disagree with anything else
   the demo says. The company's follows its currency (r010 books in dirhams);
   the customer's follows demoRegion, the same answer the Customer Region filter
   and the invoice lines give, so filtering a region and grouping by country
   agree -- as they do in production wherever D365 recorded both. */
const DEMO_COMPANY_COUNTRY: Record<string, string> = { r002: 'Saudi Arabia', r010: 'United Arab Emirates' }
const DEMO_REGION_COUNTRY: Record<string, string> = {
  AE: 'United Arab Emirates', OM: 'Oman', BH: 'Bahrain', SA: 'Saudi Arabia',
}
/** The codes that also find each demo country by search, as plat.Country_Code_MBI does. */
const DEMO_COUNTRY_CODES: Record<string, string[]> = {
  'Saudi Arabia': ['SAU', 'SA', 'KSA'],
  'United Arab Emirates': ['ARE', 'AE', 'UAE'],
  Oman: ['OMN', 'OM'],
  Bahrain: ['BHR', 'BH'],
}

const DEMO_SALES_FACTS: DemoSalesFact[] = ([
  { company: 'r002', companyName: 'REDAChem KSA', bu: 'FI', bt: 'FI-BKR', sp: 'Atif Majeed', customer: 'MASDAR AL HAYAT', product: 'Hydrochloric Acid', itemNo: 'I-1101', supplier: 'SUP ALPHA', wkSales: 160_000, sales: 8_400_000, salesPY: 7_100_000, gp: 1_890_000, gpPY: 1_420_000, qty: 42_000, qtyPY: 38_000, mtdSales: 640_000, mtdSalesPY: 590_000, mtdGp: 148_000, mtdGpPY: 121_000, mtdQty: 3_100, mtdQtyPY: 2_900 },
  { company: 'r002', companyName: 'REDAChem KSA', bu: 'FI', bt: 'FI-BKR', sp: 'Atif Majeed', customer: 'KUDU FOOD', product: 'Citric Acid', itemNo: 'I-1102', supplier: 'SUP BETA', wkSales: 95_000, sales: 5_150_000, salesPY: 5_600_000, gp: 980_000, gpPY: 1_090_000, qty: 27_500, qtyPY: 29_000, mtdSales: 410_000, mtdSalesPY: 460_000, mtdGp: 76_000, mtdGpPY: 89_000, mtdQty: 2_050, mtdQtyPY: 2_300 },
  { company: 'r002', companyName: 'REDAChem KSA', bu: 'FI', bt: 'FI-OTHER', sp: 'Nour Habli', customer: 'AL RASHED', product: 'Citric Acid', itemNo: 'I-1102', supplier: 'SUP BETA', wkSales: 88_000, sales: 4_820_000, salesPY: 4_100_000, gp: 1_060_000, gpPY: 840_000, qty: 24_000, qtyPY: 21_500, mtdSales: 380_000, mtdSalesPY: 330_000, mtdGp: 85_000, mtdGpPY: 66_000, mtdQty: 1_900, mtdQtyPY: 1_700 },
  { company: 'r002', companyName: 'REDAChem KSA', bu: 'FI', bt: 'FI-OTHER', sp: 'Nour Habli', customer: 'SAUDI BAKERIES', product: 'Sorbitol', itemNo: 'I-1103', supplier: 'SUP GAMMA', wkSales: 60_000, sales: 3_240_000, salesPY: 2_950_000, gp: 585_000, gpPY: 512_000, qty: 16_000, qtyPY: 15_000, mtdSales: 255_000, mtdSalesPY: 240_000, mtdGp: 46_000, mtdGpPY: 41_000, mtdQty: 1_250, mtdQtyPY: 1_200 },
  { company: 'r002', companyName: 'REDAChem KSA', bu: 'LAB', bt: 'LAB-A', sp: 'Lab Person', customer: 'DELTA LABS', product: 'Lab Reagent', itemNo: 'I-2201', supplier: 'SUP GAMMA', wkSales: 130_000, sales: 6_900_000, salesPY: 5_200_000, gp: 2_760_000, gpPY: 2_010_000, qty: 9_800, qtyPY: 8_100, mtdSales: 540_000, mtdSalesPY: 420_000, mtdGp: 216_000, mtdGpPY: 162_000, mtdQty: 780, mtdQtyPY: 640 },
  { company: 'r002', companyName: 'REDAChem KSA', bu: 'LAB', bt: 'LAB-A', sp: 'Lab Person', customer: 'GULF UNIVERSITY', product: 'Titration Kit', itemNo: 'I-2202', supplier: 'SUP OMEGA', wkSales: 41_000, sales: 2_150_000, salesPY: 1_800_000, gp: 902_000, gpPY: 740_000, qty: 3_400, qtyPY: 2_900, mtdSales: 170_000, mtdSalesPY: 145_000, mtdGp: 71_000, mtdGpPY: 60_000, mtdQty: 270, mtdQtyPY: 230 },
  { company: 'r002', companyName: 'REDAChem KSA', bu: 'C1', bt: 'BT-CMRC', sp: 'Rana Aziz', customer: 'CRESCENT OILS', product: 'Caustic Soda', itemNo: 'I-1104', supplier: 'SUP ALPHA', wkSales: 140_000, sales: 7_300_000, salesPY: 8_100_000, gp: 1_460_000, gpPY: 1_700_000, qty: 51_000, qtyPY: 56_000, mtdSales: 580_000, mtdSalesPY: 660_000, mtdGp: 116_000, mtdGpPY: 139_000, mtdQty: 4_050, mtdQtyPY: 4_500 },
  { company: 'r002', companyName: 'REDAChem KSA', bu: 'C1', bt: 'BT-CMRC', sp: 'Rana Aziz', customer: 'BASRA FOODS', product: 'Phosphoric Acid', itemNo: 'I-1105', supplier: 'SUP BETA', wkSales: 54_000, sales: 2_890_000, salesPY: 2_400_000, gp: 490_000, gpPY: 415_000, qty: 15_500, qtyPY: 13_000, mtdSales: 230_000, mtdSalesPY: 195_000, mtdGp: 39_000, mtdGpPY: 34_000, mtdQty: 1_240, mtdQtyPY: 1_050 },
  { company: 'r010', companyName: 'REDA Food', bu: 'FI', bt: 'FI-BKR', sp: 'Food Person', customer: 'SAUDI BAKERIES', product: 'Sorbitol', itemNo: 'I-1103', supplier: 'SUP GAMMA', wkSales: 82_000, sales: 4_450_000, salesPY: 3_900_000, gp: 845_000, gpPY: 720_000, qty: 22_000, qtyPY: 19_800, mtdSales: 350_000, mtdSalesPY: 315_000, mtdGp: 66_000, mtdGpPY: 58_000, mtdQty: 1_750, mtdQtyPY: 1_600 },
  { company: 'r010', companyName: 'REDA Food', bu: 'FI', bt: 'FI-BKR', sp: 'Food Person', customer: 'KUDU FOOD', product: 'Glucose Syrup', itemNo: 'I-3301', supplier: 'SUP OMEGA', wkSales: 70_000, sales: 3_780_000, salesPY: 3_500_000, gp: 640_000, gpPY: 590_000, qty: 19_000, qtyPY: 17_800, mtdSales: 300_000, mtdSalesPY: 285_000, mtdGp: 51_000, mtdGpPY: 48_000, mtdQty: 1_500, mtdQtyPY: 1_450 },
  { company: 'r010', companyName: 'REDA Food', bu: 'C1', bt: 'BT-DRI', sp: 'Rana Aziz', customer: 'MASDAR AL HAYAT', product: 'Glucose Syrup', itemNo: 'I-3301', supplier: 'SUP OMEGA', wkSales: 36_000, sales: 1_950_000, salesPY: 2_200_000, gp: 330_000, gpPY: 390_000, qty: 9_700, qtyPY: 11_000, mtdSales: 155_000, mtdSalesPY: 180_000, mtdGp: 26_000, mtdGpPY: 32_000, mtdQty: 780, mtdQtyPY: 900 },
  { company: 'r010', companyName: 'REDA Food', bu: 'LAB', bt: 'LAB-A', sp: 'Lab Person', customer: 'DELTA LABS', product: 'Titration Kit', itemNo: 'I-2202', supplier: 'SUP OMEGA', wkSales: 21_000, sales: 1_120_000, salesPY: 950_000, gp: 470_000, gpPY: 395_000, qty: 1_800, qtyPY: 1_500, mtdSales: 88_000, mtdSalesPY: 77_000, mtdGp: 37_000, mtdGpPY: 32_000, mtdQty: 140, mtdQtyPY: 120 },
] as Omit<DemoSalesFact, 'companyCountry' | 'customerCountry'>[]).map((f) => ({
  ...f,
  companyCountry: DEMO_COMPANY_COUNTRY[f.company] ?? '(not recorded)',
  customerCountry: DEMO_REGION_COUNTRY[demoRegion(f.customer)] ?? '(not recorded)',
}))

/**
 * The standing filters plus view-as narrowing. Borrowing an identity narrows
 * to one team - the demo must never show impersonation widening anything.
 */
/**
 * One customer sits in ONE region.
 *
 * The invoice lines used to seed a region per LINE, which quietly put a single
 * customer in three countries at once. That was invisible while the region was
 * only a column; it stopped being invisible on 10 Sep 2026, when the region
 * became a filter and the KPI strip would have counted a customer the ledger
 * had just filtered away. Derived from the customer's name so both surfaces
 * reach the same answer without carrying the field on every fact.
 */
function demoRegion(customer: string): string {
  const R = ['AE', 'OM', 'BH', 'SA']
  return R[Math.floor(seeded(`${customer}/region`) * R.length)]
}

/* The functional currency is the COMPANY's — one apiece, as in production,
   where 22 codes span 43 companies. The unit is the ITEM's: liquids move in
   tonnes, the lab items in pieces, so a selection that crosses the two really
   is adding unlike things and the strip has to say so. */
const DEMO_SALES_CURRENCY: Record<string, string> = { r002: 'SAR', r010: 'AED' }
const DEMO_SALES_UOM: Record<string, string> = {
  'I-1101': 'MT', 'I-1102': 'MT', 'I-1103': 'MT', 'I-1104': 'MT', 'I-1105': 'MT',
  'I-2201': 'ea', 'I-2202': 'ea', 'I-3301': 'MT',
}

/**
 * The code and unit for ONE GROUP of fact rows, the way the procedures compute
 * it per row: a value only when the group holds exactly one, null when it holds
 * several. This is the table contract — a grid row, a board member, a tree bar.
 */
function demoGroupDims(rows: DemoSalesFact[], fx: ReturnType<typeof demoFx>) {
  const units = new Set(rows.map((r) => DEMO_SALES_UOM[r.itemNo]).filter(Boolean))
  const currencies = new Set(rows.map((r) => DEMO_SALES_CURRENCY[r.company]).filter(Boolean))
  return {
    /* Converted, every figure is ONE unit, so there is nothing to mix — and the
       code is the one that was converted TO, never 'USD' regardless. That
       literal is the exact defect the served-currency gate exists to catch, and
       the demo has to show the same behaviour as the procedure or it stops
       being a place the behaviour can be checked. */
    currencyCode: fx.code ?? (currencies.size === 1 ? [...currencies][0]! : null),
    unit: units.size === 1 ? [...units][0]! : null,
    // 021's UnitMixed: this group adds several units together.
    unitMixed: units.size > 1,
  }
}

function demoSalesRows(options: DemoSalesFilters & { viewAs?: string }): DemoSalesFact[] {
  let rows = DEMO_SALES_FACTS
  if (options.viewAs) rows = rows.filter((r) => r.bt === 'FI-BKR' && r.company === 'r002')
  if (options.company?.length) rows = rows.filter((r) => options.company!.includes(r.company))
  if (options.bu) rows = rows.filter((r) => r.bu === options.bu)
  if (options.bt) rows = rows.filter((r) => r.bt === options.bt)
  if (options.sp) rows = rows.filter((r) => r.sp === options.sp)
  // The standing bar's three, honoured here so the demo narrows exactly the
  // way the real report does — supplier and region exact, customer substring.
  if (options.sup?.length) rows = rows.filter((r) => options.sup!.includes(r.supplier ?? ''))
  if (options.reg) rows = rows.filter((r) => demoRegion(r.customer) === options.reg)
  if (options.cust) {
    const needle = options.cust.toLowerCase()
    rows = rows.filter((r) => r.customer.toLowerCase().includes(needle))
  }
  return rows
}

/**
 * Explodes each demo fact into a few posted lines. Deterministic (seeded off
 * the fact's own identity), and the amounts divide the fact's totals so the
 * ledger's sums still agree with every other demo surface. One line per fact
 * is deliberately a Return with negative money, and one carries zero quantity,
 * so the page's two special cases are visible without inventing a story.
 */
function demoSalesLines(facts: DemoSalesFact[], scale: number): SalesLine[] {
  const UOM = ['kg', 'l', 'ea', 'drum']
  const LOC = ['DIC', 'DROPSHIP', 'DIC-JAFZA']
  const DISTRICT = ['LABS', 'TERRITORY 3', 'TERRITORY 5', 'ADHESIVE']
  const out: SalesLine[] = []
  facts.forEach((f, fi) => {
    const n = 5
    for (let i = 0; i < n; i++) {
      const seed = `${f.company}/${f.customer}/${f.itemNo}/${i}`
      const isReturn = i === 3
      const zeroQty = i === 4
      // The five lines divide the fact, so the ledger foots to the report.
      const share = (isReturn ? -0.08 : 0.27) * scale
      const sales = zeroQty ? 0 : Math.round(f.sales * share)
      const gp = zeroQty ? 0 : Math.round(f.gp * share)
      const charges = zeroQty ? 0 : Math.round(Math.abs(sales) * 0.004)
      const day = 1 + Math.floor(seeded(seed + 'd') * 27)
      const month = 1 + Math.floor(seeded(seed + 'm') * 8)
      out.push({
        companyCode: f.company,
        company: f.companyName,
        businessUnit: f.bu,
        businessTeam: f.bt,
        companyRegion: 'AE',
        customerRegion: demoRegion(f.customer),
        salesperson: f.sp,
        customerNumber: `C${String(100 + fi)}-0${String(10 + i)}`,
        customer: f.customer,
        itemNumber: f.itemNo,
        product: f.product,
        supplier: f.supplier,
        kluberCode: f.supplier === 'SUP ALPHA' && i < 2 ? `KL0${10 + i}` : null,
        location: LOC[Math.floor(seeded(seed + 'l') * LOC.length)],
        district: DISTRICT[Math.floor(seeded(seed + 't') * DISTRICT.length)],
        docType: isReturn ? 'Return' : 'Invoice',
        docNumber: `SLS-0${String(1000 + fi * 7 + i)}`,
        batch: `DXB-26${String(1000 + fi * 13 + i)}`,
        invoiceDate: new Date(Date.UTC(2026, month - 1, day)).toISOString(),
        uom: UOM[Math.floor(seeded(seed + 'u') * UOM.length)],
        qty: zeroQty ? 0 : Math.round(f.qty * share),
        sales,
        cost: sales - gp - charges,
        charges,
        gp,
        gpPct: sales ? gp / sales : null,
        // The line's own — one company, so never ambiguous. The USD case is
        // applied by the caller, which is where the currency toggle lives.
        currency: DEMO_SALES_CURRENCY[f.company] ?? null,
      })
    }
  })
  return out
}

export const IS_DEMO = import.meta.env.VITE_MBI_MODE === 'demo'

/* ===========================================================================
   DEVELOPER MODE, in memory (028)

   Mutable at module level, like DEMO_ACCESS above, so the demo behaves like the
   real screen: flip a switch and the panel changes. Everything resets on
   reload, which is what a demo should do.
   =========================================================================== */

const DEMO_DEV_FLAGS: Record<DevFlagKey, boolean> = {
  dev_mode: false,
  dev_show_source: true,
  dev_show_math: true,
  dev_show_query: true,
}

/**
 * The same words the 028 seed carries, for the two measures most worth showing
 * a reviewer: one straightforward sum, and the one with a real trap in it.
 *
 * GP % is here because it is the measure most likely to be signed off wrongly.
 * It is a ratio of sums rather than a mean of ratios, and it is deliberately
 * NOT converted in USD mode while the two figures beside it are — which is
 * invisible unless somebody says so.
 */
const DEMO_MEASURE_DOCS: MeasureDoc[] = [
  {
    surface: 'explorer',
    key: 'sales',
    displayName: 'Sales',
    definition:
      'Money invoiced in the chosen period, before cost. In YTD and MTD mode it is the sum of the pre-computed period column the ERP maintains; in a custom date range it is summed per row from the invoice date instead, because a pre-computed column only knows about "now".',
    sources: [
      {
        ordinal: 1,
        object: 'dbo.D365_PostedSales_TRx',
        role: 'fact',
        columns: 'YTD_SLS, MTD_SLS, Extended_Price, USD_Extended_Price, Exchange_Rate, Invoice_Date',
        joinKind: null,
        joinTo: null,
        joinOn: null,
        note: 'Invoice_Date is read only in custom-range mode, where the period columns cannot help; Exchange_Rate only in USD mode, where the period columns have no USD twin.',
      },
    ],
    formula: 'YTD: SUM(YTD_SLS)   MTD: SUM(MTD_SLS)   custom: SUM(Extended_Price) over rows inside the range',
    procName: 'rpt.usp_Sales_Explorer',
    sourceFile: 'db/platform/021_sales_report.sql',
    notes:
      'Samples are IN the money and OUT of the counts — kept deliberately, because the Power BI report this replaced behaved that way and cutover parity was the requirement.',
  },
  {
    surface: 'explorer',
    key: 'gpPct',
    displayName: 'GP %',
    definition:
      'Margin as a proportion of sales. It is the ratio of the SUMS — total profit over total sales — and never the average of the per-row percentages, which would weight a hundred-riyal line the same as a hundred-thousand-riyal one.',
    // TWO sources, deliberately. GP % is the demo's only multi-object figure,
    // and it exists here so the panel's join rendering is exercised rather than
    // merely implemented — the layout has to survive a second block before
    // Material Planning arrives with four of them.
    sources: [
      {
        ordinal: 1,
        object: 'dbo.D365_PostedSales_TRx',
        role: 'fact',
        columns: 'YTD_GP, YTD_SLS, MTD_GP, MTD_SLS, DATAAREAID',
        joinKind: null,
        joinTo: null,
        joinOn: null,
        note: 'Both halves of the ratio come from the same rows, so a row excluded by the cost mask leaves neither the numerator nor the denominator.',
      },
      {
        ordinal: 2,
        object: 'rpt.fn_Caller_Sales_Cost_Scope',
        role: 'security',
        columns: 'Comp_Id',
        joinKind: 'LEFT JOIN',
        joinTo: 'dbo.D365_PostedSales_TRx',
        joinOn: 'cvc.Comp_Id = f.DATAAREAID',
        note: 'The companies where THIS caller may see cost. A row whose company is absent here contributes zero rather than being dropped.',
      },
    ],
    formula: 'SUM(GP) / NULLIF(SUM(Sales), 0)',
    procName: 'rpt.usp_Sales_Explorer',
    sourceFile: 'db/platform/021_sales_report.sql',
    notes:
      'THIS ONE IS DELIBERATELY NOT CONVERTED IN USD MODE. It is the local-currency blend, the exact number the Power BI measure produced, kept unconverted for cutover parity while Sales and GP above it do convert.',
  },
  // A CONTROL, not a figure — here so the demo exercises the filter kind rather
  // than only describing it. This one is the best single example of why filters
  // needed documenting at all: it is the only control on the bar that is a
  // substring search, it looks identical to the six exact ones beside it, and
  // nothing on screen has ever said so.
  {
    surface: 'filters',
    key: 'cust',
    displayName: 'Customer contains',
    definition:
      'Narrows to customers whose name CONTAINS what you type.',
    sources: [
      {
        ordinal: 1,
        object: 'dbo.D365_PostedSales_TRx',
        role: 'fact',
        columns: 'Customer_Name',
        joinKind: null,
        joinTo: null,
        joinOn: null,
        note: 'NO MENU EXISTS for this one, deliberately: 9,742 distinct names is not something a person can scroll, so it is a substring box instead.',
      },
    ],
    formula:
      "matches Customer_Name with a leading and trailing wildcard — a substring search, not an exact match",
    procName: 'rpt.usp_Sales_Overview',
    sourceFile: 'db/platform/021_sales_report.sql',
    notes:
      'THE ONE CONTROL ON THE BAR THAT IS NOT AN EXACT MATCH. Typing "AL" matches every name containing those two letters anywhere. The wildcard on the front is also what stops an index being usable, so this is the most expensive of the seven — a deliberate trade for being able to find a customer at all.',
  },
]

/**
 * What kind of thing each demo entry is.
 *
 * Kept beside the docs rather than inside them because the real MeasureDoc does
 * not carry a kind either — the panel takes it from the manifest, so there is
 * one place the answer comes from. This map is the demo's manifest.
 */
const DEMO_MEASURE_KINDS: Record<string, MeasureKind> = {
  'explorer/sales': 'measure',
  'explorer/gpPct': 'measure',
  'filters/cust': 'filter',
}
